import { AuthenticationResult } from '@azure/msal-browser';
import { CtcAction, CtcCustomerFlags, CtcEffect, CtcPoStatusCode, CtcRole } from '../../shared/ctcLifecycle';
import { IdTokenResult, User } from 'firebase/auth';
import { Client } from '@microsoft/microsoft-graph-client';
import { FieldValue, Timestamp } from 'firebase/firestore';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';

// import { File } from "buffer";
import { PDFImage } from 'pdf-lib';
import { ComboboxItem, ComboboxProps } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import type { ReactElement } from 'react';
// import { info } from "console";

type MimeType = string;

export type UserDataType = {
    pdf?: any;
    user?: User | null;
    msal?: AuthenticationResult;
    firebaseTokenResult?: IdTokenResult;
    getAzureADToken?: (userData: UserDataType) => Promise<string | undefined>;
    microsoftClient?: Client | null;
    customSearchKey?: string;
    securitiesSearchKey?: string;
    rmsCollectionSearchKey?: string;
    servicesSearchKey?: string;
    customerCollectionSearchKey?: string;
    campaignsSearchKey?: string;
    ctcSearchKey?: string; // CTC lists in Typesense; bank users' key only matches their customer
    availableTradeAccounts?: string[];
};

export enum KFHGroup {

    'rm' = 'rm',
    'oper' = 'oper',
    'cr' = 'cr',
    'exec' = 'exec',
    'hr' = 'hr',
    'sys' = 'sys',
    'risk' = 'risk',
    'admin' = 'admin',
    'digital' = 'digital',
    'port' = 'port',
    'ipo' = 'ipo',
    'equity_capital_market' = 'equity_capital_market',
    'kfh_rm_certified' = 'kfh_rm_certified',
    'kfh_rm_private_banking' = 'kfh_rm_private_banking',
    'kfh_rm_ruwad' = 'kfh_rm_ruwad',
    'kfh_rm_tamayoz' = 'kfh_rm_tamayoz',
    'kfh_oper' = 'kfh_oper',
    'close_ended_funds' = 'close_ended_funds',
    'cram' = 'cram',
    'kfh_rm' = 'kfh_rm',
    'fund_manager' = 'fund_manager',
    'global_dpm' = 'global_dpm', // Global DPM rebalance team (/global-dpm)
    'ctc_maker' = 'ctc_maker',
    'ctc_checker' = 'ctc_checker',
    'ctc_supervisor' = 'ctc_supervisor',
    'ctc_dealer' = 'ctc_dealer',
    'ctc_backoffice' = 'ctc_backoffice',
    'ctc_admin' = 'ctc_admin',
    'ctc_viewer' = 'ctc_viewer', // view only: orders, transactions, reports; no actions
}

export interface AuthorizationClaims {
    claims: Partial<{
        [key in KFHGroup]: string[];
    }>;
    updatedBy: string;
}

/**
 * A directory group from an Entra tenant the signed-in admin cannot query through
 * Graph (e.g. the KFH bank tenant). Stored in config/externalGroups so the group
 * pickers can offer it by id + display name; the id is what customClaims matches
 * against the user's /me/memberOf result.
 */
export interface ExternalGroup {
    /** Entra object id (GUID) of the group */
    id: string;
    displayName: string;
    /** Label of the tenant the group lives in, e.g. "KFH" */
    tenant: string;
}

export interface ExternalGroupsConfig {
    groups: ExternalGroup[];
    updatedBy?: string;
}
export interface WhatsappTemplateSettings {
    id: string;
    title: string;
    templateContent?: TwilioContentType;
    tags: WhatsappTemplateTags[];
}

/** config/lsegCorporateActions — LSEG cash dividends + splits -> pending Geneva requests (functions-geneva/src/lseg_corporate_actions.ts) */
export interface LsegCorporateActionsConfig {
    manualEnabled?: boolean; // admin "Get CA" button on the Geneva tab
    scheduleEnabled?: boolean; // daily 05:00 Kuwait job
    exchanges?: string[]; // securities.EXCHANGE_CODE values
    exDateCutoff?: string; // YYYY-MM-DD: never fetch CAs with an earlier ex-date (ops keyed those in by hand)
}

export interface TradesMapConfig {
    accounts?: { [key: string]: TradeAccountConfig };
    assetsManagers?: { [key: string]: string };
    portfoliosType?: string[];
    // the structure of the commissions map is as follows:
    // first we have the exchange (KSE, …) then we have the sub-markets (MAIN, PREMIER, …) and then we have the commission config
    commissions?: Record<string, Record<string, CommissionConfig>>;
    exchanges?: ExchangeMap;
    feelessPortfolios?: string[]; // list of portfolio IDs that do not have fees
    fractionalExchanges?: string[]; // exchanges whose trades allocate in fractional units (e.g. ['ALLFUNDS']); all others are integer-only
    isinToBloombergId?: Record<string, string>; // AllFunds ISIN → security BLOOMBERGID (securities carry no ISIN); built by utils/buildGlobalDpmIsinMap.js
    sukukPortfolioTypeToCustodianAccountMap?: { [key: string]: string }; // mapping from portfolio type codes to custodian account IDs
    excel_upload_waiting_time_ms?: number; // time to wait before checking warnings/errors for excel upload and undo the allocations
    newSukukLsegOnly?: boolean; // manual trade modal: new sukuk fields come only from LSEG (Issue Country / Issuer / Sub Industry stay editable)
}

export enum WhatsappTemplateTags {
    order_submitted = 'order_submitted', // submitted and under review
    order_subscription_completed = 'order_subscription_completed', // subscription waiting_for_nav/processing -> done, includes surplus
    order_redemption_completed = 'order_redemption_completed', // redemption waiting_for_nav/processing -> done, excludes surplus

    'service' = 'service', // general service message?
    'portfolio' = 'portfolio', // portfolio specific template
    'kfhtrade' = 'kfhtrade', // kfhtrade specific template
    'order' = 'order', // order specific template
    'order_close_ended' = 'order_close_ended', // close ended order specific template ?
    'final_signed_agreement' = 'final_signed_agreement', // close ended order specific template ?
    'order_open_ended' = 'order_open_ended', // open ended order specific template ?
    'marketing' = 'marketing', // marketing campaigns (eg: instagram)
    'campaign' = 'campaign', // for general campaigns
    'kyc' = 'kyc', // for kyc templates
    'docusign_executed_copy' = 'docusign_executed_copy', // customer's executed (DocuSign-sealed) contract copy, sent by the Connect webhook after auto-submit; falls back to final_signed_agreement if untagged
    'otp' = 'otp', // OTP delivery; template must render the code from the {{otp}} (or {{1}}) variable
    'info_update' = 'info_update', // notify customer their registered info was changed (sent to the old number)
    'info_update_link' = 'info_update_link', // invite to update your info; template must build the contractapp URL from the {{linkID}} variable
    // CTC (Commodity Trading Center) beneficiary flow — quick-reply templates; variables ref, commodity, quantity, commission
    'ctc_transfer_approval' = 'ctc_transfer_approval', // "commodity deposited, approve the transfer?" buttons Approve / Reject (payloads ctc_approve / ctc_reject)
    'ctc_sell_request' = 'ctc_sell_request', // "sell or collect?" buttons Sell / Collect (payloads ctc_sell / ctc_collect)
    'ctc_transfer_confirmed' = 'ctc_transfer_confirmed', // executed copy of attachment 4 as media; variable {{mediaID}}
    'ctc_sell_confirmed' = 'ctc_sell_confirmed', // executed copy of attachment 5 as media; variable {{mediaID}}
    'ctc_purchase_details' = 'ctc_purchase_details', // purchase confirmation supplied before transfer approval
    'sms' = 'sms', // free-text replacement for the Kuwait-only SMS gateway; template body must render the {{1}} (or {{message}}) variable, e.g. "KFH Capital: {{1}}"
}
export type ConfigDataType = {
    risk?: { countries: string[] };
    approvers?: { [key: string]: string[] };
    serviceSettings?: {
        availableServices: ServiceTypeLabel[];
    };
    // segment_groups?: { [key: string]: any },
    exchange_rates?: { [key: string]: any };
    authorizationClaims?: AuthorizationClaims;
    externalGroups?: ExternalGroupsConfig;
    version?: {
        kycapp: string;
        webapp: string;
    };

    routesPermissions?: Record<
        ClaimType,
        ['common' | 'customers' | 'orders'][]
    >;
    pdf_documents?: {
        kyc: string;
        company_kyc: string;
    };
    orderTemplates?: {
        whatsappTemplate: string;
        emailTemplate: string;
    };
    kycTemplates?: {
        whatsappTemplate: string;
        whatsappTemplateArabic: string;
        emailTemplate: string;
    };

    serviceTemplates?: {
        [key in ServiceType]: {
            whatsappTemplate: string;
            emailTemplate: string;
        };
    };
    factsheetTemplates?: {
        [key in Fund['type']]: {
            whatsappFactsheetTemplate: string;
            emailFactsheetTemplate: string;
        };
    };
    segments?: {
        segmentList?: Segment[];
    };
    contractTemplates?: {
        whatsappTemplate: string;
        onlyKycWhatsappTemplate: string;
    };
    tradesMap?: TradesMapConfig;
    lsegCorporateActions?: LsegCorporateActionsConfig;

    portfolio_service?: PortfolioService;
    whatsappTemplates?: {
        [key: string]: WhatsappTemplateSettings;
    };
    fundCommunications?: {
        /** Twilio content SID for the short "a new announcement is in the portal" notice sent to RMs. */
        announcementWhatsappTemplateID?: string;
        /** Twilio content SID for the RM letter notification. Supports `mediaID` and affected-client variables. */
        letterWhatsappTemplateID?: string;
        /** Test/emulator only: the only addresses fund communication e-mails may reach. */
        testEmailRecipients?: string[];
    };
};

export interface GroupMapping {
    accountId: string;
    groupId: string;
    pending?: boolean;
    toDelete?: boolean;
    addedBy?: string; // User who added this record
    addedAt?: Timestamp; // Timestamp when added
}

export interface DefaultPortfolio {
    accountId: string;
    portfolioId: string;
    symbolId?: string;
    pending?: boolean;
    toDelete?: boolean;
    addedBy?: string; // User who added this record
    addedAt?: Timestamp; // Timestamp when added
    isFund?: boolean; // Indicates if the portfolio is a fund in order to add the fundStructure property in Trade
}

// Configuration for a single account in tradesMap
export interface TradeAccountConfig {
    groupId?: string;
    burgan_kcc_id?: string;
    burgan_broker_id?: string;
    burgan_stockcenter_id?: string;
    geneva_id?: string;
    geneva_broker?: string; // overrides the default Broker sent to Geneva (mainly Global DPM)
    defaultPortfolios: DefaultPortfolio[]; // Array of default portfolios for this account
    addedBy?: string; // User who last added/updated group/IDs for this account
    addedAt?: Timestamp; // Timestamp when group/IDs were last added/updated
    portfolioTypeCode?: string[];
    portfolioContract?: string[]; // e.g. ['DPM_GLOBAL', 'CUSTODY_GLOBAL'] — matches Portfolio.portfolioContract; takes precedence over portfolioTypeCode when the portfolio carries one
    allocateFromExcel?: boolean; // auto-allocate this account's settled trades from global_dpm_allocation_rows (tradeUpdated function)
    capitalizeExpenses?: boolean; // new trades send fees to Geneva as capitalized TradeExpenses (stamped on the trade by tradeUpdated)
}
// Configuration for a commission for each stock market
export interface CommissionConfig {
    min_commission?: number;
    rate: number;
    stamp?: {
        fee?: number;
        threshold?: number;
    };
}

export type AppConfigType = {
    apiUrl?: string;
};
export type GlobalDataType = {
    funds?: Fund[];
    fundsMap?: Record<string, Fund>;
    privateFunds?: Fund[];
    publicFunds?: Fund[];
    ipoCompanies?: IpoCompany[];
};

///////////////////////////////////////////////////////////////
// Data types shared by both firebase functions and client.
// May need to move this to a separate file.
///////////////////////////////////////////////////////////////

export interface LastOperation {
    date: Timestamp;
    msg: string;
    by?: string;
    byID?: string;
    details?: string;
}

export interface Tag {
    title: string;
    badgeColor: string;
    code: string;
    service?: string;
    shortName?: string;
}

export type Gender = 'M' | 'F';

export type SegmentType = "Ruwad" | "Private Banking" | "Tamayoz" | "Individual Finance" | "KFH Capital";
export interface Segment {
    name: string;
    color: string;
}
export interface GWIHolding {
    fund: string;
    shares: number;
    total: number;
    code: string;
    gl_code: string;
    gl_name: string;
}

export interface NotificationMSG {
    type: 'success' | 'error' | 'info';
    message: string;
}

export interface UserFiles {
    type: string;
    file: string;
}

// CRAM Task 4 — `cram` is omitted from both base interfaces because they
// disagree on its shape (`Customer.cram` is `CustomerCramSnapshot`,
// `KYC.cram` is `KycCramSnapshot`). PDF generation doesn't read either
// shape today, so the field is simply absent from `CustomerPDFData`.
export interface CustomerPDFData
    extends
    Omit<
        Customer,
        'birthDate' | 'civilIDExpiryDate' | 'passportExpiryDate' | 'cram'
    >,
    Omit<
        KYC,
        | 'birthDate'
        | 'civilIDExpiryDate'
        | 'passportExpiryDate'
        | 'created'
        | 'cram'
    > {
    userFiles?: UserFiles[];
    passportExpiryDate?: string;
    civilIDExpiryDate?: string;
    birthDate?: string;

    created?: string;
    paciStamp?: PDFImage | Uint8Array | ArrayBuffer | string; //base64

    sourcesOfIncomeSentence?: string;
    sourcesOfWealthSentence?: string;
}

export interface LastValidKYC {
    created: Timestamp;
    kycID: string;
    civilIDExpiryDate?: Timestamp;
    passportExpiryDate?: Timestamp;
    kycExpiryDate?: Timestamp;
}

export interface EmployeeSignature {
    email: string;
    signature: string; //base64
    photo?: string; //base64

    /* This is use for contract PDF*/
    name_en?: string;
    name_ar?: string;
    position_en?: string;
    position_ar?: string;
}

export enum AvailableAgreements {
    kfh_rm_access_consent = 'kfh_rm_access_consent',
}

/**
 * Document type behind `Customer.other_id` (non-Kuwaiti individuals without a Kuwait Civil ID).
 * Only the national ID is accepted: the identifier must never change, and passport numbers
 * change on renewal. A passport copy is still a required KYC document upload for non-Kuwaitis.
 */
export type OtherIdType = "national_id";
export const OTHER_ID_TYPE_LABELS: Record<OtherIdType, string> = {
    national_id: "National ID",
};

export interface Customer {
    commercial_register?: string;
    civilID?: string;
    crmID?: string;
    email?: string;
    paciEmail?: string;
    paciMobile?: string;
    id?: string;
    lastOperation?: LastOperation;
    mobile: string;
    /** Dial code ("+965", "+973"). Missing means legacy Kuwaiti (+965). KW and BH
     *  locals are both 8 digits, so country can never be inferred from `mobile` —
     *  see shared-helpers/src/mobileHelpers.ts and docs/bahrain-readiness.md. */
    mobile_code?: string;
    name_en: string;
    name_ar: string;
    value?: string;
    gender?: Gender;
    segment?: string;
    rm_email?: string;
    hashed_id?: string;
    tags?: { [key: string]: Tag };
    highRiskOverride?: string;
    lowRiskOverride?: string;
    kfhtrade_id?: string;
    nationality?: string;
    residence?: string; // get country of residence
    signature?: PDFImage | Uint8Array | ArrayBuffer | string; //base64
    paciData?: PACIData;
    tradexID?: string;
    lastValidKYC?: LastValidKYC;
    lastUnsubmittedKYCMessage?: {
        date: Timestamp;
        count: number;
    };
    oneTimeOverrideKYCData?: {
        civilIDExpiryDate?: Timestamp;
        houseID?: string;
        gender?: Gender;
        nationality?: string;
        passportExpiryDate?: Timestamp;
        passportNumber?: string;
        overrideCivilID?: boolean;
        overridePassport?: boolean;
    };
    holdings?: Record<string, GWIHolding>;
    passportData?: PassportData;
    address_from_finder?: AddressFromFinder;
    virtual?: boolean;
    agreements?: { [key in AvailableAgreements]: boolean };
    customerType?: CustomerType;
    has_active_products?: boolean;
    /**
     * Napier screening — true when this customer is linked to a customer with
     * active products, regardless of whether they also have their own:
     *   - an authorized signatory of an active company, or a company with an active signatory
     *   - a director or executive manager of an active company
     *   - a relative of a customer with active products (any family relationship type)
     * Maintained by the `onChangeCustomer` trigger (functions) via
     * `refreshActiveRelatedStatus`.  Napier treats
     * `has_active_products || has_active_related_products` as "Active" and only
     * excludes the customer from screening when both are false.
     */
    has_active_related_products?: boolean;
    /** The active customers that make this customer screen-worthy, keyed by their customer doc id. */
    active_related_customers?: Record<string, ActiveRelatedCustomer>;
    /**
     * Denormalized `authorized_signatories[].id`, maintained by `onChangeCustomer`
     * so a company can be found by one of its signatories (array-contains).
     */
    authorized_signatory_ids?: string[];
    /** Customer ids of directors/managers from the latest submitted entity controlling-person data. */
    controlling_person_ids?: string[];
    beneficial_owner_ids?: string[];
    employee?: boolean;
    //PDF encrypted URL
    qrCodeUrl?: string;
    other_id?: string;
    /** What kind of document `other_id` is. Only meaningful when the customer has no civilID. */
    other_id_type?: OtherIdType;
    /** ISO-2 country that issued `other_id`. National ID numbers are only unique per country. */
    other_id_country?: string;
    napierID?: string; // id of the customer on NAPIER AML
    relatives?: Record<string, FamilyRelative>;
    kfhtrade_subacct_id?: string;
    portfolio_subacct_id?: string;
    portfolios?: Record<string, CustomerPortfolioSearchEntry>;

    // This is for authorized signatory in Entities KYC
    authorized_signatories?: CompanyAuthorizedSignatories[];
    mociCompanyData?: CompanyRegistryData;

    // CRAM live snapshot (Task 4). Lean shape — see CustomerCramSnapshot.
    // Mirrors the active KYC's CRAM rating so CR / AML lists can filter by
    // it without joining to the kyc subcollection. Always rewritten by
    // recomputeAndPersistCram in lock-step with the per-KYC snapshot.
    cram?: CustomerCramSnapshot;
}
export interface CompanyRegistryActivity {
    activity_code?: string;
    activity_desc?: string;
    sector_desc?: string;
    license_record_id?: string;
    is_active?: string;
}
export interface CompanyRegistryAddress {
    license_record_id?: string;
    governorate_code?: string;
    district?: string;
    block?: string;
    street?: string;
    plot_no?: string;
    building_name?: string;
    floor_no?: string;
    unit_no?: string;
    unit_type?: string;
}
export interface CompanyRegistryBeneficiary {
    /** Resolved by Civil ID on the server; only owners of 25% or more are linked. */
    customerID?: string;
    name_en?: string;
    name_ar?: string;
    gender?: 'M' | 'F';
    civil_id?: string;
    name?: string;
    nationality?: string;
    from_date?: string;
    to_date?: string;
    status?: string;
    request_status?: string;
    mobile?: string;
    email?: string;
    owned_shares?: string;
    ownership_percentage?: string | number;
    owner_shares?: string;
    owner_shares_unit?: 'shares' | 'percent';
    is_manager?: string;
    address?: string;
    direct_indirect?: 'direct' | 'indirect';
}
export interface CompanyRegistryLicense {
    license_record_id?: string;
    license_central_no?: string;
    company_civil_id?: string;
    commercial_register?: string;
    company_name?: string;
    trade_name?: string;
    license_type_desc?: string;
    commercial_no?: string;
    registration_start_date?: string;
    registration_end_date?: string;
    last_budget_status?: string;
    branch_no?: string;
    status?: string;
    phone?: string;
}
export interface CompanyRegistryData {
    commercial_register: string;
    central_number?: string;
    company_civil_id?: string;
    name_ar?: string;
    name_en?: string;
    trade_name_ar?: string;
    legal_structure_desc?: string;
    license_type_desc?: string;
    license_target_desc?: string;
    license_status?: string;
    license_status_desc?: string;
    sector_desc?: string;
    registration_date?: string;
    incorporation_date?: string;
    total_capital?: string;
    total_share_no?: string;
    last_budget_status?: string;
    nationality?: string;
    email?: string;
    mobile?: string;
    website?: string;
    licenses?: CompanyRegistryLicense[];
    activities?: CompanyRegistryActivity[];
    addresses?: CompanyRegistryAddress[];
    beneficiaries?: CompanyRegistryBeneficiary[];
    registry_warnings?: string[];
}
export interface CompanyRegistryLookupRequest {
    commercial_register: string;
}
export interface CompanyRegistryLookupResult extends BaseResult {
    companyData?: CompanyRegistryData;
}
export interface CompanyRegistryCustomerRefreshRequest {
    customerID?: string;
    crmID?: string;
}
export interface CompanyRegistryCustomerRefreshResult extends BaseResult {
    customerID: string;
    companyData: CompanyRegistryData;
}
export interface CompanyRegistryBackfillRequest {
    customerIDs?: string[];
    dryRun?: boolean;
    includeAlreadyBackfilled?: boolean;
}
export interface CompanyRegistryBackfillFailure {
    customerID: string;
    commercial_register?: string;
    reason: string;
}
export interface CompanyRegistryBackfillDuplicateGroup {
    commercial_register: string;
    customerIDs: string[];
}
export interface CompanyRegistryBackfillResult extends BaseResult {
    dryRun: boolean;
    scanned: number;
    eligible: number;
    updated: number;
    skippedAlreadyBackfilled: number;
    skippedMissingCommercialRegister: string[];
    skippedDuplicateGroups: CompanyRegistryBackfillDuplicateGroup[];
    failures: CompanyRegistryBackfillFailure[];
}

export interface CompanyAuthorizedSignatories {
    id: string;
    role_within_company: string;
    customer?: Customer; // this is optional and only used for display purposes when fetching signatories.  it is not stored in the authorized_signatories array in firebase.
}

/** How a customer relates to the active customer that makes them screen-worthy on Napier. */
export type ActiveRelatedCustomerRelation = 'authorized_signatory' | 'company_signatory' | 'controlling_person' | 'beneficial_owner' | 'relative';

export interface ActiveRelatedCustomer {
    /** `authorized_signatory`: this customer signs for the active company. `company_signatory`: the active customer signs for this company. `relative`: a family link. */
    relation: ActiveRelatedCustomerRelation;
    crmID?: string;
    name_en?: string;
    customerType?: CustomerType;
}

export interface CustomerPortfolioSearchEntry {
    portfolioTypeCode?: string;
    managerName?: string;
    riskProfile?: string;
    status?: string;
}
export enum familyRelations {
    'parent' = 'parent',
    'child' = 'child',
    'grandparent' = 'grandparent',
    'grandchild' = 'grandchild',
    'sibling' = 'sibling',
    'spouse' = 'spouse',
    'other' = 'other',
}
export interface FamilyRelative {
    relative_id: string;
    relation: familyRelations;
    relative_name_en: string;
    relative_name_ar: string;
    relative_civilID: string;
    relative_crmID: string;
    updated_at: Timestamp | FieldValue;
    updated_by: string;
    created_at: Timestamp | FieldValue;
}
export enum reciprocalRelationMap {
    parent = familyRelations.child,
    child = familyRelations.parent,
    grandparent = familyRelations.grandchild,
    grandchild = familyRelations.grandparent,
    sibling = familyRelations.sibling,
    spouse = familyRelations.spouse,
    other = familyRelations.other,
}

export interface LinkFamilyMemberRequest {
    customerId: string;
    relation: familyRelations;
    relative_id: string;
}

// ---- RM relationship profile ----
// Soft RM intelligence about a customer (aliases, family, interests, notes).
// Lives at customers/{id}/rm_profile/main — deliberately OFF the customer doc
// so exports/BigQuery/mobile never see it. Admin-claim only (UI + rules).
// Distinct from `relatives`, which stays the compliance-grade customer link.

export interface RmProfileKeyPerson {
    id: string;
    name: string;
    relation: string;
    age?: string; // freeform: "12", "~40", "b. 2014"
    occupation_or_school?: string;
    interests?: string;
    notes?: string;
}

// RM notes attached to an existing `relatives` link, keyed by relative_id.
export interface RmProfileRelativeAnnotation {
    interests?: string;
    notes?: string;
}

export interface RmProfileImportantDate {
    id: string;
    label: string;
    date: string; // YYYY-MM-DD
}

export interface RmProfile {
    alias_en?: string;
    alias_ar?: string;
    preferred_language?: string;
    preferred_channel?: string;
    best_contact_time?: string;
    contact_notes?: string;
    relative_annotations?: Record<string, RmProfileRelativeAnnotation>;
    key_people?: RmProfileKeyPerson[];
    interests?: string[];
    gift_notes?: string;
    dietary_notes?: string;
    topics_to_avoid?: string;
    employer?: string;
    job_title?: string;
    business_interests?: string;
    referral_source?: string;
    life_events?: string;
    important_dates?: RmProfileImportantDate[];
    created_at?: Timestamp | FieldValue;
    updated_at?: Timestamp | FieldValue;
    updated_by?: string;
}

export interface CustomerFundData {
    id?: string; // Firestore document ID, independent of the fund product code.
    gl_code?: string;
    gl_name?: string;
    begcap?: number;
    begperc?: number;
    bunits?: number;
    endcap?: number;
    endperc?: number;
    eunits?: number;
    currency?: string;
    prevendcap?: number;
    qtd_redemptions?: RedemptionBreakdown;
    total_redemptions?: RedemptionBreakdown;
    ytd_redemptions?: RedemptionBreakdown;
    total_subscriptions?: number;
    carrying_value?: number; //this is basically for closed ended funds.  the total_subscriptions - total_redemptions from return of capital
    market_value?: number;
    cost_basis?: number;
    average_cost_per_unit?: number;
    realized_profit_loss?: number;
    unrealized_profit_loss?: number;
    unrealized_profit_loss_percent?: number;
    total_profit_loss?: number;
    profit_loss_nav_per_unit?: number;
    profit_loss_nav_date?: Timestamp;
    profit_loss_updated_at?: Timestamp;
    force_recalculate?: boolean;
    transactions?: Record<string, CustomerFundDataTransaction>;
    blocked_units?: Record<string, number | FieldValue>;
    transferred_out?: boolean;
}
export interface RedemptionBreakdown {
    other: number;
    pnl: number;
    roc: number;
    total: number;
}

export enum CustomerFundDataTransactionType {
    subscription = 'subscription',
    redemption = 'redemption',
    transfer_in = 'transfer in',
    transfer_out = 'transfer out',
    /** Geneva AdjUnits: signed units only, no cash. Already reflected in Geneva eunits/bunits. */
    unit_adjustment = 'unit adjustment',
}

export interface CustomerFundDataTransaction {
    counterparty_crm_id?: string;
    geneva_transfer_id?: number;
    geneva_pship_id?: number;
    geneva_capital_account_id?: number;
    source?: string;
    amount?: number;
    code?: string;
    currency?: string;
    deal_date?: Timestamp;
    description?: string;
    fund?: string;
    period?: number;
    end_of_period?: boolean;
    shares?: number;
    type?: CustomerFundDataTransactionType;
    updated_at?: Timestamp;
    amountInKWD?: number;
    user_amount?: number;
    user_shares?: number;
    fees?: number;
    portal_fee_override?: number;
    portal_fee_source?: string;
    portal_fee_order_id?: string;
    portal_fee_order_path?: string;
    portal_fee_backfilled_at?: Timestamp;
    order_id?: string;
    dividend?: boolean;
    profit_loss?: CustomerFundDataTransactionProfitLoss;
}

export interface CustomerFundDataTransactionProfitLoss {
    average_cost_per_unit: number;
    redeemed_cost_basis: number;
    realized_profit_loss: number;
    realized_profit_loss_percent?: number;
    units_before: number;
    cost_basis_before: number;
    units_after: number;
    cost_basis_after: number;
    method: 'weighted_average_cost';
}

export type CustomerSummaryType =
    | 'overview'
    | 'open_ended'
    | 'closed_ended'
    | 'portfolios'
    | 'kfhtrade';

export type CustomerSummaryValuationMethod =
    | 'nav'
    | 'carrying_value'
    | 'geneva_position'
    | 'brokerage_position'
    | 'mixed'
    | 'not_available';

export interface CustomerSummaryTotals {
    current_value: number | null;
    market_value: number | null;
    carrying_value: number | null;
    cost_basis: number | null;
    realized_profit_loss: number | null;
    unrealized_profit_loss: number | null;
    total_profit_loss: number | null;
    realized_profit_loss_percent: number | null;
    unrealized_profit_loss_percent: number | null;
    total_profit_loss_percent: number | null;
    cash: number | null;
    securities: number | null;
}

export interface CustomerSummaryFlowTotals {
    subscriptions: number;
    redemptions: number;
    income_distributions: number;
    return_of_capital: number;
    cash_distributions: number;
    realized_profit_loss: number | null;
    fees: number;
    net_cash_flow: number;
}

export interface CustomerSummaryCurrencyTotals {
    current_value: number;
    market_value: number;
    carrying_value: number;
    cost_basis: number;
    realized_profit_loss: number;
    unrealized_profit_loss: number;
    total_profit_loss: number;
    cash: number;
    securities: number;
    ytd_subscriptions: number;
    ytd_redemptions: number;
    ytd_income_distributions: number;
    ytd_return_of_capital: number;
    ytd_cash_distributions: number;
    ytd_realized_profit_loss: number;
    ytd_fees: number;
    ytd_net_cash_flow: number;
    lifetime_subscriptions: number;
    lifetime_redemptions: number;
    lifetime_income_distributions: number;
    lifetime_return_of_capital: number;
    lifetime_cash_distributions: number;
    lifetime_realized_profit_loss: number;
    lifetime_fees: number;
    lifetime_net_cash_flow: number;
}

export interface CustomerSummaryAvailability {
    has_market_value: boolean;
    has_cost_basis: boolean;
    has_realized_profit_loss: boolean;
    has_unrealized_profit_loss: boolean;
    has_ytd: boolean;
    valuation_method: CustomerSummaryValuationMethod;
    notes: string[];
}

export interface CustomerSummarySource {
    calculated_at: Timestamp | FieldValue;
    daily_snapshot_at?: Timestamp | FieldValue;
    source_count: number;
    source_collections: string[];
    fx_rates_used: Record<string, number>;
    missing_fx_rates: string[];
}

export interface CustomerSummaryDoc {
    schema_version: 1;
    customer_id: string;
    summary_type: CustomerSummaryType;
    base_currency: 'KWD';
    as_of_date: string;
    totals: CustomerSummaryTotals;
    ytd: CustomerSummaryFlowTotals;
    lifetime: CustomerSummaryFlowTotals;
    by_currency: Record<string, CustomerSummaryCurrencyTotals>;
    availability: CustomerSummaryAvailability;
    source: CustomerSummarySource;
}
export interface TypesenseCustomerTag {
    key: string;
    code: string;
}
export interface TypesenseCustomer extends Customer {
    holdings_arr?: GWIHolding[];
    tags_object_arr?: TypesenseCustomerTag[];
    portfolioKeys?: string[];
    documentID: string; // customer ID used for querying the customer
    kfh_rm_access_consent?: boolean; // flattened from agreements.kfh_rm_access_consent (nested maps can't be faceted)
}

export interface PassportData {
    id: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    countryOfIssue?: string;
    expiryDate?: Timestamp;
}

export type SubscriptionType = 'subscription' | 'redemption';
export type IPOType = 'mao' | 'ipo';
export type ServiceType = 'kfhtrade' | 'kfhbrokerage' | 'portfolio' | 'ipo';

export interface Account {
    iban: string;
    account?: string;
    custom?: boolean;
    verified?: boolean;
    verifiedBy?: string;
    // This is used for Mobile app check if the account is not verified then upload file.
    // if fileName is there then user not show upload certificate button.
    fileName?: string;
    currency?: string;
}

export interface TypesenseOrder extends Partial<Order> {
    customerID: string;
}
export interface UpdatedDataType {
    updated_by?: string;
    updated_on?: Timestamp;
    initial_value?: {
        [key: string]:
        | (string | null)[]
        | number
        | string
        | Timestamp
        | FieldValue;
    }[];
    updated_field_name?: string[];
    updated_fields?: {
        [key: string]:
        | (string | null)[]
        | number
        | string
        | Timestamp
        | FieldValue;
    }[];
    reason?: string;
}

export interface BasicProductAttributes {
    id?: string;
    customerObj?: Customer;
    kyc_id?: string;
    created?: Timestamp;
    submittedAt?: Timestamp; //the actual submission timestamp, preferred for NAV calculation when available
    rm?: RMUser;
    referredBy?: RMUser;
    submittedBy?: RMUser;
    tags?: { [key: string]: Tag };
    approvedAt?: Timestamp; //approval timestamp; available as a late fallback when submittedAt/created are unavailable
    draft?: boolean; //is it a draft?  Then its orderState will always be a draft which you can send or delete.
    manual?: boolean; //is it a manual?  Then its orderState will always be a manual and cannot be updated.
    voting?: boolean; //specify whether this order is voting or not and it will affect the pdf file.
    skipKYC?: boolean;
    paciData?: PACIData;
    /**
     * The guardian's/representative's PACI data — filled instead of paciData
     * when the account holder is a minor or has someone acting on their behalf.
     */
    guardianPaciData?: PACIData;
    /**
     * When the client is a minor, this is the guardian's customer doc id.  The
     * minor's KYC lives under that customer, kyc_id points into their kyc
     * collection, and determine*Status treats the guardian's KYC as the
     * client's own.
     */
    guardianCustomerID?: string;
    signature?: PDFImage | Uint8Array | ArrayBuffer | string; //base64
    /** How the contract was signed. 'docusign' products carry docusignData instead of paciData/signature. */
    signingMethod?: SigningMethod;
    /** DocuSign evidence copied from the contract link on submission (status 'completed' = signed). */
    docusignData?: DocusignData;
    senderID?: string;
    segment?: string;
    iban?: string;
    needsDocuments?: boolean;
    ibanVerified?: boolean;
    verifiedBy?: string;
    byUncertifiedRM?: boolean; //is it a draft?  Then its orderState will always be a draft which you can send or delete.
    fromMobile?: boolean;
    geneva_response?: GenevaResponse;
    documentSent?: boolean;
    /** Set by onChangeService once the portfolio "Done" notifications (created-on-Geneva email + client agreement) went out, so a Done re-entry after a KYC regression never repeats them. */
    doneNotificationsSentAt?: Timestamp;
    payment_request_sent?: Timestamp;

    /** Transfer invoices containing amount, currency, and IBAN details */
    transferInvoices?: TransferInvoice[];
}

/**
 * Represents a transfer invoice with payment details
 */
export interface TransferInvoice {
    /** Transfer amount */
    amount: number; // amount + trading commission if applicable,
    /** Currency code (e.g., KWD, USD) */
    currency: string;
    /** IBAN account number for transfer */
    iban: string;
    /** Transaction ID when payment is received */
    transactionID?: string;

    initialAmount?: number; // This is the original amount before any trading commission is applied, used for record-keeping and display purposes.
    tradingCommissionAmount?: number;
}

export interface Service extends BasicProductAttributes {
    /** KFH account number of service.iban (last 12 digits) */
    acct?: string;
    /** who set/edited the IBAN by hand from the service page, and when */
    ibanSetBy?: string;
    ibanSetAt?: Timestamp;
    /** kfhtrade: the account already exists in OMS (set by determineServiceStatus), so the IBAN gate never applies */
    kfhtradeAccountCreated?: boolean;
    serviceType: ServiceType;
    portfolioOpeningRequest?: PortfolioOpeningRequest;
    /// If any changes are made to the service, this will be updated with the changes.
    updated_data?: UpdatedDataType[];
    kcc?: {
        // response from kcc
        status_code?: string;
        status_message?: string;
        external_ref?: string;
        lastImportedAt?: Timestamp;
    };
    kccStatus?: 'pending' | 'done' | 'failed';
    kccUploadId?: string;
    /**
     * Legacy single-confirmation field. New code writes only `payment_confirmations`
     * (array). Kept on the type for reading historical service documents.
     */
    payment_confirmation?: InvoicePaymentConfirmation;
    payment_confirmations?: InvoicePaymentConfirmation[];

    approved_by?: EmployeeSignature;
    authorizedSignatoryApprovedDate?: string;
    authorizedSignatoryApprovedDayEn?: string;
    authorizedSignatoryApprovedDayAr?: string;
    geneva_responses?: {
        create_portfolio?: Record<string, string>;
        create_management_fee_expense_manager?: Record<string, string>;
        create_custodian_KCC_965080_expense_manager?: Record<string, string>;
        create_custodian_KCC_2040664_expense_manager?: Record<string, string>;
        create_deposit?: Record<string, string>;
        create_deposit_KWD?: Record<string, string>;
        create_deposit_USD?: Record<string, string>;
        // [key: string]: Record<string, string>,
    };
    kycLinkWhatsappNumber?: string; // If Entity we need to send this number 1st KYC link
    /**
     * KFHTrade OMS auto-provisioning responses (Geneva-responses shape).
     * Written by functions-kfhtrade/src/customerCreate.ts while the service is
     * "processing"; a "success" status gates the flip to "done".  Deleting an
     * entry retries the OMS call.
     */
    kfhtrade_responses?: {
        create_customer?: {
            status?: string; // adding | success | error
            status_message?: string;
            key_value?: string; // the created OMS customer number
            timestamp?: Timestamp;
        };
        /**
         * Last manual push of the KYC / civil ID expiry dates to the OMS
         * (kfhtradeUpdateKycDates in functions/src/kfhtradeOmsApi.ts).
         */
        update_kyc_dates?: {
            status?: string; // success | error
            status_message?: string;
            key_value?: string; // the OMS customer number the dates were pushed for
            payload?: { kycExpiryDate: string; civilIdExpiryDate: string }; // YYYY-MM-DD
            by?: string; // admin email
            timestamp?: Timestamp;
        };
    };
    /** Typesense-only (see ServiceLastAction); absent on Firestore documents. */
    lastAction?: ServiceLastAction;
}

export interface KCCUploads {
    completed?: number;
    createdAt?: Timestamp;
    createdBy: {
        token: any;
        uid: string;
    };
    filename: string;
    lastReplyAt?: Timestamp;
    lastReplyFile?: string;
    remotePath: string;
    status: 'pending' | 'done' | 'failed';
    total?: number;
}
export interface IpoCompany {
    available?: boolean;
    filePath?: string;
    id: string;
    image?: string;
    isParent?: boolean;
    name_ar: string;
    name_en: string;
    parentId: string; //contains parent ID
    price?: number;
    type: 'mao' | 'ipo';
}

export interface IpoRequestObject extends BasicProductAttributes {
    shares: number;
    company: IpoCompany;
    residency?: string;
    customerType?: 'company' | 'individual';
    submittedData?: KYC;
    ipoType: IPOType;
    powerOfAttorneyName?: string;
    powerOfAttorneyCivilID?: string;
    mobile: string;
    name_ar?: string;
    name_en?: string;
}

export interface addIpoRequestType {
    company?: string;
    ipoType?: IPOType;
    residency?: string;
    mobile?: string;
    mobileCode?: string;
    powerOfAttorney?: string;
    powerOfAttorneyName?: string;
    powerOfAttorneyCivilID?: string;
    civilID?: string;
    shares?: number;
    isPledged?: string;
    name?: string;
    passportNumber?: string;
    iban?: string;
    sharesDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    companyDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    filledFormDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    signatureDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    passportDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    frontOfCivilIDDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    backOfCivilIDDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
    powerOfAttorneyDoc?: {
        filename: string;
        mimetype: string;
        encoding: string;
        buffer: Buffer;
        path: string;
    };
}
// CombinedType interface definition using Partial utility type for both Service and Order
export interface GenericProduct
    extends
    Partial<Service>,
    Partial<Order>,
    Partial<Agreement>,
    Partial<IpoRequestObject>,
    Partial<BasicTransaction> {
    // type?: ServiceType | SubscriptionType,
    // geneva response types overlap causing an  error because of types
    geneva_responses?: {
        initial_subscription_journal_transaction?: Record<string, string>;
        gwi_order?: Record<string, string>;
        redemption_journal_transaction?: Record<string, string>;
        reversal_subscription_journal_transaction?: Record<string, string>;
        subscription_journal_transaction?: Record<string, string>;
        create_portfolio?: Record<string, string>;
        create_management_fee_expense_manager?: Record<string, string>;
        create_custodian_KCC_965080_expense_manager?: Record<string, string>;
        create_custodian_KCC_2040664_expense_manager?: Record<string, string>;
        create_deposit?: Record<string, string>;
    };
    regular_approved_by?: Approval,
}
export interface Order extends BasicProductAttributes {
    statusNotifications?: Partial<Record<'order_submitted' | 'order_completed', {
        status: string;
        channel?: 'whatsapp' | 'email';
        email?: string;
        messageSID?: string;
        templateSID?: string;
        details?: string;
        errorCode?: string;
        errorMessage?: string;
        createdAt?: Timestamp;
        startedAt?: Timestamp;
        updatedAt?: Timestamp;
    }>>;
    type: SubscriptionType;
    acct?: string;
    fund: Fund;
    inhereted?: boolean,
    Inherited_from?: Customer;
    units?: number;
    maxUnits?: boolean;
    unitsUpdatedBy?: string;
    transferDate?: Timestamp;
    transferAmount?: number;
    nav_per_unit?: number; //one day I will settle on a naming convention for this.
    nav_date?: Timestamp;
    expected_nav_date?: Timestamp;
    postNavAmount?: number; //this should be the amount after fees are deducted
    fees?: number;
    // postNavUnits?: number,
    surplus?: number;
    updated_data?: UpdatedDataType[];
    estimated_units?: number;
    kyc_id?: string;
    /**
     * Legacy single-confirmation field. New code writes only `payment_confirmations`
     * (array). Kept on the type for reading historical order documents.
     */
    payment_confirmation?: InvoicePaymentConfirmation;
    payment_confirmations?: InvoicePaymentConfirmation[];
    // tradex_order_id?: number  //this is the TRX_ORDER_ID from the Tradex database
    // tradex_info?: {  //this is populated by Replicator when an order is added to tradex
    //     orderStatus: string,
    //     orderStatusId: number,
    //     units: number,
    //     amount: number,
    //     changed_at: Timestamp
    // }
    // add_order_to_ams_state?: "adding" | "done" | "error"  //this is to prevent multiple attempts to add the same order to AMS
    geneva_responses?: {
        initial_subscription_journal_transaction?: Record<string, string>;
        gwi_order?: Record<string, string>;
        redemption_journal_transaction?: Record<string, string>;
        reversal_subscription_journal_transaction?: Record<string, string>;
        subscription_journal_transaction?: Record<string, string>;
    };
    // ams_state?: {
    //     art_id?: string,
    //     reverse_art_id?: string,
    //     order_transaction_id?: string,
    //     gwi_order_id?: string,
    // }

    kycLinkWhatsappNumber?: string; // If Entity we need to send this number 1st KYC link


    paidAmount?: number; //this is the amount paid by the customer via KFH Online, used on update amount
    remainingAmount?: number; //this is the remaining amount to be paid by the customer via KFH Online, used on update amount
    selectedSignatoryId?: string; //the signatory ID of the person who is signing the order when comercial KYC
}

export interface EditableObjData {
    previousValue: string[] | number;
    currentValue: string[] | number;
    updatedOn: Timestamp;
    updatedBy: string;
    reason: string;
}
export type ItemInfoEditedObj<T> = {
    [key in keyof Partial<T>]?: EditableObjData;
};

export interface OrderDocument extends Order {
    name_en?: string;
    name_ar?: string;
    email?: string;
    civilID?: string;
    mobile?: string;
    status?: string;
}

export enum KYCStatusCode {
    approval = 'approval',
    waiting_for_guardian_kyc = 'waiting_for_guardian_kyc', //waiting_for_guardian_kyc
    selling_agent_review = 'selling_agent_review', //selling_agent_review
    selling_agent_update = 'selling_agent_update',
    review = 'review',
    exec_approval = 'exec_approval',
    client_update = 'client_update',
    rejected = 'rejected',
    no_kyc = 'no_kyc',
    valid = 'valid',
    invalid = 'invalid', //what the hell is invalid?
    manual_kyc = 'manual_kyc',
    waiting_for_interview = 'waiting_for_interview', // Entities KYC only
    waiting_for_authorized_signatory = 'waiting_for_authorized_signatory', // Entities KYC only
}

export enum KYCRiskCode {
    low = 'low',
    medium = 'medium',
    high = 'high',
}

export enum OrderStatusCode {
    approval = 'approval', //under_review
    review = 'review', //under_review
    selling_agent_review = 'selling_agent_review', //selling_agent_review
    waiting_for_nav = 'waiting_for_nav', //processing
    waiting_for_kyc = 'waiting_for_kyc', //under review
    need_signature = 'need_signature', //need signature
    /** DocuSign-signed: waiting for operations (same segment) or CR/admin to confirm the signature is genuine */
    signature_verification = 'signature_verification',
    processing = 'processing', //processing
    rejected = 'rejected', //rejected
    commitment = 'commitment', //NO
    done = 'done', //done
    need_slip = 'need_slip', //need slip
    need_kyc = 'need_kyc', //need signature   (needs heavy testing)
    manual = 'manual', //NO
    unknown = 'unknown', //NO
    need_form = 'need_form',
    waiting_certified_rm = 'waiting_certified_rm',
    waiting_for_collection_date = 'waiting_for_collection_date',
}

export enum NapierHitsClassification {
    false_positive = 'false_positive', //    False Positive => The Customer will be created in Napier with all of their information.
    true_match_pep = 'true_match_pep', //True Match (PEP) => Customer will be flagged as High risk in the CRM and after the Senior Management Approval internally (Within the system as well) it will call back Napier to create the customer if approved.
    true_match_sanctions = 'true_match_sanctions', //True Match (Sanctions/Internal watchlist) => Customer will NOT be created in Napier and gets rejected automatically through the CRM.
    true_match_other = 'true_match_other', //True Match for other exception such as withdrawn citizenship
}

export enum ServiceStatusCode {
    authorized_signatory_approval = 'authorized_signatory_approval',
    waiting_for_kyc = 'waiting_for_kyc',
    need_signature = 'need_signature',
    /** DocuSign-signed: waiting for operations (same segment) or CR/admin to confirm the signature is genuine */
    signature_verification = 'signature_verification',
    rejected = 'rejected',
    done = 'done',
    failed = 'failed',
    need_slip = 'need_slip',
    approval = 'approval', //under_review
    review = 'review', //under_review
    need_kyc = 'need_kyc',
    unknown = 'unknown',
    manual = 'manual',
    need_form = 'need_form',
    manual_kyc = 'manual_kyc',
    processing = 'processing',
    waiting_for_execution = 'waiting_for_execution',
    waiting_for_activation = 'waiting_for_activation',
    waiting_for_sub_account_id = 'waiting_for_sub_account_id',
    waiting_for_operations_approval = 'waiting_for_operations_approval',
    need_iban = 'need_iban', //kfhtrade: verified KFH IBAN + account number missing
}

export enum IpoStatusCode {
    need_signature = 'need_signature',
    rejected = 'rejected',
    done = 'done',
    waiting_for_execution = 'waiting_for_execution',
}

export enum TransactionStatusCode {
    waiting_for_kyc = 'waiting_for_kyc',
    need_signature = 'need_signature',
    rejected = 'rejected',
    done = 'done',
    need_slip = 'need_slip',
    need_kyc = 'need_kyc',
    unknown = 'unknown',
    approval = 'approval', //Needs approval from CR team
    authorized_signatory_approval = 'authorized_signatory_approval', // Executive signatory approval after CR
    waiting_for_execution = 'waiting_for_execution', // Final execution from operation team
    waiting_for_geneva = 'waiting_for_geneva', // After the execution from operation team, the transaction will be sent to Geneva for processing.
}

export enum FileTag {
    Passport = 'passport',
    FrontOfCivilID = 'frontOfCivilID',
    BackOfCivilID = 'backOfCivilID',
    PaymentSlip = 'paymentSlip',
    SalaryCertificate = 'salaryCertificate',
    ProofOfInheritance = 'proofOfInheritance',
    ProofOfCredit = 'proofOfCredit',
    ProofOfCompanyOwnership = 'proofOfCompanyOwnership',
    AMLScreening = 'amlScreening',
    finalSignedAgreement = 'finalSignedAgreement',
    Other = 'other',
    TransferCert = 'transferCert',
    KFHConsent = 'kfhConsent',
    ProofOfShares = 'proofOfShares',

    LinkKYC = 'linkKYC',
}

export enum DocumentTag {
    PaciAuth = 'paciAuth',
    ProofOfIncome = 'proofOfIncome',
    RequiredFeesTransfer = 'requiredFeesTransfer-',
}

export const AllDocumentTags = { ...FileTag, ...DocumentTag };

export interface DocType {
    icon: any;
    name: string;
    color: string;
    key: string;
}

export interface KYCState {
    status: string;
    status_code: KYCStatusCode;
    risk_code: KYCRiskCode;
    risk: string;
    created?: Timestamp;
}

export enum LegacyPoliticalPosition {
    // OLD (legacy)
    royalFamilies = 'royalFamilies',
    memberOfParliament = 'memberOfParliament',
    seniorOfficial = 'seniorOfficial',
    seniorExec = 'seniorExec',
    politicalPartyOfficer = 'politicalPartyOfficer',
}

export enum ContractPoliticalPosition {
    // NEW (Contract based)
    headsOfStatesGovernments = 'headsOfStatesGovernments',
    seniorPoliticians = 'seniorPoliticians',
    seniorGovernmentalOfficials = 'seniorGovernmentalOfficials',
    seniorJudicialOfficials = 'seniorJudicialOfficials',
    seniorMilitaryOfficials = 'seniorMilitaryOfficials',
    seniorExecOfStateOwnedCompanies = 'seniorExecOfStateOwnedCompanies',
    seniorOfficialsInternationalOrganizations = 'seniorOfficialsInternationalOrganizations',
}

export const PoliticalPosition = {
    ...LegacyPoliticalPosition,
    ...ContractPoliticalPosition,
} as const;
export type PoliticalPosition =
    | LegacyPoliticalPosition
    | ContractPoliticalPosition;

// NEW (Contract based)
export const newSourcesOfIncome = [
    'job',
    'social_security',
    'investment',
    'student_allowance',
    'private_business',
    'family_support',
    'other',
] as const;

// OLD (legacy)
export const sourcesOfIncome = [
    'salary',
    'loan',
    'profit',
    'inheritance',
    'other',
] as const;

export type SourcesOfIncome =
    | (typeof sourcesOfIncome)[number]
    | (typeof newSourcesOfIncome)[number];

// NEW (Contract based)
export const newSourcesOfWealth = [
    'salary',
    'end_of_service',
    'loans',
    'savings',
    'inheritance',
    'shares',
    'real_estate',
    'precious_metals',
    'investments',
    'other',
] as const;

// OLD (legacy)
export const sourcesOfWealth = [
    'investmentBusinessProfit',
    'inheritance',
    'savings',
    'other',
] as const;

export type SourcesOfWealth =
    | (typeof sourcesOfWealth)[number]
    | (typeof newSourcesOfWealth)[number];

export const averageAnnualIncome = [
    '10000',
    '25000',
    '50000',
    '75000',
    '100000',
];
export type AverageAnnualIncome = (typeof averageAnnualIncome)[number];

export const approximateWealth = ['0', '100000', '250000', '500000', '1000000'];
export type ApproximateWealth = (typeof approximateWealth)[number];

export const investedAmountOfWealth = ['20', '40', '60', '80', '100'];
export type InvestedAmountOfWealth = (typeof investedAmountOfWealth)[number];

export const employmentOptions = [
    'privateBusiness',
    'student',
    'retired',
    'unemployed',
    'employed',
    'other',
];
export type EmploymentOptions = (typeof employmentOptions)[number];

export enum YesNo {
    Yes = 'true',
    No = 'false',
}

export interface PowerOfAttorney {
    name: string;
    nationality: string;
    address: string;
}

export interface onDemandAMLScreeningResponse {
    id: string;
    customer?: {
        customerId: string; //crm ID for customer
    };
    customerId: string;
    screeningRunId: string;
    created: {
        at: string;
        byId: string;
        by: string;
    };
    input: {
        p: Record<string, any>;
    };
    entityType: 'Person' | 'Entity';
    status: string;
    hitsInReviewCount: number;
    hitsManuallyDiscounted: number;
    hitsAutomaticallyDiscounted: number;
    hitsMatched: number;
    hitsReplaced: number;
    hitsTotal: number;
    highestScore: number;
    buId: string;

    screeningHitsOpt: AMLScreeningHits[];
}

export interface AMLScreeningHits {
    id: string;
    screeningResultId: string;
    entityType: 'Person' | 'Entity';
    customerId: string;
    refDataId: string; //"system:refinitiv:2543090",
    matchedName: string;
    status: string;
    policyRecommendation: {
        recommendation: string;
        explanation: string;
        totalScore: number;
        scoreParts: [
            {
                name: string;
                score: number;
            },
        ];
        scoreDetails: [
            {
                comparisonType: string;
                inputValue: string;
                referenceValue: string;
                referenceValueType: string;
                rawScore: number;
                isMatch: boolean;
            },
        ];
        policyTriggeredComparison: [];
    };
    created: {
        at: string;
        byId: string;
        by: string;
    };
    refPrimaryName: string;
    refWatchlistCategories: string[];
    refIntelligenceDataLists: string[];
    score: number;
    scoreParts: {
        name: string;
        score: number;
    }[];
    scoreDetails: {
        comparisonType: string;
        inputValue: string;
        referenceValue: string;
        referenceValueType: string;
        rawScore: number;
        isMatch: true;
    }[];
    refDetails: {
        refGender: string;
        refDateOfBirthOrRegistration: string;
        refCountryInformation: string;
    };
    investigate: boolean;
    screeningRunId: string;
    refWatchlistCategories2: string[];
    buId: string;
    napierReview?: {
        user_email: string;
        review?: string; //comments about this review ?
        review_code: NapierHitsClassification;
        timestamp: Timestamp;
    };
}

// New KYC: Define enum
export enum PoliticallyExposedType {
    Me = 'Me',
    Family = 'Family',
    None = 'None',
}

////ahhhh this is gonna be fun.
export interface KYC {
    id?: string;
    customerID?: string;
    name_en: string;
    name_ar: string;
    civilID?: string;
    require_selling_agent_review?: boolean;
    civilIDExpiryDate?: Timestamp;
    created?: Timestamp;
    politicalPosition?: PoliticalPosition[];
    relativePoliticalPosition?: PoliticalPosition[];
    nationality?: string;
    residence?: string;
    ip?: string;
    birthDate?: Timestamp;
    passportExpiryDate?: Timestamp;
    linkID?: string;
    passportNumber?: string;
    paciData?: PACIData;
    /** The guardian's/representative's PACI data — filled instead of paciData for minors or accounts with someone acting on their behalf */
    guardianPaciData?: PACIData;
    gender?: Gender;
    address?: Address;
    address_from_finder?: AddressFromFinder;
    employment?: {
        [key: string]: any;
    };
    employment_employer?: string;
    employment_yearsOfExperience?: number;
    employment_jobTitle?: string;
    employment_employerPhone?: string;
    // New KYC free-text employment fields consumed by the CRAM AI mapping
    // (Task 7). Stored in contract_links.filledData; surfaced here so the
    // mapper / CR UI are type-safe.
    employment_occupation?: string;
    employment_industry_type?: string;
    /** Set on contract_links.filledData for update (rejection) links so page/field
     *  conditions in the KYC configuration can detect an update flow. */
    kyc_update?: boolean;
    houseID?: string;
    placeOfBirth?: string;
    sourcesOfIncome?: SourcesOfIncome[];
    sourcesOfWealth?: SourcesOfWealth[];
    sourcesOfWealth_other_field?: string;
    sourcesOfIncome_other_field?: string;
    updated_data?: {
        updated_by?: string;
        updated_on?: Timestamp;
        initial_value?: {
            [key: string]: (string | null)[] | number | string;
        }[];
        updated_field_name?: string[];
        updated_fields?: {
            [key: string]: (string | null)[] | number | string;
        }[];
        reason?: string;
    }[];
    averageAnnualIncome?: AverageAnnualIncome;
    /// New KYC
    /// averageAnnualIncome =((sourcesOfMonthlySalary * 12) + sourcesOfBonusesAndOtherAmounts)
    sourcesOfMonthlySalary?: string;
    sourcesOfBonusesAndOtherAmounts?: string;
    approximateWealth?: ApproximateWealth;
    politicallyExposed?: boolean; // Old Version true/false
    politicallyExposedType?: PoliticallyExposedType; // New KYC
    powerOfAttorney?: boolean;
    investmentAwareness?: string;
    outsideKuwait?: string;
    outsideKuwait_address?: string;
    outsideKuwait_zip?: string;
    outsideKuwait_city?: string;
    outsideKuwait_country?: string;
    tin?: Tin[];
    step?: number;
    mobile: string;

    photo?: PDFImage | Uint8Array | ArrayBuffer | string; //base64

    tradeThroughOtherBroker?: boolean;
    tradeThroughOtherBroker_name?: string;
    brokerForOtherMarkets?: boolean;
    brokerForOtherMarkets_name?: string;
    relyOnFinancialConsultations?: boolean;

    investmentExperience?: string[];
    investmentObjectives?: string[];
    riskAppetite?: string[];
    requestedAssetClass?: string[];
    investedAmountOfWealth?: string;
    expectedVolume?: string;
    //for orders
    iban?: string;
    chosenFund?: string;

    memberOfBoard?: boolean;
    declarationInformation?: {
        position: string;
        entity: string;
        market: string;
    }[];

    // Are you the account beneficiary?
    // true if the customer has other beneficiary [guardian, power of attorney]. Also if customer is minor then tradeForBeneficiaries is true.
    // false if customer is main beneficiary
    tradeForBeneficiaries?: boolean;
    beneficiaryInformation?: {
        relation: string;
        name: string;
        beneficiary_civil_id?: string; // If user is Minor and has [guardian, Power of Attorney, Parents] as beneficiary then we need civil ID of beneficiary to verify from PACI
    }[];

    relativePoliticalPositionInformation?: {
        relation: string;
        name: string;
    }[];

    clientNature?: string[]; //I should type these since they are finite natures of clients.
    signature?: PDFImage | Uint8Array | ArrayBuffer | string; //base64
    amount?: number;
    units?: number;
    fundType?: SubscriptionType; // ([ "subscribe"| "redeem"] ) ?
    // updateType?: string // (["submit" | "order"]) ?
    assisted_by?: string;
    state?: KYCState;
    sourceOfIncomeSentence?: string; //This is to show something like these in certain closed ended funds: /* Investments in Funds with KFH.  Savings over the years from work.  Investments from the Kuwaiti Stock Market.  Inheritance from father from real estate activities.  End of Service Indemnity.
    sourceOfWealthSentence?: string; //This is to show something like these in certain closed ended funds: /* Investments in Funds with KFH.  Savings over the years from work.  Investments from the Kuwaiti Stock Market.  Inheritance from father from real estate activities.  End of Service Indemnity.

    tradexSyncStatus?: {
        status: 'pending' | 'success' | 'error';
        message?: string;
    };
    /**
     * Result of pushing this KYC's expiry dates to the KFHTrade OMS
     * (DFN/Mubasher B2B connector, RD-KFHC-2026001-059).  Written by
     * onChangeKYC when the KYC reaches the valid stage.
     */
    kfhtradeOmsKycSync?: {
        status: 'success' | 'failed' | 'skipped' | 'error';
        narration?: string;
        payload?: {
            clientId: string;
            kycExpiryDate: string;
            civilIdExpiryDate: string;
            source: string;
            referenceId?: string;
        };
        at: Timestamp;
    };
    highriskOverrideReason?: string;
    manual?: boolean;
    consent_granted?: boolean;
    // Napier AML
    aml_screening?: onDemandAMLScreeningResponse;
    aml_screening_status?: 'pending' | 'running' | 'completed' | 'failed';
    /** Identifies the screening run allowed to commit results/status. */
    aml_screening_run_id?: string;
    /**
     * On-demand screenings of the parties related to this KYC, run alongside `aml_screening`
     * and keyed by the related party's customer doc id: the authorized signatories of a
     * company or the guardian named on the minor's KYC. Hits here are
     * reviewed through `screeningReview` with `relatedCustomerID`, and the approval gate
     * requires them to be reviewed just like the customer's own hits.
     */
    aml_screening_related?: Record<string, RelatedPartyAMLScreening>;
    // New KYC Data Structure for Contract creation
    completedKYCData?: CompletedKYCData;
    contractType?: ContractType;
    // Based on tags, we can generate specific PDF documents
    pdfTags?: string[];

    // For sub portfolio reason.. Why user needs this sub portfolio?
    sub_portfolio_reason?: string;

    // For the portfolio service only Low, Medium, High
    riskTolerance?: string[];

    interviewFormData?: {
        interviewType?: 'in_person' | 'virtual' | 'phone';
        interviewAddress?: string;
        interviewBy?: string; // employee ID of the interviewer
        interviewDate?: Timestamp;
        entityRequirements?: string;
        generalRemarks?: string;
        intervieweeID?: string; // employee ID of the interviewee from auth authorized_signatories[]
    };

    sourcesOfIncomeEntity?: string; // Entity sourcesOfIncome free text

    // Entity ULTIMATE BENEFICIAL OWNERS
    beneficiaries?: CompanyRegistryBeneficiary[];

    // Controlling Persons
    board_of_directors?: ControllingPerson[];
    executive_management?: ControllingPerson[];

    // Account Beneficiary
    account_beneficiary?: {
        name: string;
        relation: string;
    }[];

    // Authorized Signatories have any board membership
    authorized_signatories_board_membership?: {
        entity_name: string;
        exchange_market: string;
        name: string;
        role: string;
    }[];
    // ENTITY (company) KYC: used as the expiry date on the company's KCC sub-account request row
    registration_expiry_date?: Timestamp | string;

    // CONNECTED PARTIES
    entity_subsidiaries?: {
        ownership_name: string;
        ownership_percentage: string;
        ownership_type: string;
    }[];

    // CRAM per-KYC immutable snapshot (Task 4). Written by
    // submitContractKYC / updateContractKYC after the engine recompute on
    // submission. Carries the full result (breakdown + listSnapshot) so
    // historic ratings stay reproducible when AML later edits lists.
    cram?: KycCramSnapshot;
    // CRAM-team override of the engine's final rating. `cram` keeps being
    // recomputed underneath this, so the score and breakdown stay live while
    // the rating stays pinned. Never read this alongside `cram.rating` by
    // hand — use cramHelper.resolveEffectiveCramRating.
    cramOverride?: CramOverride;
    // CRAM AI industry/occupation mapping (Task 7). Latest result mirrored
    // here; every run is also appended to the kyc/{id}/ai_mapping_history
    // subcollection.
    aiMapping?: KycCramAiMapping;
    // Set when CR / admin spends the one manual "Call AI" mapping run allowed
    // per KYC. Present-or-absent is the whole allowance check.
    aiMappingManualRunAt?: Timestamp;

    /// minor child KYC data
    guardianCustomerID?: string;
    guardianKycID?: string;
    // Denormalized copy of the guardian's KYC state, written onto the minor's
    // KYC by the kycRefresh fan-out. Status gates the minor's review/approve;
    // risk is the rating the minor inherits (a minor is never rated on its
    // own — see resolveKycRiskSource in functions/src/helpers.ts).
    guardianKycState?: {
        status?: string;
        status_code?: string;
        risk?: string;
        risk_code?: string;
    }
}

export interface UpdateLinkCustomClasses {
    after_class: string;
    class_name: string;
}

export interface FirebaseCustomerBankAccount {
    iban?: string;
    account?: string;
    verified?: boolean;
    verifiedBy?: string;
    created?: Timestamp;
    type?: MessageType;
}

export interface UpdateOrderRequest extends FirebaseCustomerBankAccount {
    transferDate?: Date;
    slipFilePaths?: string[];
}
export interface AddAccountRequest extends FirebaseCustomerBankAccount {
    iban: string;
    id: string;
    /// This is used for Mobile app check if the account is not verified then upload file.
    fileName?: string;
    currency?: string;
}

export interface Tin {
    country: string;
    available?: boolean;
    number?: string;
    type?: string;
}

export interface Address {
    StreetName?: string;
    PaciBuildingNumber?: string;
    Area?: string;
    FloorNumber?: string;
    Governerate?: string;
    UnitType?: string;
    BuildingNumber?: string;
    UnitNumber?: string;
    BlockNumber?: string;
    DetailsEnglish?: string;
    DetailsArabic?: string;
}

//So this is really only used for the address from Kuwait Finder.
//I need it because the address from PACI is in Arabic, which won't work for closed ended funds.
export interface AddressFromFinder {
    ID: string;
    ResultSource: string;
    FeatureType: string;
    TitleArabic: string;
    TitleEnglish: string;
    DetailsArabic: string;
    DetailsEnglish: string;
    X: number;
    Y: number;
    Distance?: number;
    GovernorateArabic: string;
    GovernorateEnglish: string;
    NeighborhoodArabic: string;
    NeighborhoodEnglish: string;
    BlockArabic: string;
    BlockEnglish: string;
    StreetArabic: string;
    StreetEnglish: string;
    ParcelArabic: string;
    ParcelEnglish: string;
    HouseArabic: string;
    HouseEnglish: string;
    Unit?: null;
    CivilID: number;
}

export type AddressKeys = keyof Address;

export interface PersonalData {
    NationalityAr: string;
    BloodGroup: string;
    Address?: Address;
    NationalityEn: string;
    Photo: string;
    Gender: string;
    DocumentNumber: string;
    EmailAddress: string;
    CivilID: string;
    MobileNumber: string;
    FullNameAr: string;
    // Sometimes card serial is not available, Ex. new cards
    CardSerial?: string;
    CardExpiryDate: string;
    PassportNumber: string;
    GovData?: {
        SponsorName: string;
        CivilIdExpiryDate: string;
    };
    FullNameEn: string;
    BirthDate: string;
}

export interface PACIData {
    ResultDetails: {
        SignatureData: string;
        UserAction: string;
        SigningDatatype: string;
        UserCivilNo: string;
        UserCertificate: string;
        ResultCode: string;
        HashAlgorithm: string;
        TransactionDate: string;
        midRequestID: string;
    };

    PersonalData: PersonalData;
    Signature: {
        SignatureData: string;
        SigningCertificate: string;
    };
    RequestDetails: {
        AdditionalData: string;
        RequestID: string;
        ServiceProviderId: string;
        CivilNo: string;
        RequestType: string;
    };
}

export type MessageType =
    | 'submit'
    | 'order'
    | 'none'
    | 'upload_docs'
    | 'needs_update'
    | 'broadcast'
    | 'kfhtrade'
    | 'kfhbrokerage'
    | 'portfolio'
    | 'agreement'
    | 'ipo'
    | 'transaction'
    | 'needs_agent_update'
    | 'customer_kyc';

export interface TwilioContentResponseType {
    meta: {
        page: number;
        page_size: number;
        first_page_url: string;
        previous_page_url: string | null;
        url: string;
        next_page_url: string | null;
        key: string;
    };
    contents: TwilioContentType[];
}

export interface TwilioContentType {
    language: string;
    date_updated: string;
    variables: {
        [key: string]: string;
    };
    friendly_name: string;
    account_sid: string;
    url: string;
    sid: string;
    date_created: string;
    types: {
        [key: string]: {
            body: string;
        };
    };
    whatsappApproval?: {
        category?: string;
        status?: string;
        reason?: string;
    } | null;
    links: {
        approval_fetch: string;
        approval_create: string;
    };
    label?: string;
    value?: string;
}

/**
 * WhatsApp chat threads live in the top-level whatsapp_threads collection,
 * keyed by the client's phone number in E.164 form (e.g. "+96565189191").
 * Threads are keyed by phone rather than customer ID because inbound Twilio
 * messages only carry a phone number, one number can belong to several
 * customer docs (guardian/minor), and unknown numbers still need a home.
 * All writes happen through cloud functions; the webapp only reads (plus
 * resetting unreadCount).
 */
export interface WhatsappThread {
    id?: string; // E.164 number, set from the doc ID
    mobile: string; // same as id, kept as a field for queries/display
    customerIds: string[]; // customer doc IDs whose mobile matches this number
    customerNames?: { id: string; name_en?: string | null; name_ar?: string | null }[]; // stamped by the webhook so the inbox renders without extra reads
    profileName?: string; // WhatsApp profile name from the last inbound message
    lastMessageAt?: Timestamp; // chat messages only — template sends deliberately don't set this, keeping template-only threads out of the inbox
    lastInboundAt?: Timestamp; // drives the 24h reply window
    lastTemplateAt?: Timestamp;
    lastMessageBody?: string;
    lastMessageDirection?: WhatsappChatDirection;
    unreadCount?: number;
}

export type WhatsappChatDirection = "inbound" | "outbound";

// Mirrors Twilio message statuses we care about; inbound messages are just "received".
export type WhatsappChatStatus = "received" | "queued" | "sent" | "delivered" | "read" | "failed" | "undelivered" | "canceled";

export interface WhatsappChatMediaFile {
    path: string; // Cloud Storage path (whatsapp_media/...)
    contentType: string;
}

export interface WhatsappChatMessage {
    id?: string;
    direction: WhatsappChatDirection;
    type?: "chat" | "template"; // absent means chat; "template" = mirrored outbound template send (campaigns, KYC links, etc.)
    templateName?: string | null;
    templateSid?: string | null;
    sendType?: string; // the send flow that produced a template message ("campaigns", "sms", "notification", ...)
    smsDocID?: string | null;
    body: string;
    mediaFiles?: WhatsappChatMediaFile[];
    twilioSid?: string;
    status: WhatsappChatStatus;
    errorCode?: string;
    errorMessage?: string;
    timestamp: Timestamp;
    profileName?: string; // inbound only
    sentBy?: { // outbound only
        uid: string;
        email?: string;
        name?: string;
    };
}

export interface SendWhatsappChatMessageRequest {
    threadID: string;
    body: string;
}

export interface UpdateLink {
    expires?: Timestamp;
    customerID: string;
    updateType: MessageType;
    id?: string;
    mobile?: string;
    passportRequired?: boolean;
    paymentSlipRequired?: boolean;
    usePACI?: boolean;
    useWhatsapp?: boolean;
    useEmail?: boolean;
    paciData?: PACIData;
    classes_to_add?: UpdateLinkCustomClasses[];
    uploaded_documents?: string[];
    selectedWhatsappTemplate?: TwilioContentType;
    selectedWhatsappTemplateID?: string;
    selectedEmailTemplate?: string | null;
    updateKycID?: string; // to be used for updating the kyc when needs update in specific cases
    kyc?: KYC; //this is the previous KYC data to show up pre-filled.
    filledData?: KYC; //this is the data the customer is actually filling in right now.  Could be incomplete.

    //for orders
    orderID?: string;
    orderInfo?: Order;
    serviceID?: string;
    serviceInfo?: Service;
    ipoRequestID?: string;
    ipoRequestInfo?: IpoRequestObject;
    transactionID?: string;
    transactionInfo?: BasicTransaction;

    currency?: string;
    skipKYC?: boolean; //Sometimes orders don't need KYCs to be filled in (like redemptions), therefore just handling order data is enough.

    //for SMS
    senderID?: string;
    smsMessage?: string;
    smsDocID?: string;
    assisted_by?: string;

    //other linkInfo data
    online?: boolean;
    disableFundTypeChange?: boolean;
    disableFundChange?: boolean;
    disableFundAmountChange?: boolean;
    showInherentanceDetails?: boolean;
    professionalOnly?: boolean;
    englishOnly?: boolean;
    needsDocuments?: boolean;
    multiples?: number;
    documentsToInclude?: [{ [key: string]: any }];
    classes_to_remove?: string[];
    manual?: boolean;
    //TODO: Might need to rename this as not to conflict with the agreement object.
    agreements?: {
        checkboxes: {
            label_ar: string;
            label_en: string;
            id: string;
            link?: string;
        }[];
    };
    createOnSubmit?: boolean;
    skipCustomerCheck?: boolean; // we're skipping all the customer creation checks and creations with this flag

    needed_documents?: string[];
    agreement?: Agreement;
    // transaction_info?: BasicTransaction,
    consent_granted?: boolean;
    initial_option?: Agreement;
    bank_info?: Account;
    postSubmitNoteEn?: string; //this is the note that will be shown in the KYC document.
    postSubmitNoteAr?: string; //this is the note that will be shown in the KYC document.
}

export interface PaciVerificationLink {
    created?: Timestamp;
    expires?: Timestamp;
    customer_id: string; // CUSTOMER CIVIL ID ONLY
    request_type: PaciVerificationType;
    id?: string;
    paci_data?: PACIData;
    requested_PACI_at?: Timestamp;
    recieved_PACI_at?: Timestamp;
    /** Written by paciVerificationCallback (the field above is a legacy misspelling). */
    received_PACI_at?: Timestamp;
    is_verified?: boolean;
}

export type PaciSignatureStatus =
    | 'requested'        // doc created, not yet sent to the signing service
    | 'sent'             // SignDocumentWithExternalUserAsync accepted our request
    | 'request_failed'   // the signing service call errored
    | 'callback_received'; // the signing service posted a result back

// A string that was too large for the Firestore doc and lives in Cloud Storage
// instead (paci_signatures/<docId>/...).  Base64 content is decoded on upload,
// so the object in the bucket is the actual file.
export interface PaciSignatureStorageRef {
    __storage: true;
    bucket: string;
    path: string;
    contentType: string;
    size: number;          // bytes in storage
    base64Length: number;  // length of the original string
    encoding: 'base64' | 'utf8';
    sha256: string;
    preview: string;       // first 64 chars of the original string
}

// paci_signatures collection.  Testing integration with the KFH digital
// signing service (KFHDigitalSigningService 1.0): every payload is stored raw
// (requestResponse, callback, statusChecks) until the live response shape is
// known.  Strings over 64KB anywhere in a payload are replaced by a
// PaciSignatureStorageRef.
export interface PaciSignature {
    id?: string;
    created?: Timestamp;
    status?: PaciSignatureStatus;
    civilID?: string;
    customerID?: string;
    linkID?: string;
    externalRequestId?: string; // our correlation id sent as signerObj.ExternalRequestId (defaults to the doc id)
    requestId?: string | null;  // the signing service's RequestId, when it returned one
    callbackUrl?: string;
    requestIp?: string | null;
    requestContext?: Record<string, any>;  // everything in the request body except signerObj
    requestSent?: Record<string, any>;     // what we posted (Data offloaded to storage, SecurityToken redacted)
    requestResponse?: any;                 // raw SignDocumentWithExternalUserAsync response
    requestResponseHttpStatus?: number;
    requestResponseHeaders?: Record<string, any>;
    requestResponseAt?: Timestamp;
    requestError?: Record<string, any>;
    callback?: Record<string, any>;        // last raw callback (body, headers, query, ip)
    callbacks?: Record<string, any>[];
    callbackCount?: number;
    lastStatusCheck?: Record<string, any>; // last raw SignDocumentWithExternalUserGetStatus response
    statusChecks?: Record<string, any>[];
    statusCheckCount?: number;
    unmatched?: boolean;        // callback that could not be matched to a request
    storedTruncated?: boolean;  // payload exceeded Firestore's 1MiB limit; large strings were truncated
    storedAsRawJson?: boolean;  // even the truncated write failed; payload kept in rawJson
    rawJson?: string;
    rawJsonLength?: number;
    rawJsonAt?: Timestamp;
}

// [approval] =  Waiting for approval (CR)
// [waiting_for_execution] = Waiting For Execution (Operations)
//change to enum ?
export type ActionType =
    | 'approve'
    | 'reject'
    | 'review'
    | 'operations_approve'
    | 'needs_update'
    | 'update'
    | 'authorized_signatory_approve'
    | 'execution'
    | 'upload_docs'
    | 'approval'
    | 'done'
    | 'activation'
    | 'add_subaccount_id'
    | 'selling_agent_review'
    | 'needs_agent_update'
    | 'interview_form_data_added'
    | 'signature_link_sent'
    /** Operations confirmed a DocuSign signature is genuine (same-segment KFH operations, or KFH Capital CR/admin). */
    | 'verify_signature'
    /** Operations rejected a DocuSign signature; the product drops back to need_signature and a new DocuSign link is sent. */
    | 'reject_signature';
export type ActionLevel = 'normal' | 'executive';

/** Human labels for approval-trail action types (shared by the webapp timeline and the services Typesense sync). */
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
    approve: 'Approved',
    reject: 'Rejected',
    review: 'Reviewed',
    operations_approve: 'Operations approved',
    needs_update: 'Needs update',
    update: 'Updated',
    authorized_signatory_approve: 'Authorized signatory approved',
    execution: 'Executed',
    upload_docs: 'Uploaded documents',
    approval: 'Approval (CR)',
    done: 'Done',
    activation: 'Activated',
    add_subaccount_id: 'Added subaccount ID',
    selling_agent_review: 'Selling agent reviewed',
    needs_agent_update: 'CR requesting updates from selling agent',
    interview_form_data_added: 'Interview form data added',
    signature_link_sent: 'Signature link sent',
    verify_signature: 'DocuSign signature verified',
    reject_signature: 'DocuSign signature rejected (new signature requested)',
};

/** Approval-trail action types that are performed by the customer rather than by KFHC/KFH staff. */
export const CUSTOMER_ACTION_TYPES: ReadonlySet<string> = new Set(['submit', 'update', 'upload_docs']);

export type ServiceLastActionActor = 'customer' | 'team' | 'system';

/**
 * The most recent thing that happened to a service, across the service's own
 * approval trail, its linked KYC, payments and automated steps.  Computed by
 * functions/src/helpers.ts (removeServiceUnsyncedKeys) and stored ONLY on the
 * Typesense services document, never on the Firestore service document.
 * `date` is a Firestore Timestamp when built and unix seconds once in Typesense.
 */
export interface ServiceLastAction {
    date: Timestamp | number;
    type: string;
    msg: string;
    by?: string;
    actor: ServiceLastActionActor;
}
export type PaciVerificationType =
    | 'register'
    | 'forgetPassword'
    | 'KYC'
    | 'contractKYC'
    | 'updateInfo';

export type CustomerUpdateRequestStatus = 'pending' | 'done';

export type CustomerUpdateField = 'mobile' | 'email' | 'iban';

export interface CustomerUpdateRequestChange {
    field: CustomerUpdateField;
    old_value?: string;
    /** for 'iban' this is the added account; accounts are additive so there is no old_value */
    new_value: string;
}

/**
 * A customer-initiated info update, authenticated via PACI (request_type
 * 'updateInfo') plus an OTP to the new mobile number.  One request can carry
 * any combination of changes (mobile, email, iban) — see `changes`.  The
 * customer doc / accounts subcollection are updated immediately; this doc
 * remains as the operations queue entry when the change must also be entered
 * manually on the trading system (which has no API).
 */
export interface CustomerUpdateRequest {
    id?: string;
    customerID?: string;
    created?: Timestamp;
    status: CustomerUpdateRequestStatus;
    channel: 'mobile_app' | 'staff' | 'link';
    changes: CustomerUpdateRequestChange[];
    /** contract_links doc id, when the update came in through an info_update link */
    link_id?: string;
    /** id of the archived paci_verifications doc that authenticated this request */
    paci_verification_id?: string;
    /** how ownership of the new value was proven: it matched the PACI record, or an OTP was verified */
    mobile_verified_by?: 'paci' | 'otp';
    email_verified_by?: 'paci' | 'otp';
    /** true when the customer has trading accounts, so ops must re-enter the change on the trading system */
    needs_trading_system_entry?: boolean;
    /** trading account ids for ops reference (kfhtrade / tradex / subaccounts) */
    trading_account_ids?: string[];
    name_en?: string;
    name_ar?: string;
    civilID?: string;
    assisted_by?: string;
    executed_by?: string;
    executed_at?: Timestamp;
}

/** Shape of an update request document in the Typesense index (see
 *  removeUpdateRequestUnsyncedKeys in functions). Timestamps become unix seconds. */
export interface TypesenseUpdateRequest {
    id: string;
    customerID: string;
    customerObj: {
        id: string;
        name_en: string;
        name_ar: string;
        civilID: string;
    };
    changes: CustomerUpdateRequestChange[];
    changed_fields: CustomerUpdateField[];
    status: CustomerUpdateRequestStatus;
    channel: string;
    assisted_by: string;
    executed_by: string;
    executed_at?: number;
    created?: number;
    needs_trading_system_entry: boolean;
    trading_account_ids?: string[];
    link_id: string;
}

/** Any combination of the new* fields may be present in one request. */
export interface InfoUpdateRequest {
    /** customer unified id */
    id: string;
    newMobile?: string;
    newEmail?: string;
    newIban?: string;
    /** storage path of the uploaded salary certificate for the new IBAN */
    ibanFileName?: string;
    ibanCurrency?: string;
    /** paci_verifications doc id from the 'updateInfo' PACI request */
    paciVerificationId?: string;
    /**
     * OTP sent to the new mobile via sendMobileOTP.  Not needed when the new
     * mobile matches the number on the PACI record.
     */
    otp?: string;
    encrypted_key?: string;
    iv?: string;
    /**
     * OTP sent to the new email via sendEmailOTP.  Not needed when the new
     * email matches the address on the PACI record.
     */
    emailOtp?: string;
    email_encrypted_key?: string;
    email_iv?: string;
}

/**
 * Staff-initiated: create an info_update contract link and WhatsApp it to an
 * arbitrary mobile number.  The customer is discovered after PACI.
 */
export interface SendInfoUpdateLinkRequest {
    /** the number the client asked to be reached on — need not match any customer record */
    mobile: string;
    updatable_fields: CustomerUpdateField[];
    /** optional: lock the link to a known customer's civil ID (PACI must then match it) */
    civilID?: string;
}

export interface SendInfoUpdateLinkResult extends BaseResult {
    linkID?: string;
}

/** Client-side submission of an info_update contract link (linkID is the bearer token). */
export interface SubmitInfoUpdateRequest extends Omit<InfoUpdateRequest, 'id' | 'paciVerificationId'> {
    linkID: string;
}

export interface FirebaseToken {
    identities: {
        [key: string]: any;
    };
    /**
     * The ID of the provider used to sign in the user.
     * One of `"anonymous"`, `"password"`, `"facebook.com"`, `"github.com"`,
     * `"google.com"`, `"twitter.com"`, `"apple.com"`, `"microsoft.com"`,
     * `"yahoo.com"`, `"phone"`, `"playgames.google.com"`, `"gc.apple.com"`,
     * or `"custom"`.
     *
     * Additional Identity Platform provider IDs include `"linkedin.com"`,
     * OIDC and SAML identity providers prefixed with `"saml."` and `"oidc."`
     * respectively.
     */
    sign_in_provider: string;
    /**
     * The type identifier or `factorId` of the second factor, provided the
     * ID token was obtained from a multi-factor authenticated user.
     * For phone, this is `"phone"`.
     */
    sign_in_second_factor?: string;
    /**
     * The `uid` of the second factor used to sign in, provided the
     * ID token was obtained from a multi-factor authenticated user.
     */
    second_factor_identifier?: string;
    /**
     * The ID of the tenant the user belongs to, if available.
     */
    tenant?: string;
    [key: string]: any;
}
export interface Approval {
    date: Timestamp;
    ip?: string;
    name: string;
    title?: string;
    type: ActionType;
    level?: ActionLevel;
    info?: FirebaseToken;
    id?: string;
    comments?: string;
    missingFiles?: FileTag[];
    is_authorized_signatory?: boolean;
    signatureData?: EmployeeSignature;
}

export interface ProcessActionsDocument {
    approvals?: Approval[];
}

export interface Action {
    action: ActionType;
    token: string;
    comments?: string;
    //TODO: I should have something generic here on what item is being actioned on
    transactionID?: string;
    kycID?: string;
    customerID?: string;
    orderID?: string;
    serviceID?: string;

    //for SMS
    sendSMS?: boolean;
    useWhatsapp?: boolean;

    linkInfo?: Partial<UpdateLink>;
    missingFiles?: FileTag[];
}
export const validActionTypes: ReadonlySet<ActionType> = new Set<ActionType>([
    'approve',
    'reject',
    'review',
    'operations_approve',
    'needs_update',
    'needs_agent_update',
    'update',
    'authorized_signatory_approve',
    'execution',
    'upload_docs',
    'approval',
    'done',
    'activation',
    'add_subaccount_id',
    'selling_agent_review',
    'interview_form_data_added',
    'signature_link_sent',
    'verify_signature',
    'reject_signature',
]);
//microsoft crap
export type ODataResponse<T> = {
    [key: string]: any;
    value: T[];
};

export type FundID =
    | '901'
    | '902'
    | '500'
    | '201'
    | '200'
    | 'Pfn6bBMWKckyFcr2EKPJ'
    | 'usFund'
    | 'XY3ENvdw0UEtjQgLeABZ'
    | '3006'
    | '1008';

export interface OrdersPath {
    metaKey?: string;
    path: string;
    doNotFlatten: boolean;
    title: string;
    agreementType?: 'voting' | 'nonvoting';
    sourcesOfIncomeTransformers?: Record<string, string>;
    sourcesOfWealthTransformers?: Record<string, string>;
    addFields?: Record<string, string>;
}

export type EditableComponentType =
    | 'MultiSelect'
    | 'Select'
    | 'TextInput'
    | 'NumberInput'
    | 'Timestamp'
    | 'NavTimestamp';

export type FundState =
    | 'internal_only'
    | 'commitment_only'
    | 'ready_for_investors'
    | "exceptions_only_min_amount"
    | 'closed_to_new_investors';

export interface Fund {
    id: FundID;
    navCampaign?: NavCampaignConfig;
    currency: string;
    name_en: string;
    // name?: string,
    name_ar: string;
    name?: string;
    distribution_frequency?: 'monthly' | 'quarterly' | 'yearly';
    nav_per_unit?: number;
    disable_duplicate_orders?: boolean;
    nav_update_day_of_week?: number;
    nav_date?: Timestamp;
    closed_ended_start_date?: Timestamp;
    lastSyncDate?: Timestamp;
    latest_available_date?: Timestamp | Date | string | number;
    syncStatus?: 'NEED_SYNC' | 'SYNCING' | 'DONE' | 'ERROR';
    auto_sync?: boolean;
    moneyCollectionDate?: Timestamp;
    latest_deal_date?: Record<string, Timestamp>;
    latest_deal_date_updated_at?: Timestamp;
    updated_at: Timestamp;
    overhead_percent: number;
    min_units: number;
    subscription_fees_percent: number;
    redemption_fees_percent: number;
    // multiples_of?: number,
    multiples_of_units?: number;
    iban: string;
    new_nav_awaiting_approval?: {
        nav_per_unit: number;
        nav_date: Timestamp;
        updatedBy: string;
    };
    nav_update_state?: {
        status: string;
        code: string;
        updated_at: Timestamp;
    };
    /**
     * @deprecated Distributions moved to `funds/{fundID}/updates` (type: 'dividend').
     * Nothing reads this any more — the NAV performance calculation and the product
     * catalogue both source distributions from that subcollection. Kept only so existing
     * documents still type-check until `utils/backfillFundDividends.js` has run everywhere.
     */
    dividends?: Dividend[];
    image?: any;
    englishOnly?: boolean;
    visible?: boolean; // fi fund shoudl show to client on mobile
    passportRequired?: boolean;
    professionalOnly?: boolean;
    needsDocuments?: boolean; //this is for closed ended funds where they ask for source of income documents, etc.  Basically it'll treat everyone as High Risk.
    type: 'closed_ended' | 'open_ended' | 'etf';
    basket_size?: number; //ETF shares per creation/redemption unit (basket)
    etf_cutoff?: string; //ETF "HH:mm" Kuwait time - creations/redemptions sent to KCC after it need an Operations late reason, e.g. "14:00". Required to send to KCC
    etf_security_id?: string; //ETF listed security in the securities collection (market price for the AP gain estimate) - not used yet
    pcf?: { //static header fields for the ETF PCF (Portfolio Composition File) endpoint
        basket_name?: string;
        issuer_name?: string;
        trading_id?: string;
        ticker?: string;
        disclaimer?: string;
    };
    subscription_tiers?: SubscriptionTier[];
    fees_included_in_transferred_amount?: boolean;
    voting_units?: number;
    code?: string;
    kfhc_website_id?: string;
    issued_units?: number;
    net_assets?: number;
    returns_since_inception?: string;
    minimum_holding?: number;
    minimum_redemption?: number;
    short_name?: string;
    acct?: string;
    private?: boolean;
    weekly_annualized_return?: number;
    yearly_annualized_return?: number;
    mtd?: number;
    ytd?: number;
    itd?: number;
    highest_nav?: number;
    lowest_nav?: number;
    highest_nav_date?: Timestamp;
    lowest_nav_date?: Timestamp;
    inception_date?: Timestamp;
    par_value?: number;
    asset_class?: String[];
    fund_state?: FundState;
    orders_path?: {
        subscription?: OrdersPath[];
        redemption?: OrdersPath[];
    };
    sync_with_tradex?: boolean; //this determines if we can add orders to tradex for this fund.  Tradex is removed, but this is still used for syncing orders from portal to geneva.  Maybe needs to be renamed.
    custodian_account?: string;
    burgan_cutoff_date?: Timestamp;
    subscription_cutoff?: CutoffSpec;
    redemption_cutoff?: CutoffSpec;
    targets?: Record<string, number>; //basically the Segment -> target to achieve for that fund in terms of sales amount.  mainly for closed ended
    require_selling_agent_review?: boolean;
    kyc_product?: string; // This subscription product id
    kyc_redemption_product?: string; // This redemption product id
    certificates_ready?: boolean,
    fund_manager_emails?: string[];
    geneva_investment?: string; //the security id in geneva.  If set, new navs are pushed to geneva as price records.
    fundraising_campaign?: {
        rm_points_per_amount: number, // gives one point to the rm per this amount once sold, eg: rm_points_per_amount is 10k and rm sold 15 k they are rewarded 1 point, no half points
        start_date: Timestamp;
        end_date: Timestamp;
        segments: SegmentType[];
    }[];
}

// ---- ETF data served by functions-geneva (getEtfData callable / etfNav|etfHoldings|etfBasket|etfPcf endpoints) ----

export interface EtfApiHolding {
    security: string;
    ticker: string;
    name: string;
    quantity: number;
    latestPrice: number; //close price in the constituent's local trading currency
    marketValue: number; //fund base currency
    marketValueLocal: number;
    currency: string;
    fxRate: number;
    bonusShares: number;
    sector: string;
    exchange: string;
    cash: boolean;
}

export interface EtfNavData {
    fund: string;
    nav: number;
    navDate: string | null;
    issuedUnits: number | null;
    updatedAt: string | null;
    stale: boolean;
}

export interface EtfHoldingsData {
    fund: string;
    asOf: string | null;
    updatedAt: string | null;
    holdings: EtfApiHolding[];
}

export interface EtfBasketData {
    fund: string;
    basketSize: number;
    issuedUnits: number;
    ratio: number;
    asOf: string | null;
    navDate: string | null;
    stale: boolean;
    cashPerCreationUnit: number;
    dividendDuePerCreationUnit: number;
    estimatedCashPerCreationUnit: number;
    basket: EtfApiHolding[];
}

//config/etf_identifiers.securities - keyed by Boursa ticker, edited on /etf/identifiers, fallback for ISIN/SEDOL
//until the replicator syncs them onto the securities docs
export type EtfIdentifiers = Record<string, { name?: string; isin?: string; sedol?: string }>;

export interface EtfPcfConstituent {
    securityDescription: string;
    securityCode: string; //Geneva investment code
    isin: string;
    sedol: string;
    cusip: string;
    basketQuantity: number;
    bonusSharesPending: number; //bonus shares / rights announced but not credited yet, per creation unit
    tradingCurrency: string;
    cleanMarketPriceLocal: number;
    marketPriceLocal: number;
    marketValueLocal: number;
    fxRate: number;
    marketValueBase: number;
    benchmarkWeightBase: number;
    baseDividendRate: number | null;
    alternativeTicker: string;
}

export interface EtfPcfData {
    fund: string;
    basketName: string;
    issuerName: string;
    tradingId: string;
    tradingTicker: string;
    evaluationDate: string;
    sharesPerCreationUnit: number;
    totalConstituents: number;
    baseCurrency: string;
    officialNav: number;
    navDate: string | null;
    stale: boolean;
    sharesOutstanding: number;
    //published cash = position cash + dividends due, scaled by sharesPerCreationUnit / sharesOutstanding
    cashPerCreationUnit: number;
    dividendDuePerCreationUnit: number;
    estimatedCashPerCreationUnit: number;
    //cross-check: NAV x CU (valued at navDate) - basket MV (valued at positionAsOf close prices)
    navImpliedCashPerCreationUnit: number;
    navValuePerCreationUnit: number;
    basketMarketValueBase: number;
    basketMarketValueLocal: number;
    positionAsOf: string | null;
    positionUpdatedAt: string | null;
    navPositionDateMismatch: boolean;
    disclaimer: string;
    constituents: EtfPcfConstituent[];
}

//etf_api_logs: one row per external API call, webapp refresh / sync click, or NAV landing on the fund doc
export interface EtfApiLog {
    id?: string;
    endpoint: string; //etfNav | etfHoldings | etfBasket | etfPcf | getEtfData | syncNav | navUpdated
    fund: string;
    status: number;
    source: 'api' | 'webapp' | 'geneva';
    entity?: 'boursa' | 'gtn' | 'kcc'; //which api key authenticated an external call
    callerIp?: string;
    user?: string;
    stale?: boolean | null;
    details?: Record<string, unknown>;
    timestamp?: Timestamp;
}

//funds/{fundId}/private/inav: last intraday NAV pushed by GTN (POST /etfInav) and pulled by Boursa (GET /etfInav),
//overwritten every ~15s - never logged, the page colours the card by the age of receivedAt
export interface EtfInav {
    inav: number;
    asOf: string | null; //calculation time sent by the agent (ISO)
    receivedAt?: Timestamp;
    source: 'boursa' | 'gtn' | 'kcc';
    callerIp?: string;
    pulledAt?: Timestamp;
    pulledBy?: 'boursa' | 'gtn' | 'kcc';
}

//etf_inav_test/current: last GTN QA feed test run (testGtnInav in functions-geneva), rewritten at most once a second
//while live. Times are epoch ms so functions-geneva shares this type. Never touches funds/{id}/private/inav.
export interface GtnInavTest {
    status: 'connecting' | 'live' | 'done' | 'error';
    symbol: string;
    startedBy: string;
    startedAt: number;
    endsAt: number;
    entitlements?: string[];
    session?: string;
    inav?: number;
    ludt?: string; //GTN calculation time, ISO UTC
    receivedAt?: number;
    updates: number;
    heartbeats: number;
    lastHeartbeatAt?: number;
    ticks: { p: number; ludt: string; receivedAt: number }[]; //newest first, last 20
    closed?: string;
    error?: string;
}

//etf_api_status/{fundId}: last call per endpoint, maintained alongside every etf_api_logs write
export interface EtfApiStatus {
    lastCalls?: Record<string, EtfApiLog>;
}

//funds/{fundId}/etf_requests/{id}: AP in-kind creation (subscription) / redemption of N baskets. Written only by the
//functions-geneva callables in etf_requests.ts; the webapp just reads. Flow: AP submits -> FM approves -> Operations
//sends to KCC (cut-off check) -> KCC processed -> Operations books Geneva (legs + journal + GWI order).
export type EtfRequestType = 'subscription' | 'redemption';
export type EtfRequestStatus = 'pending_fm_approval' | 'pending_operations' | 'with_kcc' | 'pending_geneva_booking' | 'booked' | 'rejected' | 'kcc_rejected' | 'cancelled';
export type EtfKccStatus = 'Registered' | 'Approved' | 'Created' | 'Accepted' | 'Processed' | 'Rejected';
export type EtfRequestActionName = 'submitted' | 'snapshot_refreshed' | 'approved' | 'rejected' | 'cancelled' | 'sent' | 'kcc_simulated' | 'booked' | 'undone';

export interface EtfRequestConstituent {
    securityCode: string; //Geneva investment code
    ticker: string;
    name: string;
    quantity: number; //basket quantity x baskets
    price: number; //D-1 close from the PCF
    value: number; //quantity x price, 3dp - the exact number booked in Geneva
}

//binding basket for the request, taken from the PCF published on `date` (prices = the D-1 close the NAV was struck on)
export interface EtfRequestSnapshot {
    date: string; //YYYY-MM-DD Kuwait day the snapshot is valid for = the booking day
    navDate: string;
    positionAsOf: string;
    navPerUnit: number;
    constituents: EtfRequestConstituent[];
    securitiesValue: number;
    cashComponent: number; //balancing cash: units x navPerUnit - securitiesValue (may be negative)
    total: number; //units x navPerUnit - what GWI values the units at
}

export interface EtfRequestAction {
    by: string;
    at: Timestamp;
    reason?: string;
}

export interface EtfGenevaLeg {
    key: string; //geneva_responses key, e.g. leg_KFH
    recordType: 'ReceiveLong' | 'DeliverLong';
    record: Record<string, unknown>;
}

export interface EtfGenevaResponse {
    status: string | null; //'adding' | 'error' while we publish, then the Geneva loader status (Success / Error ...)
    status_message?: string | null;
    key_value?: string | null;
    code?: string | null;
    timestamp?: Timestamp;
}

export interface EtfRequest {
    id?: string;
    fund: string;
    type: EtfRequestType;
    baskets: number;
    units: number; //baskets x basket_size
    status: EtfRequestStatus;
    snapshot: EtfRequestSnapshot;
    createdAt: Timestamp;
    actions: Partial<Record<EtfRequestActionName, EtfRequestAction>>;
    lateApproval?: { by: string; at: Timestamp; date: string; reason: string }; //sent to KCC after etf_cutoff
    kcc?: {
        mode: 'simulated';
        apBpid: string | null;
        basketRef?: string;
        requestRef?: string;
        status: EtfKccStatus;
        rejectStage?: string;
        rejectReason?: string;
        history: { status: EtfKccStatus; at: string; note?: string }[]; //at = ISO
    };
    bookingDate?: string; //YYYY-MM-DD, snapshot.navDate + 1: EventDate of every Geneva record and the GWI deal date (priced at that NAV)
    genevaRecords?: { //built when KCC processes the request; exactly what "Book in Geneva" publishes
        legs: EtfGenevaLeg[];
        journal: Record<string, unknown>;
        totals: { securities: number; cash: number; shareCapital: number; premium: number };
    };
    geneva_responses?: Record<string, EtfGenevaResponse>;
    undoKeys?: string[]; //records an Undo sent a Geneva delete for - Undo again re-sends the ones still listed
}

//config/etf - the GWI investor every request is booked on, the AP's KCC id and the KCC integration mode
export interface EtfConfig {
    gwi_entity_code?: string; //Geneva World Investor entity (= sub-entity) code every request is booked on, e.g. "111111" - a placeholder for units in issue, not a CRM customer
    ap_bpid?: string; //AP Trader BPID at KCC
    kcc_mode?: 'simulated';
}

//each section degrades to {error} independently (e.g. basket_size not configured yet)
export type EtfSection<T> = T | { error: string };

export interface EtfData {
    nav: EtfSection<EtfNavData>;
    holdings: EtfSection<EtfHoldingsData>;
    basket: EtfSection<EtfBasketData>;
    pcf: EtfSection<EtfPcfData>;
}

/**
 * Everything published against a fund — letters, announcements and distributions — lives
 * in one `funds/{fundID}/updates` subcollection discriminated by `type`, rather than a
 * subcollection per kind. Consumers filter by type.
 */
export type FundUpdateType = 'letter' | 'announcement' | 'distribution' | 'dividend';

export interface FundUpdateBase {
    id: string;
    type: FundUpdateType;
    created_at?: Timestamp;
    created_by?: string;
}

/**
 * A distribution. The only home for one — there is no `dividends` array on the fund
 * document any more. Written from the product catalogue by assigned fund managers and
 * from the NAV settings page by operations; `onFundUpdateWritten` recomputes MTD/YTD/ITD
 * whenever one changes.
 */
/** Legacy distribution shape retained while existing Firestore records are migrated. */
export interface FundDividend extends FundUpdateBase {
    type: 'dividend';
    percentage: number;
    date: Timestamp;
    /** English note; shown on the catalogue card banner and in the drawer. */
    notes?: string;
    /** Optional Arabic note; the Arabic catalogue falls back to `notes` when absent. */
    notes_ar?: string;
    updated_at?: Timestamp;
    updated_by?: string;
}

/** Canonical distribution shape for both profit/loss and return-of-capital events. */
export interface FundDistributionRecord extends FundUpdateBase {
    type: 'distribution';
    distribution_type: 'pnl' | 'roc';
    percentage: number;
    date: Timestamp;
    /** English note; shown on the catalogue card banner and in the drawer. */
    notes?: string;
    /** Optional Arabic note; the Arabic catalogue falls back to `notes` when absent. */
    notes_ar?: string;
    updated_at?: Timestamp;
    updated_by?: string;
}

export type FundDistribution = FundDividend | FundDistributionRecord;

/**
 * A quarterly report / factsheet under `funds/{fundID}/fund_reports_factsheets`.
 *
 * Field names are SCREAMING_CASE because the documents are written by the KFHC website
 * gateway rather than this codebase — see `addFundFactsheet` and the Typesense mapper
 * `removeFundReportUnsyncedKeys`. Titles are generated as "{fund} Q1 2026 Report".
 */
export interface FundReport {
    id: string;
    TITLE?: string;
    FUND_NAME_EN?: string;
    FUND_ID?: string;
    /** Epoch millis or a Firestore timestamp depending on how the gateway wrote it. */
    DATE?: Timestamp | number;
    /** Public URL of the PDF on the KFH Capital website. */
    PDF?: string;
    CAMPAIGN_ID?: string;
    CAMPAIGN_CREATED_AT?: Timestamp | number;
}

export type FundCommunicationChannel = 'email' | 'whatsapp';

/** Delivery counters shared by the letter and announcement callables. */
export interface FundDeliveryOutcome {
    emailRecipients: number;
    whatsappRecipients: number;
    failedRecipients: number;
    /** Recipients no channel could be attempted for (no address, test allow-list, …). */
    skippedRecipients: number;
    errors: string[];
}

export interface FundLetterSendRecord {
    channels: FundCommunicationChannel[];
    email_recipients: number;
    whatsapp_recipients: number;
    failed_recipients: number;
    skipped_recipients: number;
    errors?: string[];
}

export interface FundLetter extends FundUpdateBase {
    type: 'letter';
    title: string;
    file_name: string;
    storage_path: string;
    content_type?: string;
    size_bytes?: number;
    last_sent_at?: Timestamp;
    last_sent_by?: string;
    last_send?: FundLetterSendRecord;
    send_count?: number;
    affected_clients?: number;
    affected_clients_by_rm?: FundCommunicationAudience['affectedClientsByRm'];
    unassigned_subscribers?: number;
}

export interface FundAnnouncement extends FundUpdateBase {
    type: 'announcement';
    subject: string;
    /** Sanitised server-side before storage; still re-sanitised on render. */
    html: string;
    channels: FundCommunicationChannel[];
    status?: 'sending' | 'sent' | 'partial' | 'failed';
    fund_id?: string;
    fund_name_en?: string;
    fund_name_ar?: string;
    created_by_uid?: string;
    attachments?: FundAnnouncementAttachment[];
    completed_at?: Timestamp;
    email_recipients?: number;
    whatsapp_recipients?: number;
    failed_recipients?: number;
    skipped_recipients?: number;
    errors?: string[];
    affected_clients?: number;
    affected_clients_by_rm?: FundCommunicationAudience['affectedClientsByRm'];
    unassigned_subscribers?: number;
}

/**
 * A file published alongside an announcement.
 *
 * `delivery` records what actually happened at send time: small documents ride along as
 * e-mail attachments, while anything over the per-message budget — every video, in
 * practice — is delivered as a download link instead. SendGrid rejects a whole message
 * once it exceeds 30MB rather than dropping the oversized part, so this is decided
 * server-side before sending.
 */
export interface FundAnnouncementAttachment {
    file_name: string;
    storage_path: string;
    content_type?: string;
    size_bytes?: number;
    delivery?: 'attached' | 'linked';
}

/** Any document in `funds/{fundID}/updates`, narrowable on `type`. */
export type FundUpdate = FundLetter | FundAnnouncement | FundDistributionRecord | FundDividend;

/** Reach of a fund communication, shown to the fund manager before they commit to sending. */
export interface FundCommunicationAudience {
    /** Unique clients with a waiting_for_nav or done order for the selected fund. */
    subscribers: number;
    subscribersWithEmail: number;
    subscribersWithMobile: number;
    /** Legacy response field; always zero for the order-based audience calculation. */
    holdingsWithUnknownUnits: number;
    activeRms: number;
    activeRmsWithEmail: number;
    activeRmsWithMobile: number;
    /** Per-RM impact for the selected fund, including active RMs with zero affected clients. */
    affectedClientsByRm: {
        email: string;
        name: string;
        numberOfEffectedClients: number;
    }[];
    /** Qualifying clients without a matching RM e-mail on the customer record. */
    unassignedSubscribers: number;
}

export interface PublishFundAnnouncementRequest {
    fundID: string;
    subject: string;
    html: string;
    channels: FundCommunicationChannel[];
    /** Already uploaded to `funds/{fundID}/announcements/` before this call. */
    attachments?: FundAnnouncementAttachment[];
}

export interface SendFundLetterRequest {
    fundID: string;
    letterID: string;
    channels: FundCommunicationChannel[];
}

export interface FundCommunicationAudienceRequest {
    fundID: string;
}

export type CutoffSpec = {
    daysBefore: number; // e.g., 1 means NAV - 1 calendar day
    hour: number; // 0..23, e.g., 14 for 2pm
    minute?: number; // default 0
};

export interface PromotionalMaterial {
    state?: FundState;
    name_en: string;
    title: string;
    name_ar: string;
    file: string;
    reference?: string;
    whatsappTemplateID: string;
}
export interface Dividend {
    percentage: number;
    date: Timestamp;
    notes?: string;
    updatedBy: string;
}

export interface FundSummary {
    id: string;
    currency: string;
    name_en: string;
    name_ar: string;
    nav_per_unit?: number; //net assets value per units
    nav_update_day_of_week?: number; // Sunday - 0, Monday - 1, etc.
    nav_date?: string; // latest nav update date in the format dd/MM/YYYY
    minimum_units: number; //minimum number of units to subscribe with
    subscription_fees_percentage: number;
    fees_included_in_transferred_amount?: boolean; // flag to indicate if the fees will be included in the transfer amount or not
    short_name?: string; //shorter version of the name in english
    account_number?: string; // KFH account number related to the fund
    returns_since_inception?: string; // a text show percentage of return since inception (example: "23.45%")
}

export interface OrderSummary {
    id: string;
    fees: number; // fees amount in the currency of the fund
    fund: FundSummary; // summary of the fund at the time of creating order
    post_nav_amount?: number; // nav per units multiplied by the number of units
    transferAmount: number; // post_nav_amount in addition to the fees
    type: string; // subscription or redemption
    estimated_units?: number; // number of units in order
}

export interface FundHistory {
    id?: string;
    issued_units?: number;
    nav_date?: Timestamp;
    nav_per_unit?: number;
}

export interface SubscriptionTier {
    from: number;
    to: number;
    fees: number;
}
export interface Approvers {
    kyc_executive?: string[];
    kyc_normal?: string[];
    [key: string]: string[] | undefined;
}

export interface ApprovalData {
    id: string;
    name: string;
    info?: string;
    date?: string;
    level?: string;
    reasons?: string;
    comments?: string;
    active?: boolean;
    type?:
    | 'approve'
    | 'reject'
    | 'review'
    | 'operations_approve'
    | 'needs_update';
}

export interface Risk {
    countries: string[];
}
export interface Schedules {
    low_risk_expiry: number;
    high_risk_expiry: number;
    kyc_reminder_frequency?: number;
    kyc_remind_before_expiry_by_days?: number;
}

//OCR crap
export interface UsefulInfo {
    birthDate?: Date;
    civilID?: string;
    gender?: string;
    nationality?: string;
    houseID?: string | null;
    mobile?: string;
    civilIDExpiryDate?: Date;
    first?: string | null;
    last?: string | null;
    name_en?: string;

    //passport related crap
    passportExpiryDate?: Date;
    passportNumber?: string | null;
    countryOfIssue?: string | null;
    personalNumber?: string | null;
    hasExpiryDate?: boolean;
    expiryDate?: string | null;

    //payment slip related data
    iban?: string | null;
    acct?: string | null;
    transferDate?: Date;
    amount?: string | null;
    currency?: string | null;
}

export interface OcrResult {
    mime?: MimeType | string;
    frontOfCivilID?: boolean;
    backOfCivilID?: boolean;
    passport?: boolean;
    paymentSlip?: boolean;
    usefulInfo?: UsefulInfo;
}

export interface ExtractedOCRData {
    [key: string]: any;
}

export interface GenericObject {
    [key: string]: string;
}

export interface GenevaRequest {
    portfolioCode?: string;
    portfolioCodes?: string[];
    investmentCode?: string;
    date?: string;
    custom_date?: boolean;
}

export interface FileObj {
    name: any;
    link: any;
    metadataName?: string;
    created: Date;
    path?: string;
}

export interface MediaPDFFile {
    mime?: MimeType;
    type?: string;
    file?: any; //TODO: This needs to be looked at.  It used to be File, but I had to switch it to any.  Seems like code expects string or array buffer.
}

export interface HistoryEntry {
    msg: string;
    details?: string;
    activeKYC?: string;
}

export interface SMSJobResult {
    messagesTotal: number;
    messagesSent: number;
    messagesFailed: number;
    smsSendingStatus: string;
    message: string;
    date: Timestamp;
    byID?: string;
    by?: string;
}

export interface Portfolio extends CustomAutocomplete {
    // active?: boolean
    code?: string;
    group?: string;
    id?: string;
    investmentPortfolio?: string;
    key?: string;
    name?: string;
    // parent_id?: string
    crmID?: string; //this is the customer's CRM ID to link portfolio to customer
    // portfolioType?: string
    // portfolioTypeID?: number  //145 is GWI.  149 is Nondiscretionary, etc.
    // tradexID?: string  //this is the customer ID, not the portfolio ID.  I needed it for the tradex API.
    // tradexPortfolioID?: number,
    position?: PositionDetails;
    riskTolerance?: string;
    // new fields for the portfolios as a seperate collection
    portfolioTypeCode?: string;
    portfolioContract?: string; // e.g. 'DPM_GLOBAL' | 'CUSTODY_GLOBAL' — overrides portfolioTypeCode for trades-account classification when present
    name_en?: string;
    name_ar?: string;
    riskProfile?: string;
    managementFeePercentage?: string;
    status?: string;
    managerName?: string;
    managerID?: string;
}

export interface Position {
    code?: string;
    quantity?: number;
    total_market_value?: number;
    total_cost?: number;
    bonus_shares_and_right_issues?: number;
    unrealized_gain_loss?: number;
    name?: string;
    average_cost?: number;
    sector?: string;
    cash?: boolean;
    gain_loss_percent?: number;
    unrealized_equity?: number;
    total_market_value_local?: number;
    total_unrealized_gain_loss_local?: number;
    unrealized_fx?: number;
    exchange?: string;
    currency?: string;
    total_cost_year_close_local?: number;
    portfolio_percent?: number;
    firebase_key: string;
    ticker?: string;
}

export interface PositionDetails {
    end_date?: string; //"YYYY/MM/DD" (funds/{id}/private/position)
    portfolio_code?: string;
    refreshed_by?: string; //set by refreshEtfPosition when pulled manually from the ETF page
    unrealized_profit_loss?: number;
    fees?: string;
    cash_percent?: number;
    managed_by?: string;
    other_expenses?: number;
    type?: string;
    securities?: number;
    local_position?: Position[];
    foreign_position?: Position[];
    updated_at?: Timestamp;
    management_fee?: number;
    cash?: number;
    other_liabilities?: number;
    manager?: string;
    other_income?: number;
    created?: string;
    murabaha?: number;
    sukuk_profit_receivable?: number;
    cash_dividend_ytd?: number;
    dividend_due?: number;
    realized_gain_loss_ytd?: number;
    performance?: number;
    total_capital_market_value?: number;
    name?: string;
    net_income?: number;
    other_receivables?: number;
    status?: string;
}

export interface UserFileObjs extends MediaPDFFile {
    fileMetas: string[];
}

export interface CustodianAccount extends CustomAutocomplete {
    NAMELINE1: string;
    NAMESORT: string;
    _CUSTODIAN_CHAINID_: number | string;
    _CUSTODIAN_NAMESORT_: string;
    _UDF_CCY_: string;
    visible: boolean;
}
export interface FirebaseTransaction extends BasicProductAttributes {
    externalSecuritySelectionRequired?: boolean;
    securityTransferFee?: number;
    securityTransferSecurityCount?: number;
    securityTransferFeeCurrency?: 'KWD';
    securityTransferFeeIban?: string;
    payment_confirmations?: InvoicePaymentConfirmation[];
    payment_confirmation?: InvoicePaymentConfirmation;
    paymentSlipRequired?: boolean;
    active?: boolean; //What is this?
    source?: "portfolio" | "kfhtrade"
    //this is for knowing which transaction was deleted .
    amount?: number;
    comments?: string;
    convertedAmount?: number;
    currency?: string;
    currencyFromValue?: string;
    currencyToValue?: string;
    // portfolioCode?: string
    custodianAccount?: CustodianAccount;
    newCustodianAccount?: CustodianAccount;
    product?: Portfolio | KfhTradeAccount;
    kfhTradeAccount?: KfhTradeAccount;
    portfolio?: Portfolio; // temp for now
    transactionTimestamp?: Timestamp | Date | string;
    customRate?: number;
    custom_date?: boolean;
    customerID?: string;
    customerObj?: Customer;
    email?: string;
    margin?: boolean;
    id?: string;
    mobile?: string;
    name_ar?: string;
    accountId?: string;
    name_en?: string;
    securities: SecurityItem[]; //TODO: OK we have a problem here.  If it's Security In, then it's a Security.  If it's secrity_out, then it's an Investment.  I need to unify them in the first call.
    position?: PortfolioPositionWithCustodianAccountsRow[];
    rate?: number;
    transactionType: FiresotreTransactionType;
    LocationAccount?: string;
    entriesList?: JournalEntry[]; //TODO: For journal entries.  Must use a proper type instead of any.
    type?: string;
    files?: any;
    // Bank account details
    iban?: string;
    acct?: string;
    //For the new transactions.
    tags?: { [key: string]: Tag };
    geneva_status?: string; // Once geneva processes the transaction, it will update this field with the status.
    geneva_reason?: string; // if geneva_status is not "success", then this will contain the reason why it failed.
    is_all_available_cash?: boolean; // This is used to determine if the transaction can be executed with all available cash in the portfolio.
    skipKYC?: boolean; // This is used to determine if the transaction can be executed without KYC.
    signature?: string;
    paciData?: PACIData; // This is used to store the PACI data of the transaction.
    isMobile?: boolean; // This is used to determine if the transaction was created from the mobile app.
}

/// This used for the KYC transactions, which are same as the FirebaseTransaction, but with needed fields.
export interface BasicTransaction extends BasicProductAttributes {
    amount?: number;
    portfolioID: string;
    date?: Timestamp;
    customerID: string;
    id: string;
    transactionType: FiresotreTransactionType;
}

export interface TypesenseTransaction {
    amount?: number;
    type: string;
    source: string;
    productID?: string,
    portfolioID?: string;
    currency: string;
    custodianAccount: string;
    date: number;
    transactionID: string;
    customerID: string;
    id: string;
    tags?: { [key: string]: Tag };
    customerObj?: Customer; // Customer data
    is_all_available_cash?: boolean; // This is used to determine if the transaction can be executed with all available cash in the portfolio.
}

export type GenevaTransactionType =
    | 'SpotFX'
    | 'ReceiveLong'
    | 'Transfer'
    | 'DeliverLong'
    | 'Deposit'
    | 'Withdraw'
    | 'Buy'
    | 'Sell';
export type FiresotreTransactionType =
    | 'security_out'
    | 'security_in'
    | 'cash_out'
    | 'cash_in'
    | 'fx'
    | 'journal_entry';

export interface SecurityItem {
    price?: number;
    quantity: number;
    value?: Security;
}

export interface JournalEntry {
    credit: number;
    debit: number;
    financialAccount: CustodianAccount;
}

export interface BaseSecurity {
    CODE: string;
    EXCHANGE_CODE: string;
    EXCHANGE_DESCRIPTION: string;
    ISSUE_COUNTRY_CODE: string;
    _ISSUER_NAMESORT_: string;
    BLOOMBERGID?: string;
    BLOOMBERGMARKETSECTOR?: string;
    _INVESTMENTTYPE_CODE_?: string;
    _PRINCIPAL_CURRENCY_CODE_?: string;
}

export interface Security extends BaseSecurity {
    /** Kuwait Clearing Company security number. */
    KCC_NUMBER?: string;
    TICKER: string;
    _UDF_ISLAMIC_OR_NOT_: 'Yes' | 'No';
    WKN?: string; //Bojacob: This field was chosen randomly by operations to use for conflicting symbols between exchanges.  It should be used for allocating trades and sending to Geneva.  The way it works is that if you see on DFN with a symbol code of 3001, search for that symbol in the correct exchange.  If you can't find it, then fall back to WKN number and use that custom symbol code instead (BAITAKREIT)

    bloomberg_bid_price?: number;
    bloomberg_last_price?: number;
}

export interface Bond extends BaseSecurity {
    /* identifiers / meta */
    ISIN?: string;

    /* UDF fields */
    _UDF_DOMICILE_?: string;
    _UDF_RANK_?: string;
    _UDF_RATED_OR_NON_RATED_?: 'Rated' | 'Non-Rated';
    _UDF_RESET_RATE_?: number;
    _UDF_SECTOR_?: string;

    // New-sukuk only: _ISSUER_NAMESORT_ isn't in Geneva yet; the ops send flow creates the Issuer before the bond.
    NEW_ISSUER?: boolean;

    /* descriptive text */
    DESCRIPTION?: string;
    ALTERNATIVEFIELDS?: string;
    COUPON_FREQUENCY?: string;

    /* dates */
    DATEDDATE?: Timestamp;
    FIRSTCOUPONDATE?: Timestamp;
    ISSUEDATE?: Timestamp;
    MATURITYDATE?: Timestamp;

    /* numbers / amounts */
    COUPON?: number;
    ISSUEPRICE?: number;
    ISSUEVALUE?: number;
    MATURITYPRICE?: number;
    SHARESOUTSTANDING?: number;
}


export interface TransactionInvestmentType extends Security {
    COSTBOOKEND: number,
    COSTLOCALEND: number,
    CUSTODIANACCOUNT: string,
    CUSTODIANACCOUNTCURRENCY: string,
    MARKETVALUEBOOKEND: number,
    MARKETVALUELOCALEND: number,
    QTYEND: number,
    UNITCOSTBOOKEND: number,
    UNITCOSTLOCALEND: number,
    _DENOMINATION_CODE_: string,
    // _INVESTMENT_CODE_: string,  //CODE
    _INVESTMENT_DESCRIPTION_: string,
    // _INVESTMENT_EXCHANGE_DESCRIPTION_: string,  //EXCHANGE
    // _INVESTMENT_EXCHANGE_NAMESORT_: string,
    _INVESTMENT_INVESTMENTTYPE_CODE_: string,
    _INVESTMENT_INVESTMENTTYPE_DESCRIPTION_: string,
    // _INVESTMENT_ISSUECOUNTRY_CODE_: string,
    // _INVESTMENT_ISSUECOUNTRY_DESCRIPTION_: string,
    // _INVESTMENT_TICKER_: string,
}


// Response type for getPortfolioPositionWithCustodianAccounts
export interface PortfolioPositionWithCustodianAccountsRow {
    ISSUE_COUNTRY_DESCRIPTION?: string;
    _KEY_: string;
    QTYEND: number;
    MARKETVALUELOCALEND: number;
    _INVESTMENT_DESCRIPTION_: string;
    _INVESTMENT_INVESTMENTTYPE_DESCRIPTION_: string;
    _DENOMINATION_CODE_: string;
    UNITCOSTLOCALEND: number;
    CUSTODIANACCOUNTCURRENCY: string;
    TICKER?: string;
    EXCHANGE_DESCRIPTION?: string;
    CODE: string;
    UNITCOSTBOOKEND: number;
    CUSTODIANACCOUNT: string;
    COSTBOOKEND: number;
    _INVESTMENT_INVESTMENTTYPE_CODE_: string;
    COSTLOCALEND: number;
    EXCHANGE_CODE?: string;
    MARKETVALUEBOOKEND: number;
    ISSUE_COUNTRY_CODE: string;
    _UDF_ISLAMIC_OR_NOT_: 'Yes' | 'No';
    // [key: string]: string | number | undefined;
}

export interface PortfolioPositionWithCustodianAccountsResponse {
    rows: PortfolioPositionWithCustodianAccountsRow[];
}

export interface GenevaTransaction {
    LoaderAction: string;
    LoaderType: GenevaTransactionType;
    UserTranId1: string;
    UserTranId2?: string;
    KeyValue: { [key: string]: any } | string;
    TradexPortfolioId?: string;
    Portfolio: string;
    LocationAccount?: string;
    CounterInvestment?: string;
    EventDate: string;
    SettleDate: string;
    ActualSettleDate: string;
    NetCounterAmount?: number | 'CALC';
    Comments: string;
    CounterFXDenomination?: string;
    CounterTDateFx?: number;
    FinAccount?: string;
    UserDefField1?: string;
    Price?: number;
    Investment?: string;
    Quantity?: number;
    ReceiveDatePrice?: number;
    TaxLotDate?: string;
    TaxLotValue?: number;
    ContractFxRate?: string;
    ContractFxRateDenominator?: string;
    ContractFxRateNumerator?: string;
    NetInvestmentAmount?: string;
    NewLocationAccount?: string;
    TransferType?: string;
    ExternalSystemReferenceID?: string;
}

export interface TradeTransaction extends GenevaTransaction {
    AccrueCommissionFlag?: number;
    AccruedInterest?: string;
    Broker?: string;
    CounterSDateFx?: number;
    FundStructure?: string;
    InvestmentAccruedInterest?: number;
    InvestmentClosingRule?: string;
    LoanAmount?: number;
    NetTradeFlag?: number;
    ParNearPar?: number;
    PriceDenomination?: string;
    SecFeeAmount?: number;
    tradeFX?: number;
    NonCapExpenses?: NonCapExpense[];
    TradeExpenses?: TradeExpense[];
}

// Capitalized expense; ExpenseCode must be a notational financial account
export interface TradeExpense {
    ExpenseNumber: string;
    ExpenseCode: string;
    ExpenseAmt: number;
}

export interface NonCapExpense {
    NonCapNumber: string;
    NonCapCurrency: string;
    LocationAccount: string;
    NonCapExpenseCode: string;
    NonCapLiabilityCode: string;
    NonCapPaymentType: string;
    NonCapAmount: number;
}

export interface CustomAutocomplete extends Omit<
    ComboboxItem,
    'label' | 'value'
> {
    id?: string;
    key?: string;
    text_l?: string;
    text_r?: string;
    label?: any;
    value: string;
    group?: string; //for Select components.
}

export interface SMSResult {
    mobile: string;
    details: string;
    link?: string;
    code?: string;
    description?: string;
    msg?: string;
    status?: 'error' | 'success';
    statusCode?: string;
}

export interface SMSBundleItem {
    mobile: string;
    /** Dial code for `mobile`; missing means +965 (see shared-helpers/src/mobileHelpers.ts). */
    mobile_code?: string;
    email: string;
    smsMessage: string;
    name_ar?: string;
    name_en?: string;
    service?: string;
    //TODO: I can't use admin since types here is shared between functions and webapp.  Need to find a better type for this.
    userRef?: any;
    link: string;
    linkID?: string;
}

export interface RequestKYCUpdateRequest {
    updateLink: UpdateLink;
    customers: Customer[];
    // smsMessage?: string
    // updateType: MessageType,
    // senderID: string
    language?: string;

    // Optional portfolio opening request data: Only use requestKYCUpdateExternalAPI
    portfolioOpeningRequest?: PortfolioOpeningRequest;
}

export type AddOrderActions =
    | 'save_for_later'
    | 'send_order_sms'
    | 'manual'
    | 'mobile_order'
    | 'submit_as_referral';
export type AddServiceActions =
    | 'save_for_later'
    | 'send_service_sms'
    | 'manual';

export interface AddOrderRequest {
    updateLink?: UpdateLink;
    signingMethod?: SigningMethod;
    order: Order;
    slipFilePaths?: string[];
    token?: string;
    onBehalfOf?: RMUser; //the person who is actually credited for the order in case someone else is submitting.
    civilID?: string; //for backward compatibility until everyone updates their page.
    id?: string;
    maxUnits?: boolean;
    action: AddOrderActions;
    // selectedWhatsappTemplate?: UpdateLink["selectedWhatsappTemplate"]
    // useWhatsapp?: UpdateLink["useWhatsapp"]
}


// This is used for updating the order amount or quantity.  
// It is used in the mobile app/ portal when the user wants to change the amount or quantity of an order.
export interface UpdateOrderAmountRequest {
    amountOrQuantity: number;
    orderId: string;
    customerID?: string; // If update from portal it's required for the customer collection.  If update from mobile app, it's not required (we get user from auth).
    reason?: string;
}

export interface AddMobileOrderRequest {
    amountOrQuantity: number;
    fundId: string;
    type: SubscriptionType;
    iban?: string;
}

export interface AddServiceRequest {
    updateLink: UpdateLink;
    signingMethod?: SigningMethod;
    service: Service;
    token?: string;
    onBehalfOf?: RMUser; //the person who is actually credited for the order in case someone else is submitting.
    civilID?: string; //for backward compatibility until everyone updates their page.
    id?: string;
    action: AddServiceActions;
}

export interface GenevaTradesRequest {
    trades: string[]; //this would be the path of the trades in the DB so we gotta retrieve those specifically.
    action: 'add' | 'delete';
}

export interface GenerateXmlRequest {
    request: any; // GenevaRequest | BulkGenevaRequest - using any to avoid circular dependencies
    orgId: string;
    isApproval?: boolean; // Indicates if this XML generation is for approval (should call writeXML)
    isUndo?: boolean; // Indicates if this is an undo operation (LoaderAction: Delete)
}

// Geneva response structures
export interface GenevaResponse {
    code: string;
    key_value: string;
    status: string;
    status_message: string;
    timestamp: Timestamp;
}

// For single requests - geneva_response field
export interface SingleGenevaResponseData {
    geneva_response: GenevaResponse;
}

// For bulk requests - geneva_responses field (array indexed by number)
export interface BulkGenevaResponseData {
    geneva_responses: Record<string, GenevaResponse>; // Keys are "0", "1", "2", etc.
}

export interface DeleteFileRequest {
    token?: string;
    filepath?: string;
}

export interface SendOrderSMSRequest {
    smsDocID: string;
    senderID: string;
    order: Order;
    slipFilePaths?: string[];
    token?: string;
    type?: 'whatsapp' | 'sms';
}

export interface SendTransactionSMSRequest {
    smsDocID: string;
    senderID: string;
    transaction: FirebaseTransaction;
    slipFilePaths?: string[];
    token?: string;
    type?: 'whatsapp' | 'sms';
}

export interface SendServiceSMSRequest {
    smsDocID: string;
    service: Service;
    type?: 'whatsapp' | 'sms';
    token?: string;
}

export interface SendIPOSMSRequest {
    smsDocID: string;
    ipoRequestObject: IpoRequestObject;
    type?: 'whatsapp' | 'sms';
    token?: string;
}

export interface SendAgreementSMSRequest {
    smsDocID: string;
    agreement: Agreement;
    token?: string;
}

export interface Agreement extends BasicProductAttributes {
    agreement_id: AvailableAgreements;
    title_en: string;
    title_ar: string;
    content_en: string;
    content_ar: string;
    date?: Timestamp;
    paci_msg_en: string;
    paci_msg_ar: string;
}

export interface AddKFHRmRequest extends RMUser {
    emailVerified?: boolean;
    phoneNumber?: string;
    password?: string;
    displayName?: string;
    photoURL?: string;
    disabled?: boolean;
}

export interface StartClosedEndedFundRequest {
    fundID: string;
    startDate: string; // YYYY-MM-DD
}

export interface OrderRefreshRequest {
    orderID: string;
}

export interface DeleteOrderGenevaResponseRequest {
    orderID: string;
    key: keyof NonNullable<Order['geneva_responses']>;
}
export interface AddOrderGenevaResponseRequest {
    orderID: string;
    key: keyof NonNullable<Order['geneva_responses']>;
}
export interface ProductRefreshRequest {
    productID: string;
}

export interface TransactionRefreshRequest {
    transactionsID: string;
}

export type Metadata = Record<string, string | number | boolean>;

export interface UploadFileResult extends BaseResult {
    numFiles: number;
    filePaths: string[];
    google?: Record<string, Metadata>; //key is filename
    paddle?: Record<string, Metadata>; //key is filename
    info?: Record<string, any>; //key is filename  //TODO: REMOVE ANY AND USE UploadFileInfo
}

export interface UploadFileInfo {
    filepath: string;
    metadata: Metadata;
}

export interface BaseResult {
    status: 'success' | 'error';
    message?: string;
    code?: string;
    data?: any;
}

export interface PACIRequestResult extends BaseResult {
    code?: string;
}

export interface AddFundFactsheetResult extends BaseResult {
    mediaID?: string;
}

export interface FundsResult extends BaseResult {
    funds?: FundSummary[];
}
export interface CustomerInvoicesResult extends BaseResult {
    invoices?: Invoice[];
}
export interface CustomerInvoiceHistoryResult extends BaseResult {
    invoices?: InvoiceWithPaymentConfirmation[];
}
export interface PaciVerificationResult extends BaseResult {
    id?: string;
}

export interface PaciSignatureRequestResult extends BaseResult {
    id?: string;        // paci_signatures doc id
    requestId?: string; // signing service RequestId
    data?: any;         // raw signing service response (or error details)
}

export interface AddOrderResult extends BaseResult {
    link?: string;
    orderId?: string;
}

export interface RmAuthResult extends BaseResult {
    twoFactorEnabled?: boolean;
    active?: boolean;
}

export interface RequestKYCUpdateResult extends BaseResult {
    smsResult?: PromiseSettledResult<SMSResult>[]; //We can get SMSResult from the promise by using .value, but typescript doesn't know?
}

export interface GenericMessageResult extends BaseResult {
    messageID?: string;
    messageIDsList?: string[];
}

export interface MobileAppChangePasswordRequest {
    civilID?: string;
    linkId?: string;
    /**
     * Determines the type of password change request:
     * - `true`: Indicates a "Forgot Password" request where the user is resetting their password.
     * - `false`: Indicates a password change request during the "signup" process for a "email-already-in-use".
     */
    isForgotPassword?: boolean;
    password?: string;
}

export interface OneTimeOverrideKYCRequest {
    customerID: string;
    kycData: {
        birthDate?: string | Date;
        civilIDExpiryDate?: string | Date;
        passportExpiryDate?: string | Date;
    };
}

export interface RiskOverrideRequest {
    customerID: string;
    action: 'add' | 'remove';
    reason?: string;
    targetRisk?: 'high' | 'low';
}

export interface RiskOverrideReasonRequest {
    kycID: string;
    customerID: string;
    action: 'update' | 'remove';
    reason?: string;
    targetRisk?: 'high' | 'low';
}

export interface NotificationData {
    type: 'link' | 'fund' | 'kyc';
    value: string;
}

export interface CampaignCustomEmail extends CampaignTypeBase {
    subject: string;
    message: string;
}
export interface CampaignEmail extends CampaignTypeBase {
    template: string;
}
export interface CampaignWhatsapp extends CampaignTypeBase {
    template: TwilioContentType;
}
export interface CampaignSMS extends CampaignTypeBase {
    message: string;
}
export type NavDeliveryChannel = 'sms' | 'custom_email' | 'whatsapp' | 'email' | 'notification';

export interface CustomerNotification {
    navCampaignDelivery?: boolean;
    navDeliveryState?: 'pending' | 'sending' | 'finished';
    navDeliveryRequest?: string;
    navDeliveryLease?: { token: string; until: Timestamp };
    navDeliveryChannels?: Partial<Record<NavDeliveryChannel, { status: 'submitted' | 'finished'; finishedAt: Timestamp; messageSID?: string }>>;
    navDeliveryErrors?: Partial<Record<NavDeliveryChannel, string>>;
    campaign_id?: string; // not only used through campaigns, can be stand alone notifications
    title: string;
    created_on: Timestamp;
    finished_on?: Timestamp;
    status: 'running' | 'done';
    custom_email?: CampaignCustomEmail;
    email?: CampaignEmail;
    whatsapp?: CampaignWhatsapp;
    sms?: CampaignSMS;
    notifyRm?: boolean;
    notifyEmployees?: boolean;
    notification?: CampaignNotification;
    messageVariables: Record<string, string>;
    created_by?: string;
    created_by_id?: string;
    notification_type?: 'campaign' | 'marketing' | 'promotional' | 'alert'; // add any other types in future ?
    promotional_material?: PromotionalMaterial;
}

export interface CampaignNotification extends CampaignTypeBase {
    title: string;
    body: string;
    data?: NotificationData;
}

export interface CampaignTypeBase {
    message_total: number;
    message_sent: number;
    message_failed: number;
    errors: CampaignError[];
    // RM recipients are tracked separately so they don't inflate the client-facing totals.
    rm_message_total?: number;
    rm_message_sent?: number;
    rm_message_failed?: number;
    rm_errors?: CampaignError[];
}
export interface Campaign extends CreateCampaignRequest {
    id: string;
    title: string;
    created_by: string;
    created_by_id: string;
    created_on: Timestamp;
    run_by?: string;
    run_by_id?: string;
    type: ('sms' | 'email' | 'whatsapp' | 'customEmail' | 'notification')[];
    run_on?: Timestamp;
    status: 'waiting_for_approve' | 'done';
    customer_ids: string[];
    tagsRecipients: string[];
    custom_email?: CampaignCustomEmail;
    email?: CampaignEmail;
    whatsapp?: CampaignWhatsapp;
    sms?: CampaignSMS;
    notification?: CampaignNotification;
    customers?: string[];
    smsMessage?: string;
    emailMessage?: string;
    emailSubject?: string;
    selectedEmailTemplate?: string;
    selectedSMSTemplate?: string;
    selectedWhatsappTemplate?: TwilioContentType;
    selectedNotificationTemplate?: string;
    selectedCustomEmailTemplate?: string;
    notificationTitle?: string;
    notificationBody?: string;
    notificationData?: NotificationData;
    notifyRm?: boolean;
    notifyEmployees?: boolean;
    rm?: Partial<RMUser>;
    employee_errors?: CampaignError[]; // employee recipients skipped (e.g. blank mobile) or a failed employee notification step
    run_errors?: string[]; // per-step failures (employees / rms) that did not abort the run
    navCampaignSource?: {
        fundID: string;
        eventID: string;
        sendAutomatically: boolean;
    };
}
export interface CampaignError {
    customer_id: string;
    message: string;
}

export interface CustomFileWithPath extends FileWithPath {
    name: string;
    // readonly path?: string;
    serverPath?: string;
    // readonly name?: string;
}

export interface OrderActionRequest extends Action {
    customerID: string;
    orderID: string;
}

export interface ServiceActionRequest extends Action {
    customerID: string;
    serviceID: string;
}

export interface SMSDocument {
    smsDocID: string;
    by: string;
    byID: string;
    date: Timestamp;
    customerID?: string;
    linkIDs: {
        [key: string]: any;
    }[];
    createOnSubmit?: boolean;
    message?: string;
    messagesFailed?: number;
    messagesSent?: number;
    messagesTotal?: number;
    passportRequired?: boolean;
    smsMessage?: string;
    selectedSMSTemplate?: string;
    smsSendingStatus?: string;
    resendIfFailed?: boolean;
    updateType?: UpdateLinkType;
    usePACI?: boolean;
    useWhatsapp?: boolean;
    selectedWhatsappTemplate?: TwilioContentType;
}

export interface ProductActionRequest extends Action {
    customerID: string;
    productID: string;
}

export interface CustomClaimsRequest {
    token?: string;
}

export interface CustomClaimsResult extends BaseResult {
    customClaims?: CustomClaims;
    customSearchKey?: string;
    rmsCollectionSearchKey?: string;
    servicesSearchKey?: string;
    customerCollectionSearchKey?: string;
    campaignsSearchKey?: string;
    securitiesSearchKey?: string
    ctcSearchKey?: string; // CTC lists in Typesense; bank users' key only matches their customer
    availableTradeAccounts?: string[];
    claimsChanged?: boolean; // false when the token's claims were already up to date, so the client can skip the forced token refresh
}

export interface GenerateRMUpdateLinkRequest {
    linkID: string;
    token: string;
    is_contract?: boolean; // optional, default false; set true only for contract KYC links (contract_link flow)
}

export type ClaimType =
    | 'cr'
    | 'hr'
    | 'admin'
    | 'rm'
    | 'oper'
    | 'exec'
    | 'risk'
    | 'sys'
    | 'kfh_rm'
    | 'kfh_rm_certified'
    | 'kfh_rm_private_banking'
    | 'kfh_rm_ruwad'
    | 'kfh_rm_tamayoz'
    | 'kfh_oper'
    | 'digital'
    | 'kfh_rm_approver'
    | 'fund_manager'
    | 'cram'
    | 'global_dpm'
    // CTC (Commodity Trading Center) lanes — see shared/ctcLifecycle.ts and docs/ctc-module-plan.md
    | 'ctc_maker'
    | 'ctc_checker'
    | 'ctc_supervisor'
    | 'ctc_dealer'
    | 'ctc_backoffice'
    | 'ctc_admin'
    | 'ctc_viewer';

export type CustomClaimsFlags = Partial<Record<ClaimType, boolean>>;

export interface CustomClaims extends CustomClaimsFlags {
    /** Microsoft mailbox address; separate from the Firebase sign-in email and RM ID. */
    rm_mail?: string;
    segment?: string;
    availableSegments?: string[];
    id?: string[];
    client?: boolean;
    geneva_requests_create?: boolean;
    geneva_requests_approve?: boolean;
    mobile_app_user?: boolean; // This is used to determine if the user is a mobile app user.  If true, then the user can access the mobile app.
    staff?: boolean; // Minted only by the customClaims function for a corporate user who resolved at least one role and (when configured) signed in from an allowed Entra tenant. firestore.rules / storage.rules accept nothing else as "corporate" — the email domain alone is not trusted.
    portfolioTypeCodes?: string[]; // Portfolio types the user may access, used by Firestore rules to authorize manual trades reads/writes
    routeGroups?: string[]; // Route groups resolved from config/routesPermissions at token mint, used by Firestore rules to authorize reads per page group
    ctc_customer_id?: string; // CTC: the ctc_customers doc this (KFH) user acts for; stamped by customClaims from the email domain
    ctc_staff?: boolean; // CTC: brokerage / KFH Capital desk user (not bound to a customer)
    ctc_group_customer_ids?: string[]; // CTC: customers whose Microsoft group (microsoftGroupId) the user is in; maker/checker may act only for these
}

export type UploadFunction = (
    filepaths: string[],
    fields: Record<string, any>,
    created_by?: string,
) => Promise<any>;

/** Input of the `validateMobileNumber` callable (Twilio Lookup). */
export interface MobileLookupRequest {
    /** Local digits, or a full "+"-prefixed number. */
    mobile: string;
    /** Dial code for `mobile` ("+965", "+20"); ignored when `mobile` starts with "+". */
    mobile_code?: string;
}
/** Result of the `validateMobileNumber` callable. `valid` is undefined when the lookup itself failed. */
export interface MobileLookupResult {
    valid?: boolean;
    e164?: string;
    nationalFormat?: string;
    /** ISO-2 country of the number, e.g. "EG". */
    country?: string;
    validationErrors?: string[];
    error?: string;
}

export interface AddClientRequest {
    name_en: string;
    name_ar: string;
    /** Local digits (no dial code). A "+"-prefixed value is accepted and split server-side. */
    mobile: string;
    /** Dial code for `mobile`, e.g. "+965" or "+20". Defaults to the creating RM's segment dial code. */
    mobile_code?: string;
    email: string;
    civilID?: string;
    other_id?: string;
    other_id_type?: OtherIdType;
    /** ISO-2 issuing country of `other_id`; required with other_id, never "KW". */
    other_id_country?: string;
    commercial_register?: string;
    nationality: string;
    gender: string;
    isUpdate?: boolean;
    customerType: CustomerType;
}
export interface BulkAddClientRequest {
    customers: {
        civilID: string;
        kfhtrade_id: string;
        name_en: string;
        name_ar: string;
        mobile: string;
        nationality: string;
        segment?: string;
    }[];
}

export interface FirebaseHistory {
    msg?: string;
    details?: string;
    [key: string]: any;
}

export interface FundAutoComplete extends Fund, ComboboxProps, ComboboxItem {
    id: FundID;
    currency: string;
    name_en: string;
    name?: string;
    name_ar: string;
    nav_per_unit?: number;
    date?: Timestamp;
    overhead_percent: number;
    image?: string;
    min_units: number;
    subscription_fees_percent: number;
    // multiples_of?: number,
    multiples_of_units?: number;
    iban: string;
    updated_at: Timestamp;
    type: 'closed_ended' | 'open_ended' | 'etf';
    fund_state?: FundState;
}

export interface MicrosoftUserAutoComplete
    extends MicrosoftGraph.User, Omit<CustomAutocomplete, 'label'> {
    label?: string;
}

export interface RMUser {
    email: string;
    /** Microsoft Graph mail address, which can differ from the numeric sign-in email. */
    mail?: string;
    id?: string;
    name_en: string;
    name_ar: string;
    segment?: string;
    active?: boolean;
    rm_id?: string;
    /** Local digits. Legacy docs may carry a "+"-prefixed full number instead (toE164 handles both). */
    mobile?: string;
    /** Dial code ("+965", "+20"). Missing means the segment default: "+20" for KFH Egypt, otherwise "+965". */
    mobile_code?: string;
    role?: 'rm' | 'operations';
    twoFactorEnabled?: boolean;
    availableSegments?: string[];
    certified?: boolean;
    order_handler_email?: string;
    lastOperation?: LastOperation;
}

// export interface CustomerAutoComplete extends Customer, CustomAutocomplete {

//     id?: string,
//     lastOperation?: LastOperation,
//     mobile: string,
//     name_en: string,
//     name_ar: string,
//     tags?: { [key: string]: Tag },

// }

export interface CustodianAccountWithBalance extends CustodianAccount {
    balance: number;
    unitCost: number;
}

export interface PDFDocumentsDataTypes {
    name?:
    | 'orders'
    | 'kyc'
    | 'customers'
    | 'services'
    | 'ipoRequests'
    | 'transactions';
    getCustomerDataKey?: 'string'; //this is the key that contains the Customer object.  The reason it's not just civilID is because I also want the ID of the last KYC contained in the customer object.
    metaKey?: string;
    path?: string;
    title?: string;
    type?: 'collection' | 'collectionGroup';
    subCollection?: 'order' | 'kyc' | 'customers'; //eg : kyc
    subCollectionAccessorName?: string; // eg : value would be 'civilID' so we can get the actual civil id value using data[subCollectionAccessorName] which results in "295111501482" (civil id used to access kyc)
    subCollectionAccessorValue?: string;
    documentsToAddFunction?: (obj: GenericProduct) => OrdersPath[];
}

// export interface PDFDocumentsExportData {
//     metaKey?: string
//     doNotFlatten?: boolean
//     path: string
//     title: string
// }

// export type DocumentTypes = "passport" | "frontOfCivilID"

// export type DocumentMetaTypes = {
//     [key: string]: {
//         key: string;
//         name: string;
//         color: string;
//     };
// };

export type FileLink = {
    metadata: {
        key: string;
        name: string;
        color: string;
    };
};

export interface ChangeOrderRmRequest {
    orderId?: string;
    serviceId?: string;
    rmEmail?: string;
    productType?: 'order' | 'service';
    type?: 'primary' | 'referred';
}

export interface AttachmentJSON {
    content: string;
    filename: string;
    type?: string;
    disposition?: string;
    content_id?: string;
}
export enum FeedbackType {
    complaint = 'complaint',
    suggestion = 'suggestion',
    bug = 'bug',
    other = 'other',
}
export interface Feedback {
    type: FeedbackType;
    body: string;
    image_id: string | null;
    email: string | null;
    status: 'new' | 'closed';
    created_on: Timestamp;
}

export type UpdateLinkType = 'sms' | 'manual' | 'mobile';

export interface GeneralOrderRequest {
    orderId?: string;
}
export interface GetNeededDocumentsResult extends BaseResult {
    neededDocuments: string[];
}

export interface NotificationResult {
    successTokens: string[];
    successCount: number;
    failureCount: number;
    failedTokens: string[];
    error?: string;
}
export interface CreateCampaignRequest {
    messageVariables: Record<string, string>;
    title?: string;
    rmRecipients: string[];
    rmRecipientRoles?: ('rm' | 'operations')[];
    customerRecipients: string[];
    employeeRecipients?: MessageRecipientData[];
    employeeRecipientGroupIds?: string[];
    segmentsRecipients: string[];
    tagsRecipients: string[];
    smsMessage?: string;
    emailMessage?: string;
    emailSubject?: string;
    updateLink: Partial<UpdateLink>;
    selectedWhatsappTemplate?: TwilioContentType;
    selectedEmailTemplate?: string;
    notificationTitle?: string;
    selectedSMSTemplate?: string;
    selectedNotificationTemplate?: string;
    selectedCustomEmailTemplate?: string;
    notificationBody?: string;
    notificationData?: NotificationData;
    notificationDataLink?: string;
    type: ('sms' | 'email' | 'whatsapp' | 'customEmail' | 'notification')[];
}

export interface UpsertCampaignRequest extends CreateCampaignRequest {
    id?: string; //used for updating campaign
    created_on?: Timestamp;
}

export interface NavCampaignConfig {
    status: 'active' | 'inactive';
    sendAutomatically: boolean;
    payload: Omit<CreateCampaignRequest, 'customerRecipients' | 'tagsRecipients' | 'segmentsRecipients'> &
    Partial<Pick<CreateCampaignRequest, 'customerRecipients' | 'tagsRecipients' | 'segmentsRecipients'>>;
}
export interface NotificationPreferences {
    funds: NotificationPreferencesValues;
    orders: NotificationPreferencesValues;
    statements: NotificationPreferencesValues;
    tokens: NotificationToken[];
    badge_count: number;
}

export interface NotificationPreferencesValues {
    email: boolean;
    inApp: boolean;
    sms: boolean;
}
export interface NotificationToken {
    created_at: Timestamp;
    expires_at: Timestamp;
    isIOS: boolean;
    token: string;
    uuid: string;
}
export interface FeedbackRequest {
    body: string;
    type: FeedbackType;
}

export interface AddFundFactsheet {
    fundID: string;
    titleAr?: string;
    titleEn?: string;
    file: File | null;
    fileType?: string;
    fileString: string | null;
    date: Date;
}

export interface InvoicePaymentConfirmation {
    id: string;
    type: 'order' | 'service' | 'transaction';
    transactionID?: string;
    civilID?: string;
    name?: string;
    fromIban?: string;
    toIban?: string;
    transactionDate?: string;
    amountInKWD?: number;
    exchangeRate?: number;
    receivedAt?: Timestamp;
    transactionTimestamp?: Timestamp;
    amountInBaseCurrency?: number;
    currency?: string;
    /**
     * Legacy field. KFH now always provides `amountInBaseCurrency`, so we no longer
     * write `transferAmountWhenReceived` on new confirmations. It may still exist on historical
     * documents; do not rely on it for new code.
     */
    transferAmountWhenReceived?: number;
}

export type ServiceTypeLabel = {
    label: string;
    value: ServiceType;
};

export type CustomerType = 'individual' | 'company' | 'trust' | 'other';

export interface CreateAMSAccountRequest {
    customer: Customer;
    kyc?: KYC;
}

export interface CreateAMSPortfolioRequest {
    customer: Customer;
    portfolio: Omit<Portfolio, 'id' | 'createdAt' | 'updatedAt' | 'value'>;
}

export type PortfolioType = 'dpm' | 'non-dpm' | 'custody';
export type RiskTolerance = 'low' | 'medium' | 'high';

export interface PortfolioOpeningRequest {
    portfolio_db_id: string;
    portfolioType: PortfolioType;
    portfolioId?: string;
    fees: number;
    assetClass?: string;
    portfolioManager?: string;
    inceptionDate?: string;
    controlDate?: string;
    transferAmount: number;
    currency: string;
    trading_commission?: number; // Optional trading commission percentage
    /* This id adding by Portal Team, when User have a existing Portfolio */
    mainPortfolioId?: string;
    /* When User have a existing Portfolio this is true*/
    isSubPortfolio?: boolean;

    // This update after user submit KYC,
    riskTolerance?: string;
    name_ar?: string;
    name_en?: string;
    portfolio_purchase_request_name?: string; // Shown on Sukuk purchase request Page
}

export type Invoice = {
    titleAr: string;
    descriptionAr?: string;
    titleEn: string;
    descriptionEn?: string;
    currency: string;
    civilID: string;
    date?: Date;
    iban: string;
    type: 'order' | 'service' | 'transaction';
    amount: number;
    id: string;
};

export interface InvoiceWithPaymentConfirmation extends Invoice {
    payment_confirmation: InvoicePaymentConfirmation;
}

export interface MobileKYCRequest {
    id?: string;
    type: 'normal' | 'order' | 'service' | 'transaction';
}

// Type definition for uploaded trades
export interface Trade {
    id: string;
    execId: number;
    tradeDate: Timestamp;
    tradeTime: string;
    customerNo: string;
    customerName: string;
    accountId: string;
    exchOrderId: string;
    dealer: string;
    symbol: string;
    symbolCode: string;
    side: 'BUY' | 'SELL';
    price: number;
    orderQty: number;
    filledQty: number;
    pendingQty: number;
    orderValue: number;
    totalCommission: number;
    netSettle: number;
    tif: string;
    institution: string;
    exchangeCommission: number;
    type: string;
    symbolDesc: string;
    channel: string;
    customerRefNo: string;
    settlementDate: Timestamp;
    cashAcrRef: string;
    executingBrokerId: string;
    dealerExtension: string;
    securityAccount: string;
    orderId: string;
    unallocQty: number;
    createdAt: Timestamp;
    exchange: string;
    subMarket: string;
    allocations?: TradeAllocation[];
    state?: TradeState;
    genevaCode?: string; //this is the code used when inserting in Geneva (comes from code field in /securities)
    burganCode?: string; //this is the code used when inserting in Burgan (comes from WKN field in /securities)
    firebaseKey?: string; //this is the firebase key of the security
    genevaExchange?: string; //this is the exchange used when inserting in Geneva (comes from EXCHANGE_CODE field in /securities)
    fundStructure?: string; //this is the fund structure used when inserting in Geneva
    capitalizeExpenses?: boolean; // copied from tradesMap account on create; true = fees sent as TradeExpenses (capitalized), else NonCapExpenses
    originalExecId?: string; //this is the original execId which is mainly used for gcc trades as the execId have characters in it.
    currency?: string; // Currency of the trade, e.g., "KWD", "USD"
    rate?: string; // Exchange rate if applicable
    source?: 'allfunds'; // set on trades imported from the AllFunds Transactions export
    isin?: string; // ISIN from the AllFunds file (also used as symbolCode / securities join key)
    nav?: number; // NAV per unit from the AllFunds file (same as price; 0 while in-flight)
    allfundsTransactionCode?: string; // the file's Transaction Code (zero-padded string of record)
    allfundsStatus?: string; // normalized AllFunds status, e.g. "Confirmed", "Settled"
    grossAmountRequested?: number; // AllFunds "Gross Amount Requested" — DPM excel amounts (buffer included) reconcile against this
    orderDateId?: string; // dd-MM-yyyy AllFunds "Order Date" — the DPM allocation excel and DPM team views key by order date; the trade doc itself is bucketed by SETTLEMENT date (operations view / Geneva / Burgan)
}

// Fund units are fractional; float sums rarely hit exact equality. All quantity
// comparisons/accumulations in the allocation path go through these.
export const QTY_EPSILON = 1e-6;
export const stripNoise = (n: number): number => Math.round(n * 1e9) / 1e9;
export const qtyEq = (a: number, b: number): boolean => Math.abs(a - b) < QTY_EPSILON;

// AllFunds statuses that never count as cash-impacting.
export const ALLFUNDS_DEAD_STATUSES = ['Cancelled', 'Error'];
// A trade built from an AllFunds row can only be allocated once Settled
// (final NAV and awarded units). Everything earlier stays visible read-only.
export const isAllfundsTradeAllocatable = (t: { source?: string; nav?: number; filledQty: number; allfundsStatus?: string }): boolean =>
    t.source !== 'allfunds' ||
    (t.allfundsStatus === 'Settled' && (t.nav ?? 0) > 0 && t.filledQty > 0);

export interface TradeAllocation {
    portfolioId: string;
    qty: number;
    commission?: number;
    stamp?: number;
}

// A row of the Global DPM team's allocation excel, stored under
// trade_dates/{orderDate}/global_dpm_allocations until the matching GLOBAL_DPM
// trade with the same order date settles and gets auto-allocated
// (see global_dpm_matching.ts).
export interface GlobalDpmAllocationRow {
    id: string; // deterministic: {isin}_{pf}_{side}_{n} — re-uploads merge onto the same doc, so a corrected amount overwrites instead of stranding a stale pending row
    valueDate: string; // dd-MM-yyyy, equals the parent trade_dates dateId — the excel's date column, which is the ORDER date ("Order Date" header, legacy "Value Date"); matched against the trade's orderDateId (the trade doc itself sits in its settlement-date bucket)
    isin: string;
    securityNumber: string;
    fundName: string;
    side: 'BUY' | 'SELL'; // from "Transactions type"
    portfolioId: string; // "PF Number"
    amount: number; // includes the DPM team's 0.4% buffer, like the trade's grossAmountRequested; 0 on quantity-only rows
    quantity?: number; // fund units from the optional "Quantity" column — when every row of an order carries one it drives the split instead of amount; stored as 0 when blank (merge re-uploads clear stale values), absent on legacy docs
    status: 'pending' | 'allocated';
    allocatedTradeId?: string; // GLOBAL_DPM_{transactionCode}
    allocatedAt?: Timestamp;
    uploadId: string; // `${fileName}@${uploadTimestampMs}` — groups rows per upload for the uploads modal
    uploadFileName: string;
    uploadedBy: string;
    uploadedAt: Timestamp;
    allocatedQty?: number; // this portfolio's units from the allocation that flipped the row — display only, stamped by the webapp paths (panel / manual mark), NOT by the tradeUpdated trigger; the rebalance page reads the trade itself when there is one
    allocatedNav?: number; // trade.nav at allocation time
    allocatedBy?: string; // set only when the rebalance page marked the row allocated by hand (no trade) — such rows carry no allocatedTradeId
}

/* ---- Global DPM rebalance (global_dpm_rebalance.ts + routes/trades/global_dpm_rebalance.tsx) ---- */

export interface GlobalDpmModelLine {
    key: string; // ISIN for 'allfunds' (the allocation rows / matcher join on it); Geneva code for 'kfhc' (10024 | 500500 | 500501)
    kind: 'allfunds' | 'kfhc';
    name: string;
    securityNumber?: string; // Geneva CODE (150007…) — position rows carry no ISIN, so a holding joins a line when position.code equals key OR securityNumber
    targetPercent: number; // per risk level Σ targetPercent === 100
    fallbackKey?: string; // line of the same level (allfunds or another house fund) that receives a kfhc subscription below the fund minimum; a house-fund fallback is checked against its own minimum and may fall through again
    minHoldingPolicy?: 'sell_all' | 'hold'; // kfhc only: what to do when a redemption would leave less than minimum_holding
    fundId?: string; // kfhc only: funds/{id} that holds the rules (nav, minimums). Optional when the fund doc's geneva_investment equals key
    navBufferPct?: number; // kfhc only: % added to the last NAV when sizing a subscription (the next NAV may be higher; KWD funds also carry FX). Absent = 0
}

/** global_dpm_models/{id} — exactly one 'active'; activating retires the previous active. */
export interface GlobalDpmModel {
    id: string;
    label: string;
    status: 'draft' | 'active' | 'retired';
    effectiveFrom?: Timestamp;
    createdBy: string;
    createdAt: Timestamp;
    riskLevels: Record<string, GlobalDpmModelLine[]>; // keys = config/portfolio_service.multi_assets…risk_levels[].id (low | medium | high)
}

export interface GlobalDpmBatchPortfolio {
    riskProfile: string;
    totalValueUsd: number;
    cashUsd: number;
    positionEndDate: string;
    excludedReason?: string;
}

export interface GlobalDpmBatchLine {
    portfolioId: string;
    key: string;
    kind: 'allfunds' | 'kfhc';
    name: string;
    securityNumber?: string;
    phase: 'sell' | 'buy';
    side: 'SELL' | 'BUY';
    qty?: number; // sells: units (allfunds: true fractional holding; kfhc: whole units); kfhc buys: units when the fund has multiples_of_units
    amount?: number; // buys: USD
    amountLocal?: number; // kfhc: amount in the fund currency
    currency?: string; // kfhc: fund currency
    navUsed?: number; // kfhc: nav_per_unit used (fund currency)
    navBufferPct?: number; // kfhc buys: the model's NAV buffer applied on top of navUsed
    navDate?: string; // kfhc: fund nav_date (dd-MM-yyyy)
    currentPct: number;
    targetPct: number;
    driftPct: number;
    shortfallUsd?: number; // buys: target − current (draft: at draft time; remaining buys: from the live position); available cash is spread pro-rata to it
    proceedsUsd?: number; // sells: qty × last price / nav estimate — display and the draft's buy estimate only
    reason: string; // 'drift' | 'sell_all' | 'not_in_level' | 'fallback:<key>' | 'below_min_trade' | 'below_min_redemption' | 'min_holding_hold' | 'below_min_subscription' | 'unknown_holding'
    minHoldingPolicy?: 'sell_all' | 'hold';
    rowId?: string; // global_dpm_allocations doc id once written (allfunds only)
    round?: number; // placement round the line was placed in (GlobalDpmBatch.rounds[].n)
    orderDate?: string; // dd-MM-yyyy order date of that placement (allfunds: the trade_dates bucket of the row)
    excluded?: boolean; // dropped from this batch by the team — never placed, restorable
    heldUnits?: number; // kfhc buy candidates: units held at draft time (the additional-subscription minimum applies once the minimum holding is there)
    done?: boolean; // kfhc instructions: ticked by the team once the house order is placed
    doneBy?: string;
    doneAt?: Timestamp;
}

/** One placement: the team placed these orders (one per instrument and side) on these dates. Row ids carry `_r{n}` so a later round on the same instrument never collides. */
export interface GlobalDpmRound {
    n: number;
    placedAt: Timestamp;
    placedBy: string;
    dates: Record<string, string>; // `${key}|${side}` → dd-MM-yyyy
    positionSource?: string; // what the round's buys were sized from: 'trade_dates/21-09-2026' (gateway sync) or 'portfolios' (5 AM load)
}

/** global_dpm_batches/{id} — one rebalance run; lines stay on the doc (~90 portfolios × ≤ 15 lines). */
export interface GlobalDpmBatch {
    id: string;
    modelVersionId: string;
    modelLabel: string;
    name?: string; // given by the team (optional, renameable); shown instead of modelLabel — see batchTitle
    scope: { riskLevels?: string[]; portfolioIds?: string[]; keys?: string[]; excludedPortfolioIds?: string[] }; // keys: only these model lines trade (single-security rebalance); excludedPortfolioIds: left out by the team (snapshot reason 'excluded by team')
    thresholdPct: number;
    minTradeAmount: number; // 0 from the UI — house-fund minimums come from the fund docs, AllFunds has none
    cashBufferPct: number;
    minCompletionPct: number; // portfolios holding less than this share of their level's target weight are "incomplete" and excluded
    roundUpPct?: number; // multiples-of-units house funds (sukuk: 1,000): the next step is bought when the gap to it is ≤ this % of a step and the cash covers it. Absent (older batches) = BATCH_DEFAULTS.roundUpPct
    status: 'draft' | 'open' | 'done'; // open once a round is placed; done when the team closes it
    mode?: 'rebalance' | 'liquidate'; // absent = rebalance. liquidate: every held model line of the scoped portfolios is sold in full, no buys
    rounds: GlobalDpmRound[]; // placement rounds — each writes its own allocation rows and excel; sells and buys may share a round
    createdBy: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    fxUsdKwd: number; // config/exchange_rates.rates.USD (KWD per USD) at creation
    portfolios: Record<string, GlobalDpmBatchPortfolio>; // snapshot at creation (audit + which portfolios are in) — the remaining buys are sized from the live position, not from this
    lines: GlobalDpmBatchLine[]; // allfunds lines → allocation rows
    kfhcLines: GlobalDpmBatchLine[]; // house-fund instructions, never rows
    skipped: GlobalDpmBatchLine[]; // lines that did not trade, with the reason (nothing is silent)
    buyCandidates?: GlobalDpmBatchLine[]; // the draft's buy shortfalls before fallback / cash sizing — which keys the buy leg re-sizes from the live position
}

export interface BurganTrade extends Trade {
    portfolioId?: string;
    commission?: number;
    stamps?: number;
}

export interface TradeDate {
    // The date of the trades as a Firestore timestamp
    date: Timestamp;
    // Total number of buy orders
    buyTotal: number;
    // Total number of sell orders
    sellTotal: number;
    // Sum of all filled quantities
    totalFilledQty: number;
    // Sum of all order quantities
    totalOrderQty: number;
    // Sum of all pending quantities
    totalPendingQty: number;
    // Total number of rows/trades
    totalRows: number;
    // Status of the sync process
    syncStatus: 'NEED_SYNC' | 'SYNCING' | 'DONE' | 'ERROR';
    // The stats object for this trade date
    stats: Record<string, TradeDateStats>;
    // Foreign exchange rates used for converting trade values to KWD
    fx_rates: Record<string, string>; // e.g., { "USD": "0.30", "EUR": "0.25" }
}

export interface TradeDateStats {
    // The date of the trades as dd-MM-yyyy
    id: string;
    // Number of trades that were allocated
    allocatedTrades: number;
    // Total number of buy orders
    buyTotal: number;
    // The trade date as a Firestore timestamp
    date: Timestamp;
    // Total number of sell orders
    sellTotal: number;
    // Sum of all filled quantities
    totalFilledQty: number;
    // Sum of buy order quantities
    totalBuyQty: number;
    // Sum of sell order quantities
    totalSellQty: number;
    // Total number of rows/trades
    totalRows: number;
    // Total value of buy trades
    buyTotalValue: number;
    // Total value of buy trades
    sellTotalValue: number;
    // buyTotalValue - sellTotalValue
    netTotalValue: number;
    // All Trades Allocated
    allTradesAllocated?: boolean;
    //States of the trades
    states: Record<TradeState, number>;
}

export type TradeState = 'PENDING' | 'LOCKED' | 'GENEVA_ERROR';

export interface ManualTradePortfolioStats extends TradeDateStats {
    trades: ManualTrade[];
}

export interface AddManualTradeRequest {
    bondId?: string;
    nominalValue?: number;
    pricePerUnit?: number;
    totalPrice?: number;
    type?: 'BUY' | 'SELL';
    accruedProfit?: number;
    settlementAmount?: number;
    kfhcFees?: number;
}

export type ExchangeMap = Record<string, Record<string, string>>;

export interface CombinedPortfolioGroup {
    portfolioIds: string[];
    name: string;
    cash: number;
    localPositions: {
        ticker: string;
        firebase_key: string;
        quantity: number;
    }[];
}

export const getTickerID = (
    ticker: string,
    exchange: string,
    submarket: string,
    exchangeMap?: ExchangeMap,
): string => {
    if (!exchangeMap) {
        //   throw new Error('No exchange map provided');
        return '';
    }

    const subMap = exchangeMap[exchange];
    if (!subMap) {
        //   throw new Error(`No mapping found for exchange “${exchange}”`);
        return '';
    }

    const mappedCode = subMap[submarket] || subMap['DEFAULT'];
    if (!mappedCode) {
        //   throw new Error(
        //     `No mapping found for submarket “${submarket}” under exchange “${exchange}”`
        //   );
        return '';
    }

    return `${ticker}@${mappedCode}`;
};

/// For the Napier API
export interface NapierTokenResponse {
    access_token: string;
    expires_in: number;
    refresh_expires_in: number;
    refresh_token: string;
    token_type: 'Bearer';
    'not-before-policy': number;
    session_state: string;
    scope: string;
}

export interface NapierScreeningRequest {
    civilID: string;
    kycID: string;
    /** Customer doc id of the KYC owner.  Preferred over the civilID lookup when present (a company KYC may carry a signatory's civil ID). */
    customerID?: string;
}

/** How a related party is linked to the KYC being screened. `minor` is retained for historical results only. */
export type RelatedPartyScreeningRelation = 'authorized_signatory' | 'board_director' | 'executive_manager' | 'beneficial_owner' | 'guardian' | 'minor';

/** A director or executive manager declared in an entity KYC. */
export type ControllingPerson = {
    full_name: string;
    date_of_birth: string;
    nationality: string;
    role: string;
    // Optional only for historical KYC rows. New submissions validate all identity fields.
    civilID?: string;
    name_ar?: string;
    mobile?: string;
    gender?: string;
    /** Resolved server-side by Civil ID; never trust a submitted customer id. */
    customerID?: string;
};

/** One on-demand screening of a party related to a KYC — see KYC.aml_screening_related. */
export interface RelatedPartyAMLScreening {
    customerID: string;
    relation: RelatedPartyScreeningRelation;
    crmID?: string;
    name_en?: string;
    name_ar?: string;
    customerType?: CustomerType;
    status: 'completed' | 'failed';
    /** Why the screening could not run (missing customer, Napier error ...). */
    error?: string;
    screenedAt: string; // ISO date-time
    aml_screening?: onDemandAMLScreeningResponse;
}
export interface NapierCustomerUpdateRequest {
    napeirCustomerData: Record<string, any>; // customer data based on the Napier 'Customer' structure
}
export interface NapierCustomerCreateRequest {
    firestoreCustomerData: Customer; // customer data based on "firestore"
}

export interface MoneyMarketInstrument {
    id: number;
    issuer: string;
    issuerCode: string;
    issuerCountry: string;
    currency: string;
    frequency: string;
    days: number;
    maturityDate: Timestamp | null;
    issueDate: Timestamp | null;
    firstCouponDate: Timestamp | null;
    exchange: 'OTC';
    description: string;
    rate: number;
}

export interface ManualTrade {
    id?: string;
    type: 'sukuk' | 'moneyMarket';
    portfolioId: string;
    portfolioTypeCode?: string; // Denormalized from the portfolio so Firestore queries/rules can enforce visibility per portfolio type

    transactionType: 'BUY' | 'SELL';
    tradeDate?: Timestamp | null;
    settlementDate?: Timestamp | null;
    sukuk?: Bond;
    moneyMarket?: MoneyMarketInstrument;
    selectedSukukId?: string;
    selectedSukukDesc?: string;
    nominalValue: number;
    pricePerUnit: number;
    custodianAccountId?: string;
    totalPrice: number;
    kfhcFees: number;
    principal: number;
    accruedProfit: number;
    settlementAmount: number;
    yield: number;
    counterpartyName?: string;
    counterpartyAccount?: string;
    remarks?: string;
    state?: TradeState;
    createdBy?: string; // Name of the user who created the trade
    createdById?: string; // ID of the user who created the trade
    createdAt?: Timestamp; // Timestamp of when the trade was created
    updatedBy?: string; // Name of the user who updated the trade
    updatedById?: string; // ID of the user who updated the trade
    updatedAt?: Timestamp; // Timestamp of when the trade was updated
    fundStructure?: string; // Fund structure for Geneva integration
    geneva_responses?: Record<string, GenevaResponse>; // Written by the Geneva loader per record (new_sukuk, buy_sukuk, ...)
    geneva_response?: GenevaResponse; // New-Issuer creation status (sukuk.NEW_ISSUER), kept apart from geneva_responses so it never drives state
    newIssuerCreated?: string; // NameSort of the new Issuer already created in Geneva for this trade; re-sends skip it
    // Audit trail when a new-sukuk send hit an existing ISIN in Geneva and the trade
    // was re-sent once as a buy of the existing sukuk (see download_manual_trades.tsx).
    duplicateSukukRecovery?: {
        existingCode: string;
        attemptedCode: string | null;
        recoveredAt: Timestamp;
    };
    // Previous geneva_responses, archived before a re-send (see download_manual_trades.tsx).
    geneva_responses_history?: { responses: Record<string, GenevaResponse>; archivedAt: Timestamp }[];
    sentAt?: Timestamp; // Set by manualTradeUpdated when the trade becomes LOCKED; lets the ops page show sends by sending date
    sentBy?: string; // Display name of the user who last pressed Send
    sentById?: string;
}

export interface OrderStatusType {
    orderStatus: Tag;
    kycStatus?: Tag | FieldValue;
    paid?: Tag | FieldValue;
    reviewed?: string;
    approved?: string;
    operationsApproved?: string;
    manual?: Tag | FieldValue;
}

export interface ServiceStatusType {
    serviceStatus: Tag;
    riskStatus?: Tag;
    kycStatus?: Tag | FieldValue;
    authorized_signatory_approved?: string;
    execution?: string;
    activation?: string;
    paid?: Tag | FieldValue;
    manual?: Tag | FieldValue;
}

export interface IpoRequestStatusType {
    ipoRequestStatus: Tag;
    kycStatus?: Tag | FieldValue;
    authorized_signatory_approved?: string;
    paid?: Tag | FieldValue;
}

export interface TransactionStatusType {
    transactionStatus: Tag;
    kycStatus?: Tag | FieldValue;
    execution?: string;
}

// Workflow system for approval processes
export interface WorkflowStep {
    type: string;
    waitingTag: Tag;
    approvedField: string;
    validate?: (tags: ServiceStatusType) => boolean | Promise<boolean>;
    onStepComplete?: (
        stepId: string,
        tags: ServiceStatusType,
    ) => void | Promise<void>;
    onStepWaiting?: (tags: ServiceStatusType) => void | Promise<void>;
}

export interface WorkflowConfig {
    steps: WorkflowStep[];
    doneTag: Tag;
    onWorkflowComplete?: (tags: ServiceStatusType) => void | Promise<void>;
}

export type ProductType = 'customers' | 'orders' | 'services' | 'transactions';
export type UserAction = 'viewing';

export interface PresenceData {
    id: string; // Product ID being viewed
    type: ProductType; // Type of product
    uid: string; // User ID
    name: string; // Display name
    email: string; // User email
    timestamp: Timestamp; // When presence was last updated
    expiresAt: Timestamp; // When this presence expires
    action: 'viewing';
    pageKey?: string; // Compound key for easier querying: `${type}_${id}`
}

export interface Viewer {
    id: string; // Same as uid for uniqueness
    uid: string;
    name: string;
    email: string;
    timestamp?: Timestamp;
    viewingDuration: number; // In seconds
    isStale: boolean; // If connection might be stale
    isCurrentUser?: boolean; // If this is the current user
    action: 'viewing';
}

export interface PresenceContextType {
    presenceData: Record<string, Viewer[]>;
    getViewers: (productType: ProductType, productId: string) => Viewer[];
    getViewerCount: (productType: ProductType, productId: string) => number;
    getCurrentViewers: () => Viewer[];
    getCurrentViewerCount: () => number;
    isUserViewing: (
        productType: ProductType,
        productId: string,
        userId: string,
    ) => boolean;
    allActiveUsers: string[];
}

// Product comments for KYC, Orders, Services, and Transactions
// This is used to add comments to products like KYC, Orders, Services, and Transactions
export interface ProductComment {
    id?: string;
    comment: string;
    createdBy: string; // Name of the user who created the comment
    createdById: string; // ID of the user who created the comment
    createdByEmail: string; // Email of the user who created the comment
    createdAt: Timestamp; // Timestamp of when the comment was created
    customClaims?: CustomClaims; // Name of the user who created the comment
    updatedAt?: Timestamp; // Timestamp of when the comment was updated
    updatedBy?: string; // Name of the user who updated the comment
    deleted?: boolean; // Flag to indicate if the comment is deleted
    deletedAt?: Timestamp; // Timestamp of when the comment was deleted
    deletedBy?: string; // Name of the user who deleted the comment
    deletedById?: string; // ID of the user who deleted the comment
}

// handling QR code generation results
export interface QRCodeResult {
    customersQRList: Record<string, string>;
}

export interface QRCodeRequest {
    token: string;
    customersQRList: Record<string, string>;
    baseUrl: string;
}

export interface QRCodeVerifyResult {
    crmID: string;
    paciData: Record<string, string>;
    path?: string;
}

export interface QRCodeVerifyRequest {
    encryptedData: string;
    iv: string;
    token: string;
}

export interface KFHTradeTradingAccount {
    ACCOUNT_NAME: string;
    EXTERNAL_REFERENCE: string;
    INVESTOR_ACC_NO: string;
    CREATED_DATE: string;
    SOURCE: string; // security/cash/maybe others?
    CURRENCY: string;
    CRM_ID: string;
    napierID?: string;
}

// ==================== KYC CONFIGURATION TYPES ====================
/**
 * KYC Configuration System Types
 *
 * This section contains all type definitions for the dynamic KYC Configuration system.
 * The system allows creating flexible, multi-language KYC forms with:
 * - Dynamic page and product creation
 * - Nested product support
 * - Page overrides and section hiding
 * - Multiple field types with validation
 * - Bilingual support (English/Arabic)
 * - PDF generation with custom rendering
 *
 * @module KYCConfiguration
 */

// ==================== ENUMS ====================

/**
 * Available field types for KYC form components
 * Each type represents a different input control
 */
export enum FieldTypes {
    TEXT = 'text',
    INPUT = 'input',
    RADIO = 'radio',
    CHECKBOX = 'checkbox',
    SELECT = 'select',
    PACI = 'paci',
    DOCUSIGN = "docusign",
    SIGNATURE = 'signature',
    FILE_UPLOAD = 'fileUpload',
    DATE_PICKER = 'datePicker',
    COUNTRY_PICKER = 'countryPicker',
    AGREEMENT_CHECKBOX = 'agreementCheckbox',
    THANK_YOU_TICK_MARK = 'thankYouTickMark',
    TRANSFER_AMOUNT = 'transferAmount',
    DOWNLOAD_CONTRACT = 'downloadContract',
    OBJECT_LIST = 'objectList',
    SECURITIES_SELECTION = 'securitiesSelection',
    MOBILE_WITH_CODE = 'mobileWithCode',
}

// ==================== CORE INTERFACES ====================

/**
 * KYC Product
 * Represents a complete KYC product containing pages or nested products
 *
 * @example
 * const product: KYCProduct = {
 *   id: 'order_kyc',
 *   title: 'Order KYC',
 *   title_ar: 'طلب التحقق',
 *   description: 'KYC for order processing',
 *   description_ar: 'التحقق لمعالجة الطلب',
 *   pages: [{ id: 'personal_info', type: 'page' }]
 *   pdf_page_index: ['personal_info', 'address_info', 'document_upload']
 * };
 */
export interface KYCProduct {
    /** Unique identifier for the product */
    id: string;
    /** Product title in English */
    title: string;
    /** Product title in Arabic */
    title_ar: string;
    /** Product description in English */
    description: string;
    /** Product description in Arabic */
    description_ar: string;
    /** Array of page or product references */
    pages: ItemReference[];

    pdf_page_index: string[]; // Array of page IDs in order for PDF generation

    version?: string; // Version number for the product
}

/**
 * KYC View Product
 * Extended product with fully resolved page data for rendering
 */
export interface KYCViewProduct {
    /** Unique identifier */
    id: string;
    /** Product title in English */
    title: string;
    /** Product title in Arabic */
    title_ar: string;
    /** Product description in English */
    description: string;
    /** Product description in Arabic */
    description_ar: string;
    /** Fully resolved pages with all components */
    pages: PageData[];
}

/**
 * KYC Page
 * Represents a single page in the KYC form
 */
export interface KYCPage {
    /** Unique page identifier */
    id: string;
    /** Page title in English */
    title: string;
    /** Page title in Arabic */
    title_ar: string;
    /** Optional visibility condition */
    page_condition?: string;
    /** Optional description in English */
    description?: string;
    /** Optional description in Arabic */
    description_ar?: string;
    /** Array of component sections */
    components?: ComponentSection[];
    /** Array of page actions (Submit, Save, etc.) */
    action?: PageAction[];

    /** Optional rendering variables */
    pageVariables?: Record<string, string>;

    /** Optional tags to filter pages for PDF generation */
    tags?: string[];

    /** In the contract PDF, start this page's content on a new physical page */
    pdf_start_on_new_page?: boolean;

    // Skip KYC flag for specific scenarios
    /** Flag to indicate if KYC can be skipped */
    is_skip_kyc?: boolean;

    eTag: string;
}

/**
 * Page Section
 * Basic section structure with bilingual titles
 */
export interface PageSection {
    /** Section identifier */
    id: string;
    /** Section title in English */
    title: string;
    /** Section title in Arabic */
    title_ar: string;
}

/**
 * Page Data
 * Complete page with resolved components and variables
 */
export interface PageData {
    /** Page identifier */
    id: string;
    /** Page title in English */
    title: string;
    /** Page title in Arabic */
    title_ar: string;
    /** Page description in English */
    description: string;
    /** Optional visibility condition */
    page_condition?: string;

    /** Optional tags to filter pages for PDF generation */
    tags?: string[];
    /** Page description in Arabic */
    description_ar: string;
    /** Component sections */
    components: ComponentSection[];
    /** Page actions */
    action: PageAction[];
    /** Optional rendering variables */
    pageVariables?: Record<string, string>;

    /** In the contract PDF, start this page's content on a new physical page */
    pdf_start_on_new_page?: boolean;

    // Skip KYC flag for specific scenarios
    /** Flag to indicate if KYC can be skipped */
    is_skip_kyc?: boolean;

    /**
     * eTag used for concurrency control (timestamp-based).
     *
     * This eTag represents the last-updated timestamp of a KYC page.
     * When the client fetches a page, it receives this timestamp.
     * The client must send back the *same* timestamp when submitting an update.
     *
     * The server compares the incoming eTag with the stored eTag:
     * - If they match → update is allowed
     * - If they do NOT match → someone else has updated the page, so the update is rejected
     *
     * Example:
     *
     * // 1. Server returns the page with its timestamp eTag:
     * {
     *   id: "page123",
     *   title: "Company Info",
     *   eTag: "2025-01-14T10:23:11.000Z"   // Original timestamp
     * }
     *
     * // 2. Client edits the page and must send back the *same* eTag:
     * {
     *   id: "page123",
     *   title: "Updated Company Info",
     *   eTag: "2025-01-14T10:23:11.000Z"   // Must match the original eTag
     * }
     *
     * // 3. When creating or updating a KYC page:
     * //    - If incoming eTag === stored eTag → use the user's active KYC page (update allowed)
     * //    - If incoming eTag !== stored eTag → a newer version exists, so create a new KYC page version
     *
     * This mechanism ensures the system knows whether to reuse the existing KYCPage
     * or generate a new version based on timestamp comparison.
     */
    eTag: string;
}

// ==================== REFERENCE TYPES ====================

/**
 * Item Reference
 * Reference to a page or product with customization options
 */
export interface ItemReference {
    /** Referenced item ID */
    id: string;
    /** Item type - page or product */
    type: 'page' | 'product';
    /** Optional page overrides */
    override_pages?: PageOverride[];
    /** Optional section hiding rules */
    hide_sections?: PageHideSection[];

    // Skip KYC flag for specific scenarios
    /** Flag to indicate if KYC can be skipped */
    is_skip_kyc?: boolean;

    // For products - array of child page variable overrides
    pageVariablesOverride?: PageVariablesOverride[];

    // For direct pages
    pageVariables?: Record<string, string>;

    // Array of pages to skip KYC for specific scenarios
    /** Array of pages to skip KYC */
    skip_pages?: SkipKYCPage[];

    isSeparatePage?: boolean; // Whether to render this item as a separate page or inline
    hideHeaderFooter?: boolean; // Whether to hide header/footer when rendering this item as a separate page

    product_title?: string; // Optional custom title for product references >> if null or undefined, use the default title from the product itself
    product_title_ar?: string; // Optional custom title in Arabic for product references >> if null or undefined, use the default title from the product itself
    product_description?: string; // Optional custom description for product references >> if null or undefined, use the default description from the product itself
    product_description_ar?: string; // Optional custom description in Arabic for product references >> if null or undefined, use the default description from the product itself

    header_left_image?: string; // base64
    header_right_image?: string; // base64
    footer_left_image?: string; // base64
    footer_text?: string;
}

/**
 * Page Skip
 * Configuration to skip KYC for specific pages
 */
export interface SkipKYCPage {
    page_id: string;
    is_skip_kyc: boolean;
}

/**
 * Page Override
 * Configuration to replace one page with another
 */
export interface PageOverride {
    /** Page to override */
    page_id: string;
    /** Replacement page */
    override_page_id: string;
}

export interface PageVariablesOverride {
    /** Page to override */
    page_id: string;
    /** Variable overrides for the page */
    pageVariables?: Record<string, string>;
}

/**
 * Page Hide Section
 * Configuration to hide specific sections
 */
export interface PageHideSection {
    /** Page containing sections */
    page_id: string;
    /** Section IDs to hide */
    section_id: string[];
}

/**
 * Display Item
 * Item reference with display properties for UI
 */
export interface DisplayItem extends ItemReference {
    /** Display title in English */
    title: string;
    /** Display title in Arabic */
    title_ar: string;
    /** Optional description in English */
    description?: string;
    /** Optional description in Arabic */
    description_ar?: string;
    /** Child pages (for product types) */
    childPages?: KYCPage[];
}

// ==================== COMPONENT TYPES ====================

/**
 * Component Section
 * A section containing multiple fields with styling and visibility rules
 */
export interface ComponentSection {
    /** Section title in English */
    section_title: string;
    /** Section title in Arabic */
    section_title_ar: string;
    /** Section description in English */
    section_description: string;
    /** Section description in Arabic */
    section_description_ar: string;
    /** Display fields in list view */
    list_view: boolean;
    /** Visible in KYC document */
    is_visible_kyc: boolean;
    /** Visible in contract document */
    is_visible_contract: boolean;
    /** Optional background color */
    background_color?: string;
    /** Array of fields */
    section_fields: SectionField[];
    /** Optional visibility condition */
    condition?: string;
    /** Optional section identifier */
    section_id?: string;
    /** Optional numeric ID */
    unique_id?: number;

    /** Allow removing items in dynamic sections */
    remove_item?: boolean;

    /** Optional show/hide Section Title and Description */
    is_visible_title?: boolean;
}

/**
 * Section Field
 * Individual field with comprehensive configuration
 */
export interface SectionField {
    /** Field type */
    type: FieldTypes;
    /** Label in English */
    label?: string;
    /** Label in Arabic */
    label_ar?: string;
    /** Placeholder in English */
    placeholder?: string;
    /** Placeholder in Arabic */
    placeholder_ar?: string;
    /** Sub field key: for mobileWithCode, the filledData key that stores the dial code (e.g. "mobile_code"). Never holds the value itself. */
    sub_key?: string;
    /** Resolved value of `sub_key` (the dial code) set server-side when pages are initialized; used when rendering the contract. */
    sub_value?: string;
    /** Unique field key */
    key: string;
    /** Section ID (if part of section) */
    section_id?: string;
    /** Is field required */
    required?: boolean;
    /** Validation regex pattern */
    regex?: string;
    /** Options for select/radio/checkbox */
    options?: ComponentOption[];
    /** Numeric unique ID */
    unique_id?: number;
    /** Field value (string, array, or any) */
    value: string | string[] | any;

    /** Column definitions used by the reusable objectList field. */
    columns?: Array<{ key: string; key_ar?: string; label: string; label_ar: string }>;

    /** Input Field value in Arabic (if applicable) */
    value_ar?: string | string[] | any;

    // File upload configuration
    /** Max files allowed (default: 5) */
    maxFile?: number;
    /** Max file size in KB (e.g., 5000 = 5MB) */
    fileSize?: number;
    /** File storage path template */
    fileStoragePath?: string;
    /** Allowed extensions (e.g., '.jpg,.jpeg,.png,.pdf') */
    fileExtension?: string;
    /** File upload prompt text */
    filePrompt?: string;
    /** AI checkers for validation (e.g., ['chat-gpt', 'gemini']) */
    fileAIChecker?: string[];

    // PDF rendering options
    /** Show placeholder at bottom in PDF */
    showPlaceholder?: boolean;
    /** Show EN/AR boxes side-by-side in PDF */
    showTwoBox?: boolean;
    /** Visible in KYC document */
    is_visible_kyc: boolean;
    /** Visible in contract document */
    is_visible_contract: boolean;
    /** Continue from previous field (no line break in PDF) */
    isContinuation?: boolean;

    /**
     * firstDate: Minimum selectable date (earliest date user can pick).
     * Example: if today is 2024-06-15, and firstDate is 2024-01-01, user can select any date from 2024-01-01 to 2024-06-15
     * lastDate: Maximum selectable date (latest date user can pick).
     * Example: if today is 2024-06-15, and lastDate is 2024-12-31, user can select any date from 2024-06-15 to 2024-12-31
     */
    firstDate?: Timestamp;
    lastDate?: Timestamp;

    /** Field visibility condition */
    condition?: string;
    /** Agreement type for the field */
    agreementType?: AgreementType;

    /** Tooltip in English */
    tooltip?: string;
    /** Tooltip in Arabic */
    tooltip_ar?: string;
}

/**
 * Agreement Type
 * Defines the type of agreement for a field
 */
export enum AgreementType {
    Content = 'content',
    URL = 'url',
    ShownInFinalAgreement = 'shown_in_final_agreement_checkbox',
}

/**
 * Component Option
 * Option for select, radio, or checkbox fields
 */
export interface ComponentOption {
    /** Option label in English */
    label: string;
    /** Option label in Arabic */
    label_ar: string;
    /** Option value */
    value: string;
    /** Nested fields (shown when selected) */
    section_fields?: SectionField[];
    /** Numeric unique ID */
    unique_id?: number;
    /** Condition to disable option */
    disabled_when?: string;

    /** JavaScript to run against formData when this option is selected */
    on_select?: string;

    /** JavaScript to run against formData when this checkbox option is unchecked */
    on_unselect?: string;

    placeholder?: string;
    placeholder_ar?: string;

    /** Tooltip in English */
    tooltip?: string;
    /** Tooltip in Arabic */
    tooltip_ar?: string;
}

// ==================== ACTION & CONFIG TYPES ====================

/**
 * Page Action
 * Action button configuration (Submit, Save, etc.)
 */
export interface PageAction {
    /** Action type identifier */
    type: string;
    /** Action label in English */
    label: string;
    /** Action label in Arabic */
    label_ar: string;
    /** Unique action key */
    key: string;
    /** Does this action save data */
    is_save: boolean;
    /** Is action visible */
    visible: boolean;
    /** Numeric unique ID */
    unique_id?: number;
}

/**
 * Field Type Config
 * Metadata for field types in form builder
 */
export interface FieldTypeConfig {
    /** Field type */
    type: FieldTypes;
    /** Display name in English */
    name: string;
    /** Display name in Arabic */
    name_ar: string;
    /** Icon component */
    icon: any;
    /** Supports options */
    hasOptions: boolean;
    /** Supports placeholder */
    hasPlaceholder: boolean;
    /** Visible in KYC by default */
    is_visible_kyc: boolean;
    /** Visible in contract by default */
    is_visible_contract: boolean;
    /** Supports regex validation */
    hasRegex: boolean;
    /** Default field template */
    defaultTemplate: Partial<SectionField>;
}

export type ContractLang = 'ar' | 'en';

// productRef: /customers/10458626/services/SyO45Mwjlg7fcLdJXwCS
export interface GenerateContractProps {
    productRef?: string;

    /**
     * Contract link the (anonymous) contract app is working in. The server
     * renders only the link's own product, and only once the link is signed.
     * Staff calls from the portal carry an ID token instead and need no link.
     */
    linkId?: string;

    /** This flag indicates if the KYC PDF has been generated from user submission */
    is_kyc_pdf_submitted?: boolean;
    /** Optional tags for the page
     * Used for PDF generation and categorization
     */
    pdfTags?: string[];

    /** QR Code verification base URL */
    baseUrl: string;

    showQRCodePDF?: boolean;

    empty?: boolean;

    /** Language for the generated contract */
    lang: ContractLang;

    /** Optional internal PDF document title metadata */
    pdfTitle?: string;
}

export interface GenerateKYCProductPreviewPDFProps {
    productId: string;
    /** QR Code verification base URL */
    baseUrl: string;
    /** Optional tags for PDF generation and categorization */
    pdfTags?: string[];
    showQRCodePDF?: boolean;
    /** Language for the generated contract */
    lang: ContractLang;
}

export interface GenerateKYCProductPreviewHTMLResult {
    success: boolean;
    html: string;
    length: number;
}

export interface GenerateContractResult {
    success: boolean;
    pdfBase64: string;
    length: number;
}

export type ContractType =
    | 'info_update'
    | 'kfhtrade'
    | 'kfhbrokerage'
    | 'portfolio'
    | 'ipo'
    | 'subscription'
    | 'redemption'
    | 'security_out'
    | 'security_in'
    | 'cash_out'
    | 'cash_in'
    | 'kyc';

// ==================== DOCUSIGN SIGNING ====================

// Mirrors paci_verifications: one doc per signing ceremony, listened to by the
// contract app while it waits for the customer to sign.
export const docusignEnvelopesCollection = 'docusign_envelopes';

/**
 * How the customer authorizes the KYC contract.
 * 'paci' (default when absent) — Kuwait Mobile ID authentication.
 * 'docusign' — remote e-signature for customers who cannot use PACI
 * (non-residents, no Kuwait Mobile ID). First-class peer of PACI, distinct
 * from `manual` which remains the staff-filled/pen-and-paper path.
 */
export type SigningMethod = 'paci' | 'docusign';

/** Envelope lifecycle as reported by DocuSign. */
export type DocusignEnvelopeStatus = 'sent' | 'delivered' | 'completed' | 'declined' | 'voided';

export type DocusignDeliveryMethod = 'email' | 'sms' | 'whatsapp';

/**
 * Signing evidence stored on the contract link (and later inside
 * completedKYCData) — the DocuSign counterpart of paciData. The envelope's
 * completed PDF carries DocuSign's tamper seal; certificatePath points at the
 * certificate of completion (audit trail) required as signing evidence.
 */
export interface DocusignData {
    envelopeId: string;
    status: DocusignEnvelopeStatus;
    deliveryMethod: DocusignDeliveryMethod;
    /** Hash of the filledData snapshot rendered into the envelope PDF. An
     * answer edit after sending changes the hash → the envelope must be
     * voided and re-issued. */
    filledDataHash: string;
    signerName?: string;
    signerEmail?: string;
    /** E.164, e.g. +96551234567 */
    signerPhone?: string;
    sentAt?: Timestamp;
    completedAt?: Timestamp;
    declinedAt?: Timestamp;
    voidedAt?: Timestamp;
    voidedReason?: string;
    /** Storage path of the DocuSign-completed (sealed) contract PDF. */
    signedPdfPath?: string;
    /** Storage path of the certificate of completion. */
    certificatePath?: string;
    /** Operations rejected this signature (reject_signature). The product then behaves like a
     *  rejected KYC (needs client update) until the client re-signs through the update link. */
    rejected?: boolean;
    rejectedAt?: Timestamp;
    rejectedBy?: string;
    rejectionComments?: string;
}

/** Firestore doc in docusign_envelopes/{id} — the contract app listens here. */
export interface DocusignEnvelopeDoc {
    id: string;
    linkID: string;
    envelopeId: string;
    status: DocusignEnvelopeStatus;
    deliveryMethod: DocusignDeliveryMethod;
    filledDataHash: string;
    /** Civil ID (or passport) of the person signing — parity with
     * paci_verifications.customer_id. */
    customer_id: string;
    created: Timestamp;
    updated?: Timestamp;
    error?: string;
}

// ==================== COMPLETED KYC ====================

// This collection stores completed KYC submissions with all collected data and pages for the customer only.
export const contractLinksCollection = 'contract_links';

/**
 * Completed KYC Data
 * Represents a completed KYC submission
 */
export interface CompletedKYCData {
    /** Unique KYC ID */
    id: string;
    /** Product ID used */
    productId: string;
    /** Product title in English */
    productTitle: string;
    /** Product title in Arabic */
    productTitleAr: string;
    /** Product description in Arabic */
    productDescriptionAr: string;
    /** Product description in English */
    productDescription: string;

    /** Created timestamp */
    created?: Timestamp;
    expires?: Timestamp;
    /** Optional customer ID */
    customerId?: string;
    /** Optional product reference */
    productRef?: string;
    /** Customer civil ID */
    civilID: string;

    // Was the KYC filled manually by staff
    manual?: boolean;

    /**
     * How this link's contract gets signed. Absent/'paci' → PACI
     * authentication (existing behavior). 'docusign' → remote e-signature;
     * submission then requires docusignData.status === 'completed' instead
     * of paciData.
     */
    signingMethod?: SigningMethod;

    /** DocuSign signing evidence — the docusign counterpart of paciData. */
    docusignData?: DocusignData;

    /** Optional PACI data */
    paciData?: PACIData;
    /** Optional form data for conditional rendering */
    filledData?: KYC;
    /** Key-value pairs for template variable replacement */
    pageVariables?: Record<string, string>;

    /** Optional prefill data for customer */
    prefillData?: Record<string, any>;

    /** The guardian's/representative's PACI data — filled instead of paciData for minors or accounts with someone acting on their behalf */
    guardianPaciData?: PACIData;

    // Optional linked contract KYC ID
    // We can use on Generating contract from KYC
    kyc_id?: string;

    /** Contract link lifecycle status. */
    status?: string;
    createOnSubmit?: boolean;
    cancelled?: boolean;
    cancelledAt?: Timestamp;
    submittedAt?: Timestamp;

    // Optional flag to auto-save KYC when the user changes from Flutter side
    autoSave?: boolean;
    contractType?: ContractType;
    uploaded_documents?: string[];
    needed_documents?: string[];
    version?: string;

    /** for contractType 'info_update': which fields the client may change through this link (the staff-side checkboxes) */
    updatable_fields?: CustomerUpdateField[];
    /**
     * for contractType 'info_update': the (possibly arbitrary) mobile number the
     * link was sent to.  The customer is only discovered after PACI reveals the
     * civil ID — civilID stays empty until then unless CR prefilled it.
     */
    mobile?: string;
    /** Dial code for `mobile` ("+965", "+20"); missing means +965. Set on public (virtual) links. */
    mobile_code?: string;
    /** Identity of a not-yet-created (virtual) non-Kuwaiti client: passport / national ID instead of a civil ID. */
    other_id?: string;
    other_id_type?: OtherIdType;
    /** ISO-2 issuing country of `other_id` (public page: the client's nationality). */
    other_id_country?: string;
    /** ISO-2 nationality captured on the public open-portfolio page for virtual clients. */
    nationality?: string;

    // Optional portfolio opening request data: Only use requestKYCUpdateExternalAPI
    portfolioOpeningRequest?: PortfolioOpeningRequest;

    // when kyc Rejection and need to fill another KYC with some prefilled data and selected pages
    selectedPages?: string[];
    rejectKycComments?: string;
    selectedSignatoryId?: string; // entity ID of the selected authorized signatory for the KYC PACI, Agreement and Signatures
}

export interface SubmitContractKYC {
    linkID: string;
    ip: string; // Optional IP address of the user submitting the KYC, can be used for logging or security purposes
    filledData?: KYC;
    // If provided, overrides the existing KYC ID on the link
    kycID?: string;
}

export interface RejectionFormValues {
    comments: string;
    sendSMS: boolean;
    usePACI: boolean;
    action: ActionType;
    linkInfo: Partial<UpdateLink>;
    missingFiles?: FileTag[];

    /** New KYC Data */
    kycID?: string;
    selectedPages?: string[];
    customerID?: string;
    token?: string;
    selectedSignatoryId?: string; // entity ID of the selected authorized signatory for the KYC PACI, Agreement and Signatures
}

export interface AgentNotficationFormValues {
    comments: string;
    missingFiles?: FileTag[];
    productID: string;
    token?: string;
}

export interface CreateContractLink {
    /**
     * Firestore reference path
     * Examples: "services/abc", "transactions/xyz"
     */
    productRef: string;

    /**
     * Optional customer ID (used only in the web app)
     */
    customerID?: string;

    /**
     * Optional access token (used only in the web app)
     */
    token?: string;

    /**
     * Create the link as a manual (pen-and-paper) KYC: no message is sent to
     * the customer and staff fill it in on their behalf. Portal only.
     */
    manual?: boolean;

    /**
     * Signing method for the link. Defaults to 'paci'. Use 'docusign' for
     * customers who cannot use Kuwait Mobile ID (e.g. non-residents).
     */
    signingMethod?: SigningMethod;

    linkWhatsappNumber?: string; // This number use for the commercial_register update KYC, Optional WhatsApp number to send the link 
}

// Portfolio warning type definition
export interface PortfolioWarning {
    accountId: string;
    tradeId: string;
    message: string;
    timestamp: number;
    side: 'BUY' | 'SELL';
    symbolID: string;
    portfolioId?: string;
}

export interface AllocationItem {
    name_ar: string;
    name_en: string;
    percentage: number;
    color: string;
}

export interface RiskLevel {
    id: string;
    name_ar: string;
    name_en: string;
    description_ar: string;
    description_en: string;

    // only present in multi_assets.*.risk_levels[].allocation
    allocation?: AllocationItem[];
}

export interface MinimumAmount {
    currency: string;
    amount: number;
}

/**
 * Represents a currency configuration with IBAN details for portfolio
 */
export interface PortfolioAllowedCurrency {
    /** IBAN account number */
    iban: string;
    /** Currency code (e.g., KWD, USD) */
    currency: string;
}

export interface ManagementOption {
    id: string;
    name_ar: string;
    name_en: string;
    /** Management option description in English */
    description_en: string;
    /** Management option description in Arabic */
    description_ar: string;

    portfolio_type: string; // e.g. "dpm" | "non-dpm"
    /** Asset class type */
    // e.g. "regular" | "sukuk"
    asset_class: string;
    /** Management fees percentage */
    fees: number;
    /** List of available risk levels */
    risk_levels: RiskLevel[];
    /** List of allowed currencies with IBAN */
    allowed_currencies: PortfolioAllowedCurrency[];
    /** KYC product identifier for main portfolio */
    kyc_product: string;
    /** Optional legacy KYC product override for sub-portfolios. Falls back to kyc_product. */
    sub_kyc_product?: string;

    /** Minimum investment amount requirement */
    minimum_amount: MinimumAmount;
    trading_commission?: number; // Optional trading commission percentage
    active: boolean;
    state: FundState;
}

export interface PortfolioEntry {
    id: string; // e.g. "equity_portfolio"
    name_ar: string; // e.g. "الأسهم"
    name_en: string; // e.g. "Equities"
    description_ar: string;
    description_en: string;
    active?: boolean; // not always present
    position: number; // 1,2,3,...
    minimum_amount: number; // 1000, 1500, ...
    type: string; // "portfolio"
    portfolio_type: string; // "equities" | "sukuk" | "multi_assets" | future values
    management_options: ManagementOption[];
    whatsappTemplate?: string;
    state: FundState;
    portfolio_purchase_request_name?: string; // Shown on Sukuk purchase request Page
}

/**
 * Dynamic root.
 * Keys like "equity_portfolio", "sukuk_portfolio", "multi_assets", etc.
 */
export interface PortfolioService {
    [portfolioKey: string]: PortfolioEntry;
}

export interface CashBalance {
    date: Timestamp;
    balance: number;
    blockedAmount: number;
}

export interface StockBalance {
    date: Timestamp;
    symbol: string;
    lastTradePrice: number;
    weightedAvgPrice: number;
    avgPrice: number;
    exchange: string;
    netHoldings: number;
    avgCost: number;
    previousClosed: number;
    weightedAvgCost: number;
}

export interface KfhTradeAccount {
    CRM_ID: string;
    id?: string;
    CURRENCY: string;
    ACCOUNT_NAME: string;
    KFHTRADE_ID: string;
    CREATED_DATE: string;
    INVESTOR_ACC_NO: string;
    EXTERNAL_REFERENCE: string;
    SOURCE: 'CASH' | 'SECURITY';
    cash_balance?: CashBalance;
    stock_balance?: StockBalance[];
}

export interface SyncNavRequest {
    fundCodes: string[];
}

export interface DeleteOrderRequest {
    /** product reference */
    // orders/abc
    // transactions/xyz
    productRef: string;
}

export interface MessageRecipientData {
    mobile: string;
    /** dial code for `mobile` ("+965", "+20"); missing means +965 */
    mobile_code?: string;
    id: string;
    email: string;
    smsMessage: string;
    name_ar: string;
    name_en: string;
}

/** @deprecated Use AiConfig. Kept for dual-read of legacy config/aiPrompt docs. */
export interface AIPrompt {
    service: string;
    order: string;
    kyc: string;
}

/**
 * Universal AI config at Firestore `config/ai`.
 * Nested maps for prompts, models, feature toggles, and a shared usage budget.
 */
export interface AiConfig {
    prompts?: {
        service?: string;
        order?: string;
        kyc?: string;
        cramMapping?: string;
    };
    models?: {
        /** One model for all KYC precheck contexts (service | order | kyc). */
        kyc?: string;
        cramMapping?: string;
    };
    enabled?: {
        kycReview?: boolean;
        cramMapping?: boolean;
    };
    limits?: {
        dailyLimit?: number;
        monthlyLimit?: number;
        /** CRAM mapping per-customer cooldown (seconds). */
        cooldownSeconds?: number;
    };
}

export type KycAiFieldFindingSeverity = "attention" | "warning";

export interface KycAiFieldFinding {
    field: string;
    severity: KycAiFieldFindingSeverity;
    note: string;
}

/** A product (order/service) linked to a KYC and surfaced to the AI review. */
export interface KycAiLinkedProduct {
    productType: "order" | "service";
    /** Firestore document id of the order/service. */
    id?: string;
    /** e.g. subscription / redemption for orders, the serviceType for services. */
    kind?: string;
    fundName?: string;
    amount?: number;
    units?: number;
}

export type KycAiPrecheckStatus = "completed" | "error" | "budget_skipped";
export type KycAiPrecheckLabel = "good" | "review" | "incomplete";
export type KycAiPrecheckRecommendedAction =
    | "auto_label_good"
    | "manual_review"
    | "request_more_info";

/**
 * Company PEP categories the AI matches employment info against. Mirrors
 * ContractPoliticalPosition plus seniorDiplomats / politicalPartyOfficials,
 * which exist in the written PEP definition but not the contract form enum.
 */
export type KycAiPepCategory =
    | "headsOfStatesGovernments"
    | "seniorPoliticians"
    | "seniorGovernmentalOfficials"
    | "seniorJudicialOfficials"
    | "seniorMilitaryOfficials"
    | "seniorDiplomats"
    | "seniorExecOfStateOwnedCompanies"
    | "politicalPartyOfficials"
    | "none";

export interface KycAiPepAssessment {
    /** 0–100: how likely the customer is a PEP, judged from employment info only. */
    likelihood: number;
    matchedCategory: KycAiPepCategory;
    /** One short sentence explaining the match (or why there is none). */
    reasoning: string;
}

/** Persisted at customers/{cid}/kyc/{kycId}/private/ai_precheck */
export interface KycAiPrecheck {
    status: KycAiPrecheckStatus;
    source?: "openrouter";
    version?: number;
    label?: KycAiPrecheckLabel;
    /** Short overall verdict (1–2 sentences). */
    summary?: string;
    reasons?: string[];
    missingFields?: string[];
    riskFlags?: string[];
    recommendedAction?: KycAiPrecheckRecommendedAction;
    fieldFindings?: KycAiFieldFinding[];
    /** PEP likelihood judged from employment info against the company PEP definitions. */
    pepAssessment?: KycAiPepAssessment;
    /** Orders / services tied to this KYC that were considered in the review. */
    linkedProducts?: KycAiLinkedProduct[];
    contentHash?: string;
    modelRequested?: string;
    modelResponded?: string;
    productType?: "service" | "order" | "kyc";
    promptInputSummary?: {
        kycKeys: string[];
        productKeys: string[];
    };
    errorMessage?: string;
    updatedAt?: Timestamp;
}

// ============================================================================
// KYC CONSTANTS
// ============================================================================

/**
 * Pages always included in KYC submission regardless of selected pages.
 * These pages are mandatory for every KYC flow:
 * - kyc_requirements: initial requirements page
 * - agreement_submit: final agreement and submission
 * - kyc_thank_you: confirmation page
 */
export const ALWAYS_SELECTED_PAGES = [
    'kyc_requirements',
    'agreement_submit',
    'kyc_thank_you',
    'agreement_submit_kyc_entity', // For commercial_register
];

export type AlwaysSelectedPage = (typeof ALWAYS_SELECTED_PAGES)[number];

export type IndividualIDType = 'civilID' | 'other_id';

// ---------------------------------------------------------------------------
// CRAM (Customer Risk Assessment Methodology)
// ---------------------------------------------------------------------------
// See docs/CRAM_PLAN.md. Types are project-wide; the scoring engine lives in
// shared-helpers/src/cramHelper.ts. Firestore data is seeded by utils/seedCRAMLists.js
// into config/cram + cram_lists/{listId}/items/{itemId}.

export type CramRating = 'Low' | 'Medium' | 'High' | 'Prohibited';
export type CramGeoRating =
    | 'Low'
    | 'Medium'
    | 'High'
    | 'Very High'
    | 'Prohibited';

export type CramCategoryKey =
    | 'customerType'
    | 'geography'
    | 'productsServices'
    | 'industryType'
    | 'directHigh';

export interface CramListItem {
    key: string;
    labelEn?: string;
    labelAr?: string;
    score?: number;
    scoreYes?: number;
    scoreNo?: number;
    flags?: Record<string, any>;
}

export interface CramBucketRange {
    min: number;
    max: number;
}

// CRAM rollout switch (config/cram.mode):
//   off    — legacy risk only; engine never runs.
//   shadow — compute + persist cram.* for comparison, but tags / expiry /
//            validation stay on the legacy flow and the live KYC-fill
//            trigger does NOT touch the customer journey.
//   live   — CRAM drives tags / expiry / validation and the live trigger.
// Rollback = set back to "off". (Replaced the old 5-value
// legacyOnly/cramShadow/cramLiveNewKyc/cramLive/rollbackToLegacy enum, which
// had no seed or UI behind it — see docs/CRAM_PLAN.md.)
export type CramMode = "off" | "shadow" | "live";

export interface CramSnapshotMetadata {
    method: "cram";
    modeAtCalculation: CramMode;
    engineVersion: string;
    configVersion: string;
    inputHash: string;
}

export interface CramConfig {
    weights: {
        customerType: number;
        geography: number;
        productsServices: number;
        industryType: number;
        directHigh: number;
    };
    buckets: {
        low: CramBucketRange;
        medium: CramBucketRange;
        high: CramBucketRange;
        prohibited: CramBucketRange;
    };
    expiry: {
        low_years: number;
        medium_years: number;
        high_years: number;
    };
    // Rollout switch (off | shadow | live). Optional for backward
    // compatibility: when absent, the engine falls back to the legacy
    // `audience.enabled` boolean (true → "live").
    mode?: CramMode;
    // CRAM activation date. KYCs submitted / last updated BEFORE this instant
    // stay on the legacy risk flow forever (even new-schema KYCs) — CRAM is
    // never computed or applied for them. Only KYCs created or
    // re-submitted/updated ON OR AFTER it are CRAM-bound. When unset there is
    // no date gate (all KYCs eligible). Stored as a Firestore Timestamp by the
    // Settings UI; the engine also accepts an ISO string or epoch-millis.
    activationDate?: Timestamp | string | number | null;
    // Audience here only carries the CRAM pipeline feature flags. "Who can
    // see / manage CRAM Configuration" is no longer assigned here — it lives
    // in the `cram` field of config/authorizationClaims (Settings → Claims).
    // `enabled` is the legacy kill-switch kept for back-compat reads.
    // `enabledForCorporate` / `enabledForTrust` gate whether CRAM applies to
    // company / trust customers; when off, those kinds stay on legacy risk
    // (no compute, no snapshot). Individuals are always in scope.
    audience: {
        enabled: boolean;
        enabledForCorporate: boolean;
        enabledForTrust: boolean;
    };
    /**
     * @deprecated Moved to `config/ai.limits`. Kept optional for dual-read
     * during migration; new code should use AiConfig.limits.
     */
    aiMapping?: {
        dailyLimit: number;
        monthlyLimit: number;
        cooldownSeconds?: number;
    };
}

// Runtime container for in-memory list lookups. Uses Map<> for O(1) item
// lookup by key. Only constructed by loadCramLists() at runtime; never
// serialized to Firestore.
export interface CramLists {
    config: CramConfig;
    customerSegmentation: Map<string, CramListItem>;
    employmentStatus: Map<string, CramListItem>;
    occupation: Map<string, CramListItem>;
    geography: Map<string, CramListItem>;
    productsServices: Map<string, CramListItem>;
    industrySectors: Map<string, CramListItem>;
    directHighIndividuals: Map<string, CramListItem>;
    snapshotVersion: string;
}

export interface CramInput {
    clientNature?: string[];
    employmentStatus?: string[];
    geographyCountries: string[];
    // Existing holdings plus anything the linked order / service is applying
    // for, deduped. Scored as one set via the per-category MAX.
    productKeys: string[];
    // The subset of `productKeys` that came from the linked order / service
    // rather than from current holdings. Only used to label breakdown
    // candidates; scoring treats both identically.
    requestedProductKeys?: string[];
    industryKey?: string;
    occupationKey?: string;
    directHighAnswers?: Record<string, 'yes' | 'no'>;
}

// Where a scored product key came from: something the customer already holds
// versus something the linked order / service in this KYC is applying for.
// Both are scored identically (per-category MAX); the distinction only drives
// how the breakdown and the CRAM PDF label the candidate.
export type CramProductOrigin = 'existing' | 'requested';

// One value that was considered for a category's per-category MAX. `score` is
// the item's own score; `chosen` marks the value whose score became the
// category `maxScore` (the winner). Lets the AML breakdown show every input
// that was weighed and exactly how the highest one was picked.
export interface CramCandidate {
    key: string;
    score: number;
    chosen: boolean;
    // Only set for productsServices candidates. Absent on every other
    // category and on snapshots written before requested products existed.
    origin?: CramProductOrigin;
}

export interface CramCategoryBreakdown {
    category: CramCategoryKey;
    maxScore: number;
    chosenKey?: string;
    // Every input considered for this category (with its own score). Optional
    // so snapshots written before this field existed still type-check.
    candidates?: CramCandidate[];
    weight: number;
    weighted: number;
}

export interface CramBreakdown {
    categories: CramCategoryBreakdown[];
    weightedSum: number;
    totalWeight: number;
    /** @deprecated no longer written — the score IS the weighted sum now.  Only present on pre-cutover snapshots. */
    composite?: number;
    preRuleRating: CramRating;
}

export interface CramListSnapshot {
    version: string;
    listIds: string[];
}

export interface CramResult {
    score: number;
    rating: CramRating;
    breakdown: CramBreakdown;
    directHighReasons: string[];
    prohibitedReasons: string[];
    fatfHits: string[];
    listSnapshot: CramListSnapshot;
}

// Persisted shapes (CRAM Task 4).
//
// `KycCramSnapshot` is the immutable per-KYC snapshot written to
// `customers/{cid}/kyc/{kycId}.cram` by submitContractKYC / updateContractKYC.
// It carries the full `CramResult` (including breakdown + listSnapshot) plus
// a `computedAt` timestamp for audit. See docs/CRAM_PLAN.md §3.4.
export interface KycCramSnapshot extends CramResult {
    computedAt: Timestamp;
    metadata?: CramSnapshotMetadata;
    engineVersion?: string;
    configVersion?: string;
    modeAtCalculation?: CramMode;
    inputHash?: string;
}

// A CRAM-team override of the engine's final rating, stored on the KYC as
// `cramOverride`. The engine keeps recomputing underneath it — score and
// breakdown stay live — but every operational consumer (risk tag, expiry,
// approval guard, UI, PDF) reads the overridden rating until it is removed.
// Resolve it with cramHelper.resolveEffectiveCramRating rather than reading
// `cram.rating` directly.
export interface CramOverride {
    // The forced rating.
    rating: CramRating;
    // Engine rating at the moment the override was applied, kept so the audit
    // trail records what was actually changed.
    fromRating: CramRating;
    // Mandatory justification.
    reason: string;
    byUid: string;
    byEmail: string;
    byName: string;
    at: Timestamp;
}

// `CustomerCramSnapshot` is the lean live snapshot mirrored onto
// `customers/{cid}.cram`. It deliberately omits the full breakdown and
// listIds so the customer doc stays small; see docs/CRAM_PLAN.md §3.3.
export interface CustomerCramSnapshot {
    score: number;
    rating: CramRating;
    directHighReasons: string[];
    prohibitedReasons: string[];
    computedAt: Timestamp;
    snapshotVersion: string;
    metadata?: CramSnapshotMetadata;
    engineVersion?: string;
    configVersion?: string;
    modeAtCalculation?: CramMode;
    inputHash?: string;
}

// ---------------------------------------------------------------------------
// CRAM Task 7 — AI industry/occupation mapping.
//
// A background Cloud Function (functions-ai) maps the customer's free-text
// employment fields to the seeded cram_lists/occupation and
// cram_lists/industry_sectors doc IDs. Each target carries an AI `suggestion`
// (or a deterministic exact match) and an optional CR-approved `selected`
// override. The CRAM engine reads the effective key (selected > suggestion).
// ---------------------------------------------------------------------------

export type CramAiMappingSource = 'exact' | 'ai' | 'none';

export interface CramAiMappingSuggestion {
    // Seeded list doc ID, or null when no confident match was produced.
    suggestedKey: string | null;
    suggestedLabel?: string | null;
    // 0..1 confidence (exact matches are 1).
    confidence: number;
    source: CramAiMappingSource;
    // True when the AI call was skipped because the budget cap was reached.
    budgetSkipped?: boolean;
}

export interface CramAiMappingSelection {
    key: string | null;
    label?: string | null;
    byUid: string;
    // Email of the CR who made the confirmation/override, for display (the raw
    // uid is not human-readable). Populated on new overrides; may be absent on
    // historic ones.
    byEmail?: string | null;
    at: Timestamp;
}

export interface CramAiMappingEntry {
    suggestion: CramAiMappingSuggestion;
    selected?: CramAiMappingSelection;
}

export type CramAiMappingStatus =
    | 'pending'
    | 'completed'
    | 'partial'
    | 'error'
    | 'skipped';

export interface CramAiMappingRawInput {
    occupation?: string;
    employer?: string;
    industryType?: string;
    jobTitle?: string;
}

// Latest mapping result, mirrored onto kyc.aiMapping.
export interface KycCramAiMapping {
    occupation: CramAiMappingEntry;
    industry: CramAiMappingEntry;
    status: CramAiMappingStatus;
    // sha256 fingerprint of the normalized employment fields + versions +
    // model — used by the trigger for idempotency.
    inputHash: string;
    modelUsed?: string;
    promptVersion: string;
    listVersion: string;
    rawInput: CramAiMappingRawInput;
    computedAt: Timestamp;
    error?: string;
}

// One entry per mapping run, appended to
// customers/{cid}/kyc/{kycID}/ai_mapping_history/{autoId} for audit.
export interface CramAiMappingHistoryEntry {
    occupation: CramAiMappingSuggestion;
    industry: CramAiMappingSuggestion;
    status: CramAiMappingStatus;
    inputHash: string;
    modelUsed?: string;
    promptVersion: string;
    listVersion: string;
    rawInput: CramAiMappingRawInput;
    at: Timestamp;
    budgetSkipped?: boolean;
    // Provenance: an actual AI run vs. a reuse of a prior KYC's mapping (no AI
    // spend) when the employment text was unchanged.
    source?: 'ai_run' | 'reused_previous_kyc';
    // When source === 'reused_previous_kyc', the KYC doc the mapping came from.
    reusedFromKycID?: string;
}

// Private AI request log, written only by backend functions to ai_history/{autoId}.
// This is intentionally generic so non-CRAM AI workflows can reuse the same
// collection shape later. It is for backend diagnostics/cost audit only and is
// not exposed in the UI.
export interface AiHistoryEntry<
    TTarget extends string = string,
    TParsed = unknown,
    TStatus extends string = 'completed' | 'error',
> {
    hash: string;
    feature: string;
    target: TTarget;
    rawInput: string;
    normalizedInput: string;
    request: Record<string, any>;
    response?: Record<string, any> | null;
    parsedResult?: TParsed;
    model: string;
    modelResponded?: string;
    promptVersion: string;
    listVersion?: string;
    cost?: number | null;
    usage?: Record<string, any> | null;
    status: TStatus;
    error?: Record<string, any> | string;
    createdAt: Timestamp;
    linkID?: string;
    customerID?: string;
    kycID?: string;
}

export type CramAiHistoryEntry = AiHistoryEntry<
    'occupation' | 'industry',
    CramAiMappingSuggestion,
    'completed' | 'error'
>;

/** KYC AI precheck audit row in ai_history (feature: "kyc_review"). */
export type KycAiHistoryEntry = AiHistoryEntry<
    'service' | 'order' | 'kyc',
    {
        label?: string;
        summary?: string;
        reasons?: string[];
        missingFields?: string[];
        riskFlags?: string[];
        recommendedAction?: string;
        fieldFindings?: KycAiFieldFinding[];
    },
    'completed' | 'error'
>;

// ---------------------------------------------------------------------------
// CRAM Task 6 — dynamic recalculation (preview-before-save).
//
// AML edits a list in the portal and clicks Save. Instead of writing the list
// straight to Firestore, the editor sends the proposed rows to
// `cramPreviewRecalc`, which creates a `cram_recalc_jobs/{jobId}` doc. A
// Firestore-triggered worker recomputes every CRAM-bound customer against the
// proposed list (without persisting) and fills in `preview`. AML confirms via
// `cramApplyRecalc`, which writes the list diff and flips the job to
// `applying`; the worker then persists the new cram.* per affected customer
// and appends `cram_history`. See docs/CRAM_PLAN.md Task 6.
// ---------------------------------------------------------------------------

export type CramRecalcStatus =
    | 'previewing'
    | 'preview'
    | 'applying'
    | 'applied'
    | 'failed'
    | 'discarded';

// Full desired state of one list's rows (adds + edits, deletes are the items
// missing from this array). Loose superset of the editor's row shape so the
// worker can rebuild the in-memory list via patchListInCramLists.
export interface CramRecalcProposedItem {
    id: string;
    key?: string;
    labelEn?: string;
    labelAr?: string;
    score?: number;
    scoreYes?: number;
    scoreNo?: number;
    flags?: Record<string, any>;
    sectorKey?: string;
    [k: string]: any;
}

export interface CramRecalcMovement {
    from: CramRating;
    to: CramRating;
    count: number;
}

export interface CramRecalcSample {
    cid: string;
    name?: string;
    oldRating: CramRating;
    newRating: CramRating;
}

export interface CramRecalcPreview {
    totalCandidates: number;
    totalAffected: number;
    // Customers whose score changes but whose rating band stays the
    // same. They are not counted in `totalAffected` (which is rating movements)
    // but their stored score + breakdown are still refreshed on apply.
    scoreOnlyAffected?: number;
    movements: CramRecalcMovement[];
    sample: CramRecalcSample[];
}

export interface CramRecalcApply {
    processedCount: number;
    changedCount: number;
    cursor?: string | null;
    appliedAt?: Timestamp;
}

export interface CramRecalcJob {
    listId: string;
    proposedItems: CramRecalcProposedItem[];
    status: CramRecalcStatus;
    byUid: string;
    byName?: string;
    createdAt: Timestamp;
    updatedAt?: Timestamp;
    preview?: CramRecalcPreview;
    apply?: CramRecalcApply;
    attempts?: number;
    error?: string;
}

// Appended to customers/{cid}/cram_history when the rating changes. Written by
// two paths with slightly different shapes:
//   - recalc worker: includes listId + jobId
//   - per-KYC persist (field change / submit): includes reason
// so the list/job fields and reason are optional, and byUid may be null.
export interface CramHistoryEntry {
    from: CramRating;
    to: CramRating;
    byUid: string | null;
    at: Timestamp;
    listId?: string;
    jobId?: string;
    reason?: string;
}



//KYC tags
export const AvailableKYCTags = {
    // "requestedKYCUpdate": { bgColor: "#DC143C", badgeColor: "green", color: "white", code: "requested_update", title: "Requested KYC Update" },
    // "professionalClient": { badgeColor: "violet", code: "prof", title: "Professional Client" },

    "waitingForApproval": { badgeColor: "orange", code: KYCStatusCode.approval, title: "Waiting for approval" },
    "waitingForReview": { badgeColor: "cyan", code: KYCStatusCode.review, title: "Waiting for review" },
    "waitingForGuardianKYC": { badgeColor: "cyan", code: KYCStatusCode.waiting_for_guardian_kyc, title: "Waiting for guardian KYC" },
    "waitingForKFHReview": { badgeColor: "teal", code: KYCStatusCode.selling_agent_review, title: "Waiting for selling agent review" },
    "waitingForKFHUpdate": { badgeColor: "red", code: KYCStatusCode.selling_agent_update, title: "Waiting for selling agent Update" },
    "waitingForExecApproval": { badgeColor: "yellow", code: KYCStatusCode.exec_approval, title: "Waiting for exec. approval" },
    // "waitingForOperationsApproval": { badgeColor: "indigo", code: KYCStatusCode.operations_approval, title: "Waiting for operations approval" },
    "waitingForClientUpdate": { badgeColor: "pink", code: KYCStatusCode.client_update, title: "Waiting for client update" },
    "rejected": { badgeColor: "red", code: KYCStatusCode.rejected, title: "Rejected" },
    "noKYC": { badgeColor: "pink", code: KYCStatusCode.no_kyc, title: "No KYC" },
    "invalidKYC": { badgeColor: "red", code: KYCStatusCode.invalid, title: "KYC Invalid" }, //title will be dynamic
    "validKYC": { badgeColor: "green", code: KYCStatusCode.valid, title: "Valid" },
    "waitingForManualKYC": { badgeColor: "blue", code: KYCStatusCode.manual_kyc, title: "Waiting for manual KYC" },
    "waitingForInterview": { badgeColor: "blue", code: KYCStatusCode.waiting_for_interview, title: "Waiting for interview" },
    "waitingForAuthorizedSignatory": { badgeColor: "blue", code: KYCStatusCode.waiting_for_authorized_signatory, title: "Waiting for authorized signatory" },
};


// =====================================================================================
// CTC (Commodity Trading Center) — replacement for the legacy KFH-NEW-CTC application.
// Collections are prefixed ctc_ and written only by functions-ctc. See docs/ctc-module-plan.md.
// =====================================================================================

export interface CtcAuditStamp {
    created?: Timestamp;
    createdBy?: string; // email
    updated?: Timestamp;
    updatedBy?: string;
}

export interface CtcNamed extends CtcAuditStamp {
    id?: string;
    name_en: string;
    name_ar: string;
    retired?: boolean; // hidden for now; can be shown again (Back office → "Show retired records")
    archived?: boolean;
    /**
     * Soft delete (Back office → Delete): hidden everywhere for good, but the document stays so
     * orders, PDFs and commission calculations that reference it by id keep working.
     * CTC master data is never hard-deleted (firestore.rules).
     */
    deleted?: boolean;
    deletedAt?: Timestamp;
    deletedBy?: string; // email
}

/** A corporate customer of the CTC. For now there is exactly one: KFH (the bank). */
export interface CtcCustomer extends CtcNamed, CtcCustomerFlags {
    shortName?: string;
    groupId?: string;
    currencyId?: string;
    isRestrictedCurrency?: boolean;
    amountLimit?: { active: boolean; amount: number; currencyId?: string };
    beneficiaryLimit?: { active: boolean; amount: number };
    bankAccountId?: string;
    settlementAccountId?: string;
    sendReminderToCustomer?: boolean;
    sendReminderToBackOffice?: boolean;
    /** Text blocks baked into the generated PDFs, by attachment number (legacy TbEmailTemplate ids). */
    templateIds?: Partial<Record<'purchaseApproval' | 'purchaseConfirmation' | 'transferConfirmation' | 'sellConfirmation' | 'sell2Confirmation', string>>; // legacy, replaced by attachmentTemplateIds
    /**
     * Text template per generated attachment (ctc_config/ctcTemplates/templates id by attachment number).
     * New customers start with each number's default; missing/inactive falls back to the default.
     */
    attachmentTemplateIds?: Partial<Record<CtcAttachmentNumberKey, string>>;
    refPrefix: string; // e.g. "KFH"
    refCounter?: number; // last used sequence, incremented in a transaction
    logoPath?: string; // Storage path of the logo stamped on PDFs
    /** Beneficiary emails and the answer page (ctc-email-decision.md): full legal name for the footer; default the name. */
    legalName?: string;
    /** Helpline in beneficiary emails, the answer page and WhatsApp texts; default 1855855. */
    supportPhone?: string;
    /** Top stripe and main button colour in beneficiary emails and the answer page, "#RRGGBB"; default dark grey. */
    brandColor?: string;
    /** Email domains whose Entra users act for this customer (maker/checker lanes). */
    authDomains: string[]; // e.g. ["kfh.com"]
    /**
     * Microsoft (Entra) group(s) whose members are this customer's makers/checkers: one id (older records)
     * or a list, e.g. a makers group and a checkers group. Members of any of them act for this customer
     * (their maker / checker role still comes from their CTC claims). Matched at sign-in
     * (customClaims → ctc_group_customer_ids); not set = nobody but admins can create or check its orders.
     */
    microsoftGroupId?: string | string[];
    /**
     * Commission profiles (ctc_commissions ids) this customer's orders may use. New purchase order
     * offers only these (active ones), and ctcSaveOrder rejects any other. Retiring a profile keeps
     * the link; deleting a profile removes it from every customer.
     */
    commissionIds?: string[];
}

export interface CtcCustomerGroup extends CtcNamed { }

/**
 * A field back office adds to one customer's orders (ctc_customers/{id}/dynamic_fields): shown on New purchase
 * order and the order page. "datetime" only on older fields, handled as text.
 */
export interface CtcDynamicFieldDef {
    id?: string;
    name_en: string;
    name_ar: string;
    type: 'text' | 'number' | 'bool' | 'datetime';
    required: boolean;
    order: number;
}

/** A dynamic field's labels as they were when the order was saved (CtcPurchaseOrder.dynamicFieldDefs). */
export interface CtcDynamicFieldSnapshot {
    name_en: string;
    name_ar: string;
    type: CtcDynamicFieldDef["type"];
    order: number;
}

export interface CtcBrokerLocation {
    id?: string;
    name_en: string;
    name_ar: string;
}

export interface CtcBroker extends CtcNamed {
    email?: string;
    phone?: string;
    mobile?: string;
    mobile_code?: string;
    country?: string;
    commissionId?: string;
    currencyId: string;
    language?: 'en' | 'ar';
    locations?: CtcBrokerLocation[];
}

export interface CtcCommodity extends CtcNamed {
    measurementUnit: string; // e.g. "MT", "oz"
    price?: number;
    currencyId?: string;
}

export interface CtcCurrency extends CtcNamed {
    shortName: string; // ISO code, doc id
    decimalPoint: number;
    rates?: { buy?: number; sell?: number; onlineBuy?: number; onlineSell?: number; mid?: number; updated?: Timestamp };
    hasIntegration?: boolean; // refreshed from kfh.com rates
}

export interface CtcCommissionRange {
    startAmount: number;
    endAmount?: number; // undefined = open ended
    isFixedAmount: boolean;
    fixedAmount?: number;
    percentage?: number;
    minimumAmount?: number;
}

/** A commission profile. Legacy called this "trading type" on the PO. */
export interface CtcCommission extends CtcNamed {
    currencyId: string;
    tradingType?: string;
    isOnRange: boolean;
    ranges: CtcCommissionRange[];
}

export interface CtcBankAccount extends CtcNamed {
    accountNumber: string;
    currencyId: string;
    availableBalance?: number;
}

export interface CtcBankTransaction extends CtcAuditStamp {
    id?: string;
    bankAccountId: string;
    type: 'deposit' | 'withdraw';
    amount: number;
    date: Timestamp;
    reference?: string;
    notes?: string;
}

export interface CtcPoStatusTag {
    code: CtcPoStatusCode;
    title: string;
    badgeColor: string;
}

export type CtcAttachmentNumberKey = '1' | '2' | '3' | '4' | '5';

/**
 * Text of a generated attachment (1-5): one English and one Arabic paragraph; the PDF uses the
 * order's language. Exactly one template per attachment number is the default. Every save is a
 * new version, kept in the `versions` subcollection; customers always get the latest.
 * Written only by functions-ctc (ctcSaveAttachmentTemplate / ctcUpdateAttachmentTemplate).
 */
export interface CtcAttachmentTemplate extends CtcAuditStamp {
    id?: string;
    attachmentNumber: 1 | 2 | 3 | 4 | 5;
    name: string;
    text_en: string;
    text_ar: string;
    isDefault: boolean;
    version: number; // latest version number
    retired?: boolean;
    deleted?: boolean;
    deletedAt?: Timestamp;
    deletedBy?: string;
}

/** ctc_config/ctcTemplates/templates/{id}/versions/{version}: immutable snapshot of one save. */
export interface CtcAttachmentTemplateVersion {
    version: number;
    name: string;
    text_en: string;
    text_ar: string;
    savedAt: Timestamp;
    savedBy: string;
    note?: string; // e.g. "Restored from v2"
}

export interface CtcPoAttachment {
    number: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10; // 1-5 generated PDFs; 6-9 dealer uploads (CTC_DEALER_UPLOADS); 10 other documents (order page, ctcAddAttachments)
    path: string; // Storage path: ctc/{poId}/ (attachment_* generated, dealer_* uploaded, other_* other documents)
    generatedAt: Timestamp;
    version?: string;
    sha256?: string;
    templateId?: string; // generated: attachment template id used (absent = built-in text)
    templateVersion?: number;
    language?: 'en' | 'ar'; // generated: language of the attachment text
    kind?: string; // dealer uploads: CtcDealerUploadSpec.kind
    source?: "generated" | "upload";
    label?: string;
    fileName?: string;
    contentType?: string;
    uploadedBy?: string; // email
}

export interface CtcBeneficiary {
    number: string; // legacy BeneficiaryNo (the bank's customer/account reference)
    accountNumber?: string; // older orders only: no longer asked for (Attachments 3 / 4 use it if present, else the number)
    name?: string; // older orders only: no longer asked for
    mobile: string; // local digits, never with dial code
    mobile_code: string; // "+965"
    whatsapp?: string; // E.164, derived by functions
    email?: string;
    language?: 'en' | 'ar';
}

export interface CtcPurchaseOrder extends CtcAuditStamp {
    id?: string;
    referenceNumber: string; // {refPrefix}-{YY}-{seq}
    clientRefNo?: string;
    customerId: string;
    customerName?: string; // the customer's name_en when the order was created (lists)
    makerName?: string; // the maker's display name when the order was created (lists); older orders: makerEmail
    commissionName?: string; // the commission profile's name_en (Trading type) when the order was saved (lists)
    brokerName?: string; // the broker's name_en when the purchase was confirmed (lists)
    commodityName?: string; // the commodity's name_en when the purchase was confirmed (lists)
    acceptedByName?: string; // the accepting user's display name (lists: Activated by); older orders: acceptedByEmail
    tags: { poStatus: CtcPoStatusTag };
    state?: string; // aggregate label (Draft/Pending/.../Completed) for reports
    lane?: CtcRole | null; // who the PO is waiting on (denormalised for queues)
    beneficiary: CtcBeneficiary;
    amount: number;
    currencyId: string;
    commissionId: string; // commission profile (legacy TradingTypeId)
    quantity?: number;
    unitPrice?: number;
    commodityId?: string;
    brokerId?: string;
    locationId?: string;
    locationName?: string;
    certificateNumber?: string;
    brokerCommission?: number;
    customerCommission?: number;
    valueDate?: string | Timestamp; // treasury customers only: "YYYY-MM-DD" (older orders may hold a Timestamp)
    dynamicFields?: Record<string, string | number | boolean>; // by dynamic field id
    dynamicFieldDefs?: Record<string, CtcDynamicFieldSnapshot>; // labels at save time, by the same id
    attachments?: CtcPoAttachment[];
    uploads?: string[]; // supporting documents uploaded by the maker (Storage paths)
    // actors (emails)
    makerEmail: string;
    checkerEmail?: string;
    acceptedByEmail?: string;
    assignedDealerEmail?: string;
    sellRequestedByEmail?: string;
    transferRequestedByEmail?: string;
    soldMethod?: 'whatsapp' | 'email' | 'manual' | 'customer' | 'ctc';
    // timestamps of the lifecycle steps
    submittedAt?: Timestamp;
    acceptedAt?: Timestamp;
    purchasedAt?: Timestamp;
    transferredAt?: Timestamp;
    soldAt?: Timestamp;
    completedAt?: Timestamp;
    rejectionReason?: string;
    /** Cancel (after Accepted, before Completed): why, who, when. */
    cancelReason?: string;
    cancelledByEmail?: string;
    cancelledAt?: Timestamp;
    lastAction?: { action: CtcAction; by: string; at: Timestamp };
    pendingEffects?: CtcEffect[]; // side effects that have not run yet (retry with ctcRetryEffects)
    effectErrors?: Partial<Record<CtcEffect, string>>; // last failure message per effect
    commissionCurrency?: { customer: string; broker?: string };
    whatsappCopySent?: Partial<Record<'2' | '4' | '5', boolean>>; // display status; durable delivery records guard retries
    whatsappPrompt?: { kind: 'transfer_approval' | 'sell_request'; sentAt: Timestamp; twilioSid?: string | null; emailTo?: string | null } | null; // last decision prompt (WhatsApp and / or email)
    legacy?: { poId?: string; statusId?: number }; // migration cross-reference
    workflowRevision?: number;
    activeJobId?: string;
    activeDecisionRequestId?: string;
    commissionSnapshot?: Record<string, unknown>;
}

export type CtcSettlementStatus = 'settled' | 'unsettled' | 'pending_waive_approval' | 'waived';

export interface CtcSettlementSide {
    status: CtcSettlementStatus;
    amount?: number;
    currencyId?: string;
    settledAt?: Timestamp;
    settledBy?: string;
    reference?: string; // bank / payment reference entered when marking settled
    comment?: string; // why there is no payment reference (bulk upload: Payment Ref or Comments is required)
    waived?: boolean;
}

export interface CtcSettlement extends CtcAuditStamp {
    id?: string; // = poId
    poId: string;
    referenceNumber: string;
    customerId: string;
    brokerId?: string;
    customer: CtcSettlementSide;
    broker: CtcSettlementSide;
    settled?: boolean; // both sides settled or waived; drives the "Open" view
    /** Set when the order is cancelled after completion (sides closed as not due). */
    orderState?: string;
    /** Soft delete: the order was cancelled after completion before any side was settled (sides stay "unsettled"). */
    deleted?: boolean;
    deletedAt?: Timestamp;
    deletedBy?: string;
    deleteReason?: string;
}

/** One row of the bulk-settlement sheet, as parsed by the portal. Fields stay raw so the server reports bad values. */
export interface CtcBulkSettleRow {
    row: number; // 1-based Excel row, echoed back so back office can find it in the sheet
    referenceNumber: string;
    side: string; // "customer" | "broker" (case-insensitive)
    amount?: number;
    reference?: string; // payment / core-banking reference
    comment?: string; // required when there is no payment reference
    settledAt?: string; // ISO date
}

export interface CtcBulkSettleRequest {
    rows: CtcBulkSettleRow[];
    dryRun: boolean; // true = check only (nothing written), false = check again and settle the valid rows
}

export interface CtcBulkSettleRowResult {
    row: number;
    referenceNumber: string;
    side: string;
    poId?: string;
    amount?: number; // system amount
    currencyId?: string;
    // dry run: ready | invalid. Commit: settled | invalid | failed (passed the check but the write failed)
    status: 'ready' | 'invalid' | 'settled' | 'failed';
    reason?: string;
}

export interface CtcBulkSettleResponse {
    dryRun: boolean;
    results: CtcBulkSettleRowResult[];
}

export interface CtcReversal extends CtcAuditStamp {
    id?: string;
    poId: string;
    side: 'customer' | 'broker'; // whose commission is waived
    amount: number;
    reason: string;
    status: 'pending' | 'approved' | 'rejected';
    decidedBy?: string;
    decidedAt?: Timestamp;
}

/** One open WhatsApp decision per phone number (doc id = E.164). */
export interface CtcPendingReply {
    requestId?: string; // absent only on pre-hardening records; these cannot authorize an action
    requestCode?: string;
    phone: string;
    poId: string;
    referenceNumber: string;
    kind: 'transfer_approval' | 'sell_request';
    options: string[]; // accepted answers, e.g. ["approve", "reject"]
    sentAt: Timestamp;
    remindAt?: Timestamp;
    expiresAt: Timestamp;
    reminded?: boolean;
    templateSid?: string;
    lastInboundBody?: string; // last unmatched reply, for the re-prompt and for staff
    lastInboundAt?: Timestamp;
    repromptCount?: number;
    /** Set by the inbound webhook when the answer matched; functions-ctc applies it and deletes the doc. */
    decision?: 'approve' | 'reject' | 'sell' | 'collect';
    decidedAt?: Timestamp;
    decisionMessageSid?: string;
    decisionError?: string; // transition failed (e.g. order moved on); dealer resolves manually
    // After the answer is applied the doc stays for a short "change your answer" window
    appliedAt?: Timestamp;
    appliedAction?: CtcAction;
    appliedDecision?: 'approve' | 'reject' | 'sell' | 'collect';
    changeableUntil?: Timestamp;
    /** Set by the inbound webhook when the client asks to change a just-applied answer. */
    changeRequested?: boolean;
    changeRequestedAt?: Timestamp;
    /** The new answer, when the client typed it directly instead of "change". */
    requestedDecision?: 'approve' | 'reject' | 'sell' | 'collect';
    changeError?: string;
    /** Which channel answered first (WhatsApp or the email answer page); the other one is then closed. */
    decisionChannel?: CtcDecisionChannel;
    changeChannel?: CtcDecisionChannel;
}

export type CtcDecisionChannel = 'whatsapp' | 'email';

/** Brand and order values frozen on a decision request for the email and the answer page (ctcBrand.ts). */
export interface CtcDecisionView {
    language: 'en' | 'ar';
    customerName: string;
    legalName: string;
    supportPhone: string;
    brandColor: string; // validated "#RRGGBB"
    logoPath?: string;
    serviceName: string;
    reference: string;
    commodity: string;
    quantity: string;
    total: string;
    unitPrice: string;
    location: string;
    commission: string;
    documentName: string; // e.g. "Attachment 2 - Purchase confirmation"
}

/** Permanent consent evidence. The phone-keyed pending collection is only a pointer. */
export interface CtcDecisionRequest extends CtcPendingReply {
    requestId: string;
    requestCode: string;
    revision: number;
    status: 'preparing' | 'open' | 'decided' | 'applied' | 'closed' | 'expired' | 'failed';
    outboundSids: string[];
    templateBody: string;
    variables: Record<string, string>;
    documents: CtcPoAttachment[];
    changeMessageSid?: string;
    /** Email channel (ctc-email-decision.md); the link token itself is never stored. */
    emailTo?: string;
    emailTokenHash?: string;
    emailTokenSalt?: string;
    emailSentAt?: Timestamp;
    emailReminderSentAt?: Timestamp;
    emailError?: string; // email failed while WhatsApp delivered
    whatsappError?: string; // WhatsApp failed while the email delivered
    emailAckSentAt?: Timestamp;
    emailAckError?: string;
    view?: CtcDecisionView;
    /** Why the question was closed: replaced by a new question (reopen / change of answer) or closed by staff. */
    closedReason?: 'replaced' | 'staff';
    closedAt?: Timestamp;
    expiredAt?: Timestamp;
    ackPending?: boolean;
    ackBody?: string;
    ackSid?: string;
    ackError?: string;
    changeBody?: string;
}

/** Per-staff profile the PDFs need (no user creation: docs are created lazily, keyed by email). */
export interface CtcStaffProfile extends CtcAuditStamp {
    email: string;
    displayName?: string;
    signaturePath?: string; // Storage path of the signature image (signers of attachments: makers / checkers / dealers)
    signatureUpdatedAt?: Timestamp; // when the signature image last changed
    groups?: { id: string; displayName: string }[]; // Microsoft groups at last sign-in (written by customClaims)
    groupIds?: string[];
    groupsSyncedAt?: Timestamp;
}

/** ctc_config/administration: Microsoft group ids whose members are listed as dealers in Back office. */
export interface CtcAdministrationConfig extends CtcAuditStamp {
    administrationGroupId?: string[];
}



export interface CtcTransitionRequest {
    poId: string;
    action: CtcAction;
    reason?: string; // rejections
    dealerEmail?: string; // accept / reassign
    purchase?: { // confirm_purchase
        brokerId: string;
        commodityId: string;
        quantity: number;
        unitPrice: number;
        locationId?: string;
        locationName?: string;
        certificateNumber?: string;
        purchasedAt?: string; // ISO
    };
    at?: string; // ISO timestamp for confirm_transfer / confirm_sell (legacy asked the dealer for date+time)
    documents?: { kind: string; path: string }[]; // confirm_purchase / confirm_transfer / confirm_sell / beneficiary_sell (dealer): dealer uploads (CTC_DEALER_UPLOADS)
}

/** kfhtradeUpdateKycDates callable (functions/src/kfhtradeOmsApi.ts) — dates as YYYY-MM-DD */
export interface KfhtradeUpdateKycDatesRequest {
    customerID: string;
    serviceID: string;
    kycExpiryDate: string;
    civilIdExpiryDate: string;
}

export interface KfhtradeUpdateKycDatesResult {
    outcome: 'updated' | 'failed';
    message: string;
    customerNo?: string;
}

/** Why a customer is selected for a DFN KYC-renewal batch: no KYC at all, or the latest submitted KYC predates the current KYC flow (no completedKYCData). */
export type KfhtradeBatchKycReason = 'no_kyc' | 'outdated_kyc';
export interface KfhtradeBatchCustomer {
    id: string; name: string; crmID: string; customerNo: string;
    civilID: string; kfhtradeStatus: string; holdingsKwd?: number; kycReason: KfhtradeBatchKycReason;
}
export interface KfhtradeWhatsappRequest {
    action: 'send' | 'status'; customerIDs: string[]; batchID: string;
}
export interface KfhtradeWhatsappResult {
    results: { id: string; status: string; message: string; dfnUpdatedAt?: string; whatsappSentAt?: string; canSend: boolean }[];
}
export interface KfhtradeBatchRequest {
    action: 'preview' | 'confirm' | 'status' | 'skip';
    limit?: number; customerIDs?: string[]; kycExpiryDate?: string; batchID?: string; sendWhatsapp?: boolean;
    minHoldingsKwd?: number; maxHoldingsKwd?: number;
}
export interface KfhtradeBatchResult {
    customers?: KfhtradeBatchCustomer[]; batchID?: string; expiryDate?: string;
    results?: { id: string; status: string; message?: string }[];
}

export interface KfhtradeHistoryRow {
    id: string; customerID: string; customerNo: string; clientName: string; batchID: string;
    status: string; whatsappStatus: string; createdAt: number; dfnUpdatedAt: number; whatsappSentAt: number;
}
export interface KfhtradeHistoryRequest {
    action: 'search' | 'reindex'; query?: string; page?: number; cursor?: string;
}
export interface KfhtradeHistoryResult {
    rows: KfhtradeHistoryRow[]; found: number; indexed?: number; nextCursor?: string;
}
